import { holdCountdown, minutesPhrase, DEFAULT_GIFT_CARD_AGREEMENT } from './deposit-step';
import { describeDraft } from './draft';
import {
  serviceVerdict, enhancementVerdict, providerVerdict,
  dayLimitedPlans, dayLabel, planCoversDate, filterEmptyReason,
  planHasBookableServices, planServiceIds, planCoversProvider,
  upsellPlanFor, upsellLine,
} from './membership-eligibility';
import type { Verdict } from './membership-eligibility';
import type { PortalState } from './state';
import { NOT_UPCOMING } from './state';
import type { Booking, BookingCredit, ClientPromotion, ClientPackage, ClientMembership, CreditGroup, FormSubmission } from './types';
import type { FormField, SelfServiceTerms, PortalBookingItem, Category, Provider, EnhancementGroup, BookingItem, Addon } from './types';
import type { PortalTagGroup } from './api';

// ── Helpers ────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Sanitize HTML to a safe subset (strips scripts, event handlers, iframes). */
function sanitizeHtml(html: string): string {
  return String(html)
    .replace(/<script[\s>][\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s>][\s\S]*?<\/iframe>/gi, '')
    .replace(/<object[\s>][\s\S]*?<\/object>/gi, '')
    .replace(/<embed[\s>][\s\S]*?>/gi, '')
    .replace(/\son\w+\s*=/gi, ' data-removed=');
}

/**
 * A membership perk in words. Each one is either a percentage or a flat amount
 * off — the plan says which.
 */
export function membershipPerkLabel(
  type: string | undefined, percent: number | undefined,
  amount: number | undefined, what: string,
): string {
  return (type === 'amount')
    ? `${formatCurrency(amount || 0)} off ${what}`
    : `${percent || 0}% off ${what}`;
}

export function membershipPerks(p: any): string[] {
  const perks: string[] = [];
  if (p.discountServices) {
    perks.push(membershipPerkLabel(p.discountServicesType, p.discountServicesPercent, p.discountServicesAmount, 'services'));
  }
  if (p.discountEnhancements) {
    perks.push(membershipPerkLabel(p.discountEnhancementsType, p.discountEnhancementsPercent, p.discountEnhancementsAmount, 'enhancements'));
  }
  if (p.discountProducts) {
    perks.push(membershipPerkLabel(p.discountProductsType, p.discountProductsPercent, p.discountProductsAmount, 'products'));
  }
  return perks;
}

/**
 * Terms are rich text since the plan editor grew an editor; rows written
 * before that are plain text, so render whichever this actually is rather than
 * dumping tags at the client or escaping their formatting away.
 */
/**
 * "With Kayla or Sarah" — a plan limited to certain providers has to say so
 * before the money changes hands, not at the desk afterwards.
 */
export function membershipProviderNote(p: any): string {
  const names: string[] = p?.redemptionStaffNames || [];
  if (!names.length) return '';
  if (names.length === 1) return `With ${names[0]}`;
  return `With ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/**
 * The eligibility pill.
 *
 * Three states, and the WORDS differ, not only the color: roughly one man in
 * twelve can't reliably separate green from amber, and a disclosure a client
 * cannot perceive is a weaker argument than one they can read. Color
 * reinforces a distinction that already survives greyscale.
 */
export function membershipPill(v: Verdict, opts: { short?: boolean } = {}): string {
  if (v.kind === 'none') return '';
  if (v.kind === 'covered') {
    return `<span class="pp-mem-pill pp-mem-pill-in">Included in your membership</span>`;
  }
  if (v.kind === 'not-covered') {
    return `<span class="pp-mem-pill pp-mem-pill-out">Not in your membership</span>`;
  }
  // Advertising: name the plans. Several is fine — different plans are
  // different price points, which is information rather than clutter.
  return v.plans.map(p => `<span class="pp-mem-pill pp-mem-pill-ad">${
    esc(opts.short ? p.name : `${p.name} eligible`)}</span>`).join('');
}

/** The legend that makes the two member colors mean something. */
export function membershipLegend(v: 'member' | 'none'): string {
  if (v !== 'member') return '';
  return `<div class="pp-mem-legend">
    <span><span class="pp-mem-dot pp-mem-dot-in"></span>Included in your membership</span>
    <span><span class="pp-mem-dot pp-mem-dot-out"></span>Not included — full price</span>
  </div>`;
}

/**
 * "Only what my membership covers" — the filter offered on step 1.
 *
 * Only plans that leave something bookable are offered: a chip that filters to
 * nothing is a dead end dressed up as a choice.
 */
export function membershipFilterChips(s: PortalState): string {
  if (s.membershipFilterPlanId != null) return '';
  const bookableIds: number[] = [];
  for (const cat of s.categories) for (const svc of cat.services) bookableIds.push(Number(svc.id));
  const owned = new Set(s.eligibility.ownedPlanIds);
  const pool = s.membershipPlans.filter(p =>
    planHasBookableServices(p, bookableIds));
  if (!pool.length) return '';
  const chips = pool.map(p => {
    const mine = owned.has(Number(p.id));
    return `<button class="pp-mem-chip${mine ? ' mine' : ''}" data-action="set-mem-filter" data-planid="${p.id}">${
      esc(mine ? `Only what ${p.name} covers` : `Show ${p.name} services`)}</button>`;
  }).join('');
  return `<div class="pp-mem-chip-row">${chips}</div>`;
}

/**
 * The bar that follows the filter through every later step. Without it, three
 * screens on, a provider is simply missing with no explanation.
 */
export function membershipFilterBar(s: PortalState): string {
  const plan = s.filterPlan;
  if (!plan) return '';
  return `<div class="pp-mem-filter-bar">
    <span>Showing <strong>${esc(plan.name)}</strong> only</span>
    <button class="pp-mem-filter-clear" data-action="clear-mem-filter" aria-label="Show everything">Show everything</button>
  </div>`;
}

/** An empty filtered step explains itself, in the client's words. */
export function membershipFilterEmpty(s: PortalState): string {
  const plan = s.filterPlan;
  if (!plan) return '';
  // An alarm, not a metric: a filter that dead-ends loses the booking silently,
  // so it has to be visible in the numbers. Fired once per plan+step.
  s.noteFilterDeadEnd(plan.id);
  return `<div class="pp-mem-empty">
    <p class="pp-mem-empty-why">${esc(filterEmptyReason(plan))}</p>
    <button class="pp-btn pp-btn-accent pp-btn-sm" data-action="clear-mem-filter">Show everything</button>
  </div>`;
}

/**
 * "$216 today, then $108/mo" — never just the monthly price when more leaves
 * the account today. A first charge nobody was told about is how a signup
 * becomes a chargeback.
 */
export function signupChargeNote(plan: any): string {
  const t = plan?.signupTerms;
  if (!t || (t.periods || 1) <= 1) return '';
  const per = plan.billingInterval === 1 ? plan.billingUnit
    : `${plan.billingInterval} ${plan.billingUnit}s`;
  return `${formatCurrency(t.dueToday)} today — ${t.periods} periods, non-refundable. `
    + `Then ${formatCurrency(t.perPeriod)}/${per}.`;
}

/**
 * The offer shown once a booking is banked.
 *
 * Deliberately AFTER the booking, not during it: interrupting someone
 * mid-decision to sell them something risks the booking itself, and there is
 * nothing to gain by asking a second question before the first is answered.
 */
export function membershipUpsell(s: PortalState): string {
  const svc = s.curr.service;
  if (!svc || s.membershipsSilent) return '';
  const staffId = s.curr.provider?.type?.startsWith('filter') ? null : s.curr.provider?.id;
  const plan = upsellPlanFor(s.eligibility, svc.id, staffId ?? null);
  if (!plan) return '';
  // Shown-vs-accepted is the only way to read this feature; fired once per
  // plan so a re-render can't inflate it.
  s.noteUpsellShown(plan.id);
  return `
    <div class="pp-mem-upsell" data-upsell-plan="${plan.id}">
      <div class="pp-mem-upsell-head">Member price on visits like this one</div>
      <div class="pp-mem-upsell-line">${esc(upsellLine(plan))}</div>
      <button class="pp-btn pp-btn-accent pp-btn-sm" data-action="upsell-join" data-planid="${plan.id}">
        Join ${esc(plan.name)}
      </button>
      <button class="pp-mem-upsell-no" data-action="upsell-dismiss">No thanks</button>
    </div>`;
}

export function renderTermsBody(text: string): string {
  const t = text || '';
  return /<[a-z][\s\S]*>/i.test(t) ? sanitizeHtml(t) : esc(t);
}

function checked(b: boolean): string { return b ? 'checked' : ''; }

// The registered A2P 10DLC disclosures as the portal's preferences show them
// under each texts switch (Tim, 2026-11-02). The sentences are the booking
// form's own (_smsConsentBlock / _smsMarketingCheckbox below): keep the two
// in step — both must match what the campaign registration declares.
export function smsApptDisclosure(spaName: string): string {
  return `${esc(spaName)} sends texts about your appointments and account &mdash; reminders &amp; confirmations,
        deposit/payment links, verification codes, and customer support. Message frequency varies. Msg &amp; data
        rates may apply. Reply STOP to opt out, HELP for help.`;
}
export function smsMarketingDisclosure(spaName: string): string {
  return `Occasional promotions, offers, and news from ${esc(spaName)}. Consent is not a condition of purchase.
        Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. We will
        not share your mobile information with third parties for promotional or marketing purposes.
        Privacy policy: <a href="https://getcalendoula.com/privacy" target="_blank" rel="noopener">getcalendoula.com/privacy</a>`;
}

// SMS marketing opt-in checkbox (A2P 10DLC): marketing consent is collected
// SEPARATELY from the transactional consent (the smsOpt radio / consent statement),
// matching how the campaign use cases + opt-in disclosures are declared.
function _smsMarketingCheckbox(s: PortalState): string {
  return `
    <label class="pp-bk-sms-marketing">
      <input type="checkbox" data-field="bkMarketingTexts" ${checked(s.bkMarketingTexts)}>
      <span>I'd like to receive marketing texts &mdash; occasional promotions, offers, and news &mdash; from ${esc(s.spaName)}.
        Consent is not a condition of purchase. Message frequency varies. Msg &amp; data rates may
        apply. Reply STOP to opt out, HELP for help. We will not share your mobile information
        with third parties for promotional or marketing purposes. Privacy policy: getcalendoula.com/privacy</span>
    </label>`;
}

// SMS consent block shown on the booking confirm step (A2P 10DLC opt-in).
// Transactional consent statement (appointments, payment links, verification, support)
// + the separate optional marketing checkbox above.
function _smsConsentBlock(s: PortalState): string {
  return `
    <div class="pp-bk-sms-consent">
      <p class="pp-bk-sms-text">
        By booking, you agree to receive text messages from ${esc(s.spaName)} about your appointments and
        account &mdash; reminders &amp; confirmations, deposit/payment links, verification codes, and customer
        support. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
        See our <a href="https://getcalendoula.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
      </p>
      ${_smsMarketingCheckbox(s)}
    </div>`;
}
function disabled(b: boolean): string { return b ? 'disabled' : ''; }

function _parseSlotMins(t: string): number | null {
  const m = t.trim().toLowerCase().match(/^(\d+):(\d+)\s*(am|pm)$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (m[3] === 'pm' && h !== 12) h += 12;
  if (m[3] === 'am' && h === 12) h = 0;
  return h * 60 + min;
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Render Stripe card element or legacy raw inputs based on state.stripePublishableKey */
function renderCardField(s: PortalState, prefix: string, pd: {cardNumber?: string; expiry?: string; cvc?: string}): string {
  if (s.stripePublishableKey) {
    // Apple Pay / Google Pay above the card form. Hidden until Stripe reports a
    // wallet ready on this device (widget.ts mounts it); memberships bill again
    // every period, so they stay card-only.
    const wallet = prefix === 'mem' ? '' : `
      <div id="pp-stripe-wallet-${prefix}" class="pp-wallet pp-wallet--pending">
        <div data-wallet-slot></div>
        <div class="pp-wallet-or"><span>or pay with card</span></div>
      </div>`;
    return `${wallet}
      <div class="pp-f-row">
        <label class="pp-f-label">Credit card</label>
        <div class="pp-gc-card-field-wrap" style="padding:10px 12px">
          <div id="pp-stripe-card-element-${prefix}" style="min-height:20px"></div>
        </div>
      </div>`;
  }
  return `
      <div class="pp-f-row">
        <label class="pp-f-label">Credit card</label>
        <div class="pp-gc-card-field-wrap">
          <div class="pp-gc-card-row">
            <span class="pp-gc-card-icon">
              <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
                <rect width="28" height="18" rx="3" fill="#E5DDD6"/>
                <rect x="2" y="6" width="24" height="3" fill="#D4C8BE"/>
                <rect x="2" y="11" width="8" height="2" rx="1" fill="#C4B8AE"/>
              </svg>
            </span>
            <input class="pp-gc-card-input pp-gc-card-input-num"
              type="text" inputmode="numeric" placeholder="Card number"
              value="${esc((pd.cardNumber || ''))}"
              data-field="${prefix}CardNumber"
              maxlength="19" autocomplete="cc-number">
            <div class="pp-gc-card-sep-v"></div>
            <input class="pp-gc-card-input pp-gc-card-input-exp"
              type="text" inputmode="numeric" placeholder="MM / YY"
              value="${esc((pd.expiry || ''))}"
              data-field="${prefix}Expiry"
              maxlength="7" autocomplete="cc-exp">
            <div class="pp-gc-card-sep-v"></div>
            <input class="pp-gc-card-input pp-gc-card-input-cvc"
              type="text" inputmode="numeric" placeholder="CVC"
              value="${esc((pd.cvc || ''))}"
              data-field="${prefix}Cvc"
              maxlength="4" autocomplete="cc-csc">
          </div>
        </div>
      </div>`;
}

/** "$92" or "$86.25" — whole dollars stay whole. */
function fmtSlotPrice(n: number): string {
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function formatTime12(t: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t || '');
  if (!m) return t || '';
  const h = Number(m[1]);
  return `${(h % 12) || 12}:${m[2]} ${h < 12 ? 'am' : 'pm'}`;
}

export function formatCurrency(amount: number): string {
  return '$' + amount.toFixed(2);
}


function statusBadgeClass(status: string): string {
  switch (status) {
    case 'confirmed': return 'pp-badge pp-badge-confirmed';
    case 'completed': return 'pp-badge pp-badge-completed';
    case 'cancelled': return 'pp-badge pp-badge-cancelled';
    case 'unconfirmed': return 'pp-badge pp-badge-pending';
    case 'in-service': return 'pp-badge pp-badge-active';
    case 'waiting': return 'pp-badge pp-badge-pending';
    case 'active': return 'pp-badge pp-badge-active';
    case 'paused': return 'pp-badge pp-badge-pending';
    case 'expired': return 'pp-badge pp-badge-cancelled';
    case 'closed': return 'pp-badge pp-badge-completed';
    case 'open': return 'pp-badge pp-badge-active';
    case 'refunded': return 'pp-badge pp-badge-cancelled';
    default: return 'pp-badge';
  }
}

function formStatusBadge(status: string): string {
  switch (status) {
    case 'completed': return 'pp-badge pp-badge-completed';
    case 'pending':   return 'pp-badge pp-badge-pending';
    default:          return 'pp-badge';
  }
}

// ── SVG Icons ──────────────────────────────────────────────────────────────

const ICON_BACK = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CLOSE = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
const ICON_CHEVRON = `<svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CALENDAR = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.3"/><path d="M2 8h16" stroke="currentColor" stroke-width="1.3"/><path d="M6 2v4M14 2v4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`;
const ICON_RECEIPT = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 2h10a1 1 0 011 1v14l-2-1.5L12 17l-2-1.5L8 17l-2-1.5L4 17V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.3"/><path d="M7 6h6M7 9h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`;
const ICON_GIFT = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="8" width="16" height="10" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M10 8v10M2 12h16" stroke="currentColor" stroke-width="1.3"/><path d="M10 8C10 8 7 8 5.5 6.5C4 5 5 3 6.5 3S10 5 10 8zM10 8C10 8 13 8 14.5 6.5C16 5 15 3 13.5 3S10 5 10 8z" stroke="currentColor" stroke-width="1.3"/></svg>`;
const ICON_PACKAGE = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 6l7-4 7 4v8l-7 4-7-4V6z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M3 6l7 4m0 0l7-4m-7 4v8" stroke="currentColor" stroke-width="1.3"/></svg>`;
const ICON_MEMBER = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2l2.09 4.26L17 7.27l-3.5 3.41.82 4.82L10 13.27l-4.32 2.23.82-4.82L3 7.27l4.91-1.01L10 2z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>`;
const ICON_WALLET = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" stroke-width="1.3"/><path d="M14 10.5a.5.5 0 100-1 .5.5 0 000 1z" fill="currentColor"/><path d="M2 8h16" stroke="currentColor" stroke-width="1.3"/></svg>`;
const ICON_FORM = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 2h12a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.3"/><path d="M7 6h6M7 9h6M7 12h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`;
const ICON_SIGNOUT = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_RECURRING = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 10a7 7 0 0112.45-4.36" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M17 10a7 7 0 01-12.45 4.36" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M15 3v3h-3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 17v-3h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_COPY = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M3 11V3a1 1 0 011-1h8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`;

// ── GC ARTWORK SVGs ────────────────────────────────────────────────────────

const GC_SVG_FULL = `<svg class="pp-gc-artwork-svg" viewBox="0 0 420 200" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="gcBg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#C9D9C2"/>
      <stop offset="100%" stop-color="#A8BFA0"/>
    </linearGradient>
  </defs>
  <rect width="420" height="200" fill="url(#gcBg)"/>
  <g opacity="0.75">
    <ellipse cx="14" cy="28" rx="42" ry="16" fill="#86A882" transform="rotate(-35 14 28)"/>
    <ellipse cx="44" cy="8"  rx="36" ry="13" fill="#96B890" transform="rotate(-15 44 8)"/>
    <ellipse cx="4"  cy="58" rx="34" ry="13" fill="#7A9C78" transform="rotate(-55 4 58)"/>
    <ellipse cx="62" cy="18" rx="28" ry="11" fill="#A0C09C" transform="rotate(-5 62 18)"/>
    <ellipse cx="28" cy="52" rx="30" ry="11" fill="#8AAE86" transform="rotate(-42 28 52)"/>
    <path d="M0,72 Q28,46 52,22" stroke="#6A9068" stroke-width="0.8" fill="none" opacity="0.6"/>
    <path d="M0,58 Q22,38 48,14" stroke="#6A9068" stroke-width="0.8" fill="none" opacity="0.5"/>
  </g>
  <g opacity="0.75" transform="translate(420,200) rotate(180)">
    <ellipse cx="14" cy="28" rx="42" ry="16" fill="#86A882" transform="rotate(-35 14 28)"/>
    <ellipse cx="44" cy="8"  rx="36" ry="13" fill="#96B890" transform="rotate(-15 44 8)"/>
    <ellipse cx="4"  cy="58" rx="34" ry="13" fill="#7A9C78" transform="rotate(-55 4 58)"/>
    <ellipse cx="62" cy="18" rx="28" ry="11" fill="#A0C09C" transform="rotate(-5 62 18)"/>
    <ellipse cx="28" cy="52" rx="30" ry="11" fill="#8AAE86" transform="rotate(-42 28 52)"/>
    <path d="M0,72 Q28,46 52,22" stroke="#6A9068" stroke-width="0.8" fill="none" opacity="0.6"/>
    <path d="M0,58 Q22,38 48,14" stroke="#6A9068" stroke-width="0.8" fill="none" opacity="0.5"/>
  </g>
  <text x="210" y="77"  text-anchor="middle" font-family="Georgia,serif" font-size="10.5" fill="#4A6E48" letter-spacing="5" opacity="0.9">A SPECIAL TREAT</text>
  <text x="210" y="116" text-anchor="middle" font-family="Georgia,serif" font-size="44"   fill="#364E34" font-weight="300" font-style="italic" letter-spacing="1">Gift Card</text>
  <text x="210" y="143" text-anchor="middle" font-family="Georgia,serif" font-size="10.5" fill="#4A6E48" letter-spacing="5" opacity="0.9">JUST FOR YOU</text>
</svg>`;

const GC_SVG_COMPACT = `<svg class="pp-gc-artwork-svg" viewBox="0 0 420 160" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="gcBg2" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#C9D9C2"/>
      <stop offset="100%" stop-color="#A8BFA0"/>
    </linearGradient>
  </defs>
  <rect width="420" height="160" fill="url(#gcBg2)"/>
  <g opacity="0.7">
    <ellipse cx="14" cy="22" rx="38" ry="14" fill="#86A882" transform="rotate(-35 14 22)"/>
    <ellipse cx="40" cy="6"  rx="32" ry="11" fill="#96B890" transform="rotate(-15 40 6)"/>
    <ellipse cx="4"  cy="46" rx="30" ry="11" fill="#7A9C78" transform="rotate(-55 4 46)"/>
  </g>
  <g opacity="0.7" transform="translate(420,160) rotate(180)">
    <ellipse cx="14" cy="22" rx="38" ry="14" fill="#86A882" transform="rotate(-35 14 22)"/>
    <ellipse cx="40" cy="6"  rx="32" ry="11" fill="#96B890" transform="rotate(-15 40 6)"/>
    <ellipse cx="4"  cy="46" rx="30" ry="11" fill="#7A9C78" transform="rotate(-55 4 46)"/>
  </g>
  <text x="210" y="62"  text-anchor="middle" font-family="Georgia,serif" font-size="10" fill="#4A6E48" letter-spacing="5" opacity="0.9">A SPECIAL TREAT</text>
  <text x="210" y="97"  text-anchor="middle" font-family="Georgia,serif" font-size="38" fill="#364E34" font-weight="300" font-style="italic" letter-spacing="1">Gift Card</text>
  <text x="210" y="120" text-anchor="middle" font-family="Georgia,serif" font-size="10" fill="#4A6E48" letter-spacing="5" opacity="0.9">JUST FOR YOU</text>
</svg>`;


// ── CONTACT STRIP + RESCHEDULE CART (pure, tested) ────────────────────────

/** The spa's address and number as tappable links under the portal's welcome
 *  (Tim, 2026-11-02): the address opens the phone's maps app (Apple Maps on
 *  an iPhone, Google Maps elsewhere), the number dials or texts according to
 *  what the line takes (Location.phone_actions). '' when the spa has neither. */
export function contactStrip(phone: string, phoneActions: string, address: string, ua?: string): string {
  const rows: string[] = [];
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (phone) {
    const digits = phone.replace(/[^\d+]/g, '');
    // A stored number is bare digits ('4232182841'); show it the way people read it.
    const d10 = digits.replace(/^\+?1/, '');
    const shown = /^\d{10}$/.test(d10) ? `(${d10.slice(0, 3)}) ${d10.slice(3, 6)}-${d10.slice(6)}` : phone;
    const canCall = phoneActions !== 'text';
    const canText = phoneActions === 'text' || phoneActions === 'both';
    const links: string[] = [];
    if (canCall) links.push(`<a class="pp-contact-link" href="tel:${esc(digits)}">Call</a>`);
    if (canText) links.push(`<a class="pp-contact-link" href="sms:${esc(digits)}">Text</a>`);
    const primary = canCall ? `tel:${digits}` : `sms:${digits}`;
    rows.push(`<a class="pp-contact-row" href="${esc(primary)}">${ICON_PHONE}<span>${esc(shown)}</span></a>${links.length > 1 ? `<span class="pp-contact-links">${links.join('')}</span>` : ''}`);
  }
  if (address) {
    const apple = /iPhone|iPad|iPod|Macintosh/.test(agent);
    const href = apple
      ? `https://maps.apple.com/?q=${encodeURIComponent(address)}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    rows.push(`<a class="pp-contact-row" href="${esc(href)}" target="_blank" rel="noopener">${ICON_PIN}<span>${esc(address)}</span></a>`);
  }
  if (!rows.length) return '';
  return rows.map(r => `<span class="pp-contact-line">${r}</span>`).join('');
}

const ICON_PIN = `<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 14.5s-4.5-4.2-4.5-7.5a4.5 4.5 0 019 0c0 3.3-4.5 7.5-4.5 7.5z" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="7" r="1.6" stroke="currentColor" stroke-width="1.3"/></svg>`;
const ICON_PHONE = `<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 2h2.3l1.2 3-1.5 1a8 8 0 004.5 4.5l1-1.5 3 1.2v2.3a1 1 0 01-1 1A11.5 11.5 0 012.5 3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>`;

/** The appointment as a booking cart for the reschedule grid — every row of
 *  the visit, matched to the online menu BY ID first (a row's service name can
 *  differ from the menu's; a provider's display name too), by name as the
 *  fallback. Returns [] when any service of the visit is not on the online
 *  menu (the client is told to call) — a partial cart would move only part of
 *  the appointment's picture while the server moves all of it. */
export function rescheduleCart(
  items: PortalBookingItem[],
  categories: Category[],
  providers: Provider[],
  enhGroups: Record<string, EnhancementGroup>,
  opts: { anyProvider?: boolean; pinAll?: boolean } = {},
): BookingItem[] {
  const cart: BookingItem[] = [];
  for (const it of items) {
    let svc = null;
    for (const cat of categories) {
      svc = cat.services.find(x => it.serviceId != null && Number(x.id) === Number(it.serviceId))
        || cat.services.find(x => x.name === it.serviceName)
        || null;
      if (svc) break;
    }
    if (!svc) return [];
    // Only a REQUESTED therapist is kept (Tim, 2026-11-02); any other
    // preference opens the grid to everyone who does the service — the same
    // 'Anyone' / female / male choice a new booking makes. `pinAll` (a
    // reschedule REQUEST, which staff approve with the same provider) and a
    // requested therapist without `anyProvider` keep the person.
    const pref = (it.providerPreference || '').toLowerCase();
    const keep = opts.pinAll || (pref === 'requested' && !opts.anyProvider);
    const named = providers.find(p => it.staffId != null && !p.type?.startsWith('filter') && Number(p.id) === Number(it.staffId))
      || providers.find(p => !p.type?.startsWith('filter') && p.name === it.staffName);
    const filterType = pref === 'female' ? 'filter-female' : pref === 'male' ? 'filter-male' : 'filter-any';
    const prov = (keep ? named : null)
      || providers.find(p => p.type === filterType)
      || providers.find(p => p.type === 'filter-any')
      || named
      || null;
    if (!prov) return [];
    const wanted = new Set((it.addonIds || []).map(String));
    const addons: Addon[] = [];
    for (const g of Object.values(enhGroups || {})) {
      for (const a of g.addOns || []) if (wanted.has(String(a.id))) addons.push(a);
    }
    cart.push({
      service: svc,
      selectedAddons: addons,
      provider: prov,
      totalPrice: Number(it.totalPrice ?? (it as { price?: number }).price ?? svc.price) || svc.price,
      guestIndex: it.guestIndex ?? 0,
    });
  }
  return cart;
}


// ── FORMS TO COMPLETE: which are required before the visit ────────────────

/** 'Nov 4' from 'YYYY-MM-DD' (no year — these are upcoming visits). */
function _monthDay(iso: string): string {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Required forms first (Tim, 2026-11-02 — 'indicate which ones are
 *  required'), the soonest visit first within each. A required form carries the
 *  date of the visit it is for, when it belongs to one. */
export function formsWithRequirement(forms: FormSubmission[], bookings: Booking[] | null): Array<{ form: FormSubmission; required: boolean; byDate: string }> {
  const dateOf = new Map((bookings || []).map(b => [Number(b.id), String(b.date || '').slice(0, 10)]));
  return forms
    .map((form, i) => {
      const iso = form.appointmentId ? (dateOf.get(Number(form.appointmentId)) || '') : '';
      return { form, i, iso, required: !!form.requiredBeforeService, byDate: iso ? _monthDay(iso) : '' };
    })
    // Required first; within each, the soonest visit first (a form with no
    // visit last), then the order they came in.
    .sort((a, b) => Number(b.required) - Number(a.required)
      || (a.iso || '9999').localeCompare(b.iso || '9999') || a.i - b.i)
    .map(({ form, required, byDate }) => ({ form, required, byDate }));
}

/** The banner's rows and the line under its title. */
export function renderFormsToComplete(s: PortalState, forms: FormSubmission[], title: string, limit: number, extraClass = ''): string {
  if (!forms.length) return '';
  const rows = formsWithRequirement(forms, s.portalBookings);
  const req = rows.filter(r => r.required);
  // 'Required before your Nov 4 visit' when they all point at one visit.
  const dates = [...new Set(req.map(r => r.byDate).filter(Boolean))];
  const sub = req.length
    ? `<div class="pp-forms-required-note">${req.length === rows.length
        ? (rows.length === 1 ? 'It is required' : 'All are required')
        : `${req.length} ${req.length === 1 ? 'is' : 'are'} required`}${dates.length === 1 ? ` before your ${esc(dates[0])} visit` : ' before your visit'}.</div>`
    : '';
  return `
      <div class="pp-home-banner pp-home-banner-amber${extraClass ? ` ${extraClass}` : ''}">
        <div class="pp-home-banner-title">${title}</div>
        ${sub}
        ${rows.slice(0, limit).map(r => `<button class="pp-home-banner-row" data-action="portal-fill-form" data-id="${r.form.id}" ${disabled(s.portalFormBusy)}><span>${esc(r.form.templateName)}${r.required ? ` <em class="pp-form-required">Required${r.byDate ? ` before ${esc(r.byDate)}` : ''}</em>` : ''}</span><b>Fill in →</b></button>`).join('')}
      </div>`;
}

/** Above the reschedule grid (Tim, 2026-11-02): whose times these are. A
 *  therapist the client asked for keeps the grid to their times, with a way
 *  to see everyone's — and the soft warning once they do. */
export function rescheduleProviderNote(s: PortalState): string {
  if (!s.rescheduleBookingId) return '';
  const names = s.rescheduleRequestedNames;
  if (!names.length) return '';
  const who = names.length === 1 ? esc(names[0]) : names.map(esc).join(' and ');
  if (s.rescheduleAnyProvider) {
    return `
    <div class="pp-bk-notice-block pp-resched-warning" style="margin-bottom:12px;">
      Showing times with any of our therapists. If ${who} isn't free at the time you pick, your appointment moves to another therapist who does this service — we'll tell you who.
      <button class="pp-link-btn" data-action="reschedule-requested-only">Only show ${who}'s times</button>
    </div>`;
  }
  return `
    <div class="pp-bk-notice-block pp-resched-note" style="margin-bottom:12px;">
      Showing times with ${who}, who you asked for.
      <button class="pp-link-btn" data-action="reschedule-any-provider">Also show times with other therapists</button>
    </div>`;
}

// ── HEADER ─────────────────────────────────────────────────────────────────

export function renderHeader(s: PortalState): string {
  const logo = s.spaLogoUrl
    ? `<img src="${esc(s.spaLogoUrl)}" class="pp-logo-img" alt="Logo" style="height:32px;width:auto">`
    : `<svg class="pp-logo-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="14.5" stroke="currentColor" stroke-width="1.2"/>
        <path d="M10 16a6 6 0 1112 0" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
        <circle cx="16" cy="11" r="3" stroke="currentColor" stroke-width="1.2"/>
      </svg>`;

  const backBtn = s.canGoBack
    ? `<button class="pp-back-btn" data-action="go-back" aria-label="Go back">${ICON_BACK}</button>`
    : '';

  const closeBtn = s.isStandalone ? '' :
    `<button class="pp-close-btn" data-action="close-panel" aria-label="Close">${ICON_CLOSE}</button>`;
  // Hosted /book/ page that led with the same-day surface: a way back to it,
  // otherwise the root screen has no exit at all.
  const todayBtn = s.canReturnToToday
    ? `<button class="pp-today-return" data-action="return-today" aria-label="Back to today's openings">${ICON_BACK}<span>Today's openings</span></button>`
    : '';

  return `
    <div class="pp-header-left">
      ${backBtn}
      <div class="pp-logo">
        ${logo}
        <div class="pp-logo-text">${esc(s.spaName)}</div>
      </div>
    </div>
    <div class="pp-header-right">
      ${todayBtn}
      ${s.client && s.step !== 'dashboard' ? `<button class="pp-home-btn" data-action="go-dashboard" aria-label="My account"><svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="7" r="3.5" stroke="currentColor" stroke-width="1.3"/><path d="M2.5 17c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></button>` : ''}
      ${s.client ? `<button class="pp-signout-btn" data-action="sign-out" aria-label="Sign out">${ICON_SIGNOUT}</button>` : ''}
      ${closeBtn}
    </div>`;
}

// ── BREADCRUMB ─────────────────────────────────────────────────────────────

export function renderBreadcrumb(s: PortalState): string {
  const step = s.step;
  const backSvg = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M7 2L3 5L7 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;


  // Portal sections (bookings, an appointment's details, receipts, wallet…)
  // carry one link home: '← Back to account' (Tim, 2026-10-18). The
  // dashboard, sign-in and the booking flow keep their own navigation.
  if (!step.startsWith('book-') && !step.startsWith('gc-') && !step.startsWith('pkg-') && !step.startsWith('mem-')) {
    if (s.entryMode === 'portal' && s.clientId && !['dashboard', 'login', 'register'].includes(step)) {
      return `<button class="pp-bc-back pp-bc-back--account" data-action="go-dashboard">${backSvg} Back to account</button>`;
    }
    return '';
  }
  if (step === 'book-location' || step === 'book-categories' || step === 'book-success' || step === 'book-pending-text' || step === 'book-rebook-pick') return '';
  if (step === 'gc-success') return '';
  if (step === 'pkg-success' || step === 'pkg-list') return '';
  if (step === 'mem-success' || step === 'mem-list') return '';

  // Package flow — simple back button
  if (step === 'pkg-contact' || step === 'pkg-payment' || step === 'mem-contact' || step === 'mem-payment') {
    return `<button class="pp-bc-back" data-action="go-back">${backSvg} Back</button>`;
  }

  // Gift card breadcrumb
  if (step === 'gc-details' || step === 'gc-payment') {
    return `
      <button class="pp-bc-back" data-action="gc-go-back">${backSvg} Back</button>
      <span class="pp-bc-sep">&rsaquo;</span>
      <span class="pp-bc-step ${step === 'gc-details' ? 'active' : 'done'}">Details</span>
      <span class="pp-bc-sep">&rsaquo;</span>
      <span class="pp-bc-step ${step === 'gc-payment' ? 'active' : 'future'}">Payment</span>
      <span class="pp-bc-sep">&rsaquo;</span>
      <span class="pp-bc-step future">Done</span>`;
  }

  // Standard booking breadcrumb
  return `
    ${s.canGoBack ? `<button class="pp-bc-back" data-action="go-back">${backSvg} Back</button>
    <span class="pp-bc-sep">&rsaquo;</span>` : ''}
    <span class="pp-bc-step ${s.bcClass('service')}">Service</span>
    <span class="pp-bc-sep">&rsaquo;</span>
    <span class="pp-bc-step ${s.bcClass('date')}">Date</span>
    <span class="pp-bc-sep">&rsaquo;</span>
    <span class="pp-bc-step ${s.bcClass('confirm')}">Confirm</span>
    <span class="pp-bc-sep">&rsaquo;</span>
    <span class="pp-bc-step ${s.bcClass('success')}">Success</span>`;
}

// ── BODY DISPATCHER ────────────────────────────────────────────────────────

function brandingHtml(apiBase: string): string {
  let origin = '';
  try { origin = new URL(apiBase).origin; } catch {}
  const logoUrl = origin + '/static/calendoula-logo.png';
  return `<div class="pp-branding"><a href="https://getcalendoula.com" target="_blank" rel="noopener"><img src="${logoUrl}" alt="" class="pp-branding-logo" width="18" height="18" /> Powered by Calendoula</a></div>`;
}

export function renderBody(s: PortalState): string {
  let content: string;
  switch (s.step) {
    // Portal steps
    case 'login':              content = renderLogin(s); break;
    case 'register':           content = renderRegister(s); break;
    case 'dashboard':          content = renderDashboard(s); break;
    case 'bookings':           content = renderBookings(s); break;
    case 'booking-detail':     content = renderBookingDetail(s); break;
    case 'sales':              content = renderSales(s); break;
    case 'sale-detail':        content = renderSaleDetail(s); break;
    case 'giftcards':          content = renderGiftCards(s); break;
    case 'packages':           content = renderPortalPackages(s); break;
    case 'memberships':        content = renderMemberships(s); break;
    case 'wallet':             content = renderWallet(s); break;
    case 'wallet-pay':         content = renderWalletPay(s); break;
    case 'message':            content = renderPortalMessage(s); break;
    case 'today':              content = '<div id="pp-portal-today-host" class="pp-portal-today"></div>'; break;
    case 'preferences':        content = renderPortalPreferences(s); break;
    case 'forms':              content = renderForms(s); break;
    case 'form-detail':        content = renderFormDetail(s); break;
    case 'recurring':          content = renderRecurring(s); break;
    case 'recurring-detail':   content = renderRecurringDetail(s); break;
    case 'book-rebook-pick':   content = renderRebookPick(s); break;
    // Booking flow steps
    case 'book-ai':            content = renderBkAi(s); break;
    case 'book-location':      content = renderBkLocation(s); break;
    case 'book-categories':    content = renderBkCategories(s); break;
    case 'book-enhancements':  content = renderBkEnhancements(s); break;
    case 'book-provider':      content = renderBkProvider(s); break;
    case 'book-addAnother':    content = renderBkAddAnother(s); break;
    case 'book-datetime':      content = renderBkDatetime(s); break;
    case 'book-contact':       content = renderBkContact(s); break;
    case 'book-payment':       content = renderBkPayment(s); break;
    case 'book-pending-text':  content = renderBkPendingText(s); break;
    case 'book-resume':        content = renderBkResume(s); break;
    case 'book-hold-exists':   content = renderBkHoldExists(s); break;
    case 'book-card-on-file':  content = renderBkCardOnFile(s); break;
    case 'book-confirm':       content = renderBkConfirm(s); break;
    case 'book-success':       content = renderBkSuccess(s); break;
    // Gift card flow
    case 'gc-details':         content = renderGcDetails(s); break;
    case 'gc-payment':         content = renderGcPayment(s); break;
    case 'gc-success':         content = renderGcSuccess(s); break;
    // Package purchase flow
    case 'pkg-list':           content = renderPkgList(s); break;
    case 'pkg-contact':        content = renderPkgContact(s); break;
    case 'pkg-payment':        content = renderPkgPayment(s); break;
    case 'pkg-success':        content = renderPkgSuccess(s); break;
    // Membership enrollment flow
    case 'mem-list':           content = renderMemList(s); break;
    case 'mem-contact':        content = renderMemContact(s); break;
    case 'mem-payment':        content = renderMemPayment(s); break;
    case 'mem-success':        content = renderMemSuccess(s); break;
    default:                   content = ''; break;
  }
  return content + brandingHtml(s.apiBase);
}

// ── FOOTER ─────────────────────────────────────────────────────────────────

export function renderFooter(s: PortalState): string {
  if (!s.showFooter) return '';
  const isLoading = s.gcSubmitting || s.pkgSubmitting || s.bookingSubmitting;
  // On the contact step a blocked Continue stays TAPPABLE: a disabled button
  // cannot receive the click that would explain itself (and is silent to
  // screen readers). It looks muted, and tapping it lists what's missing.
  const explainable = (s.step === 'book-contact' || s.step === 'book-payment') && !isLoading;
  const blocked = !s.canProceed;
  const banner = explainable && blocked && s.showBlockedSummary
    ? `<div class="pp-blocked-banner" role="alert">
        <div class="pp-blocked-title">Almost there — still needed:</div>
        ${s.blockedReasons.map(r =>
          `<button type="button" class="pp-blocked-item" data-action="goto-missing" data-target="${esc(r.field)}">${esc(r.label)}</button>`
        ).join('')}
      </div>`
    : '';
  return `
    ${banner}
    <button class="pp-footer-btn${isLoading ? ' is-loading' : ''}${explainable && blocked ? ' pp-footer-btn--blocked' : ''}"
      data-action="footer-action" ${disabled(blocked && !explainable)}>
      ${esc(s.footerLabel)}
    </button>`;
}

// ── MODAL ──────────────────────────────────────────────────────────────────

export function renderModal(s: PortalState): string {
  if (s.leaveDepositModal && s.deposit) {
    const d = s.deposit;
    return `
    <div class="pp-modal-backdrop">
      <div class="pp-modal-card pp-leave-card">
        <div class="pp-modal-title">Your appointment is reserved</div>
        <div class="pp-modal-body">It stays yours once the $${d.amount.toFixed(2)} deposit is paid — the hold lasts until then. What would you like to do?</div>
        <div class="pp-leave-actions">
          <button class="pp-btn-primary" data-action="leave-continue" ${s.leaveDepositBusy ? 'disabled' : ''}>Continue to deposit</button>
          <button class="pp-btn-outline" data-action="leave-text" ${s.leaveDepositBusy ? 'disabled' : ''}>${s.leaveDepositBusy ? 'Working…' : 'Text me the deposit link now'}</button>
          <button class="pp-bk-gc-link pp-leave-cancel" data-action="leave-cancel" ${s.leaveDepositBusy ? 'disabled' : ''}>Cancel and release my reservation</button>
        </div>
      </div>
    </div>`;
  }
  const removeModal = walletRemoveModal(s);
  if (removeModal) return removeModal;
  if (!s.modalSvc) return '';
  return `
    <div class="pp-modal-backdrop" data-action="close-modal">
      <div class="pp-modal-card">
        <button class="pp-modal-close-btn" data-action="close-modal" aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M11 3L3 11M3 3L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="pp-modal-title">${esc(s.modalSvc.name)}</div>
        <div class="pp-modal-body">${esc(s.modalSvc.description || '')}</div>
      </div>
    </div>`;
}

// ── SHARED ─────────────────────────────────────────────────────────────────

function renderLoading(): string {
  return `
    <div class="pp-loading">
      <div class="pp-spinner"></div>
      <div class="pp-loading-text">Loading...</div>
    </div>`;
}

function renderEmpty(title: string, subtitle: string): string {
  return `
    <div class="pp-empty">
      <div class="pp-empty-title">${esc(title)}</div>
      ${subtitle ? `<div class="pp-empty-subtitle">${esc(subtitle)}</div>` : ''}
    </div>`;
}

// ── LOGIN ──────────────────────────────────────────────────────────────────

function renderLogin(s: PortalState): string {
  const errorHtml = s.loginError
    ? `<div class="pp-login-error">${esc(s.loginError)}</div>` : '';

  // Multi-account picker
  if (s.multiAccountClients) {
    const clientRows = s.multiAccountClients.map(c => `
      <button class="pp-list-item" data-action="pick-client" data-clientid="${c.id}" style="display:flex;align-items:center;gap:12px;padding:14px 16px;border:1px solid var(--pp-border,#E5DDD6);border-radius:8px;background:#fff;cursor:pointer;width:100%;text-align:left;margin-bottom:8px">
        <div style="flex:1">
          <div style="font-weight:600;font-size:15px;color:var(--pp-espresso,#1E1813)">${esc(c.name)}</div>
          ${c.phoneMasked ? `<div style="font-size:13px;color:var(--pp-muted,#8A7D73);margin-top:2px">${esc(c.phoneMasked)}</div>` : ''}
        </div>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="color:var(--pp-muted,#8A7D73)"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    `).join('');

    return `
      <div class="pp-login-wrap">
        <div class="pp-login-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="14" cy="16" r="5" stroke="currentColor" stroke-width="1.5"/>
            <circle cx="26" cy="16" r="5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M4 34c0-5.523 4.477-10 10-10M26 24c5.523 0 10 4.477 10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="pp-login-title">Select Account</div>
        <div class="pp-login-subtitle">Multiple accounts were found for this email. Please select yours.</div>
        ${errorHtml}
        <div style="margin-top:16px">${clientRows}</div>
        <button class="pp-btn-link" data-action="otp-back" style="margin-top:12px;font-size:13px;color:#888;background:none;border:none;cursor:pointer">
          Use a different phone or email
        </button>
      </div>`;
  }

  if (s.otpSent) {
    const debugHint = s.otpDebugCode
      ? `<div class="pp-login-debug" style="margin-top:8px;padding:8px 12px;background:#fffbe6;border:1px solid #ffe58f;border-radius:6px;font-size:12px;color:#874d00;">Dev mode — code: <strong>${esc(s.otpDebugCode)}</strong></div>`
      : '';
    const noticeHint = s.otpNotice
      ? `<div class="pp-login-notice" style="margin-top:8px;padding:8px 12px;background:#fff4e5;border:1px solid #ffc48a;border-radius:6px;font-size:12px;color:#8a4b00;">${esc(s.otpNotice)}</div>`
      : '';
    return `
      <div class="pp-login-wrap">
        <div class="pp-login-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect x="8" y="12" width="24" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 18h16M12 23h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="pp-login-title">Verification</div>
        <div class="pp-login-subtitle">We sent a 6-digit code to <strong>${esc(s.loginQuery)}</strong>. Enter it below.</div>
        ${errorHtml}
        ${noticeHint}
        ${debugHint}
        <div class="pp-login-form">
          <input class="pp-input" type="text" placeholder="Enter 6-digit code"
            value="${esc(s.otpCode)}" data-field="otpCode"
            maxlength="6" inputmode="numeric" autocomplete="one-time-code"
            style="text-align:center;font-size:20px;letter-spacing:6px">
          <button class="pp-btn-primary${s.loginLoading ? ' is-loading' : ''}"
            data-action="login-submit" ${s.loginLoading || s.otpCode.trim().length < 6 ? 'disabled' : ''}>
            ${s.loginLoading ? 'Verifying...' : 'Verify'}
          </button>
          <button class="pp-btn-link" data-action="otp-resend" style="margin-top:8px;font-size:13px;color:var(--pp-accent,#B8623E);background:none;border:none;cursor:pointer">
            Resend code
          </button>
          <button class="pp-btn-link" data-action="otp-back" style="margin-top:4px;font-size:13px;color:#888;background:none;border:none;cursor:pointer">
            Use a different phone or email
          </button>
        </div>
      </div>`;
  }

  // Birthday auth: phone + birthday in one step
  if (s.authMethod === 'birthday') {
    const canSubmit = !!(s.loginQuery.trim() && s.loginBirthday);
    return `
      <div class="pp-login-wrap">
        <div class="pp-login-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="16" r="6" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 34c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="pp-login-title">Welcome</div>
        <div class="pp-login-subtitle">Enter your phone number and birthday to access your account.</div>
        ${errorHtml}
        <div class="pp-login-form">
          <input class="pp-input" type="text" placeholder="Phone number"
            value="${esc(s.loginQuery)}" data-field="loginQuery"
            autocomplete="tel">
          <input type="date" class="pp-input" data-field="loginBirthday" value="${s.loginBirthday}" />
          <button class="pp-btn-primary${s.loginLoading ? ' is-loading' : ''}"
            data-action="login-submit" ${s.loginLoading || !canSubmit ? 'disabled' : ''}>
            ${s.loginLoading ? 'Verifying...' : 'Log In'}
          </button>
        </div>
      </div>`;
  }

  // OTP auth: phone/email → send code → verify
  return `
    <div class="pp-login-wrap">
      <div class="pp-login-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="16" r="6" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 34c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <div class="pp-login-title">Welcome</div>
      <div class="pp-login-subtitle">Enter your phone number or email to access your account.</div>
      ${errorHtml}
      <div class="pp-login-form">
        <input class="pp-input" type="text" placeholder="Phone or email"
          value="${esc(s.loginQuery)}" data-field="loginQuery"
          autocomplete="tel email">
        <button class="pp-btn-primary${s.loginLoading ? ' is-loading' : ''}"
          data-action="login-submit" ${s.loginLoading || !s.loginQuery.trim() ? 'disabled' : ''}>
          ${s.loginLoading ? 'Sending code...' : 'Continue'}
        </button>
      </div>
    </div>`;
}

// ── REGISTER (new client self-registration) ────────────────────────────────

/** The sign-in code went to an email address, so no phone was verified. */
export function registrationNeedsPhone(s: PortalState): boolean {
  return s.loginQuery.includes('@');
}

function renderRegister(s: PortalState): string {
  const errorHtml = s.regError
    ? `<div class="pp-login-error">${esc(s.regError)}</div>` : '';

  // A code requested by email verified no phone; the account still needs one
  // (the phone is how the business finds you), so ask for it here.
  const needsPhone = registrationNeedsPhone(s);
  const canSubmit = s.regFirstName.trim() && s.regLastName.trim() && s.regEmail.trim()
    && (!needsPhone || s.regPhone.trim()) && !s.regLoading;
  const phoneHtml = needsPhone
    ? `<input class="pp-input" type="tel" placeholder="Mobile number"
          value="${esc(s.regPhone)}" data-field="regPhone" autocomplete="tel" inputmode="tel">`
    : '';

  return `
    <div class="pp-login-wrap">
      <div class="pp-login-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="14" r="6" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 34c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <line x1="32" y1="10" x2="32" y2="18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <line x1="28" y1="14" x2="36" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <div class="pp-login-title">Create Account</div>
      <div class="pp-login-subtitle">We don't have an account for this ${needsPhone ? 'email' : 'number'} yet. Fill in your details to get started.</div>
      ${errorHtml}
      <div class="pp-login-form">
        <input class="pp-input" type="text" placeholder="First name"
          value="${esc(s.regFirstName)}" data-field="regFirstName" autocomplete="given-name">
        <input class="pp-input" type="text" placeholder="Last name"
          value="${esc(s.regLastName)}" data-field="regLastName" autocomplete="family-name">
        <input class="pp-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Email address"
          value="${esc(s.regEmail)}" data-field="regEmail" autocomplete="email">
        ${phoneHtml}
        <label class="pp-checkbox-row" style="display:flex;align-items:center;gap:8px;margin-top:8px;font-size:14px;color:var(--pp-espresso,#1E1813);cursor:pointer">
          <input type="checkbox" data-field="regMarketingEmails" ${checked(s.regMarketingEmails)} style="width:16px;height:16px;accent-color:var(--pp-accent,#B8623E)">
          Receive promotional emails
        </label>
        <label class="pp-checkbox-row" style="display:flex;align-items:center;gap:8px;margin-top:4px;font-size:14px;color:var(--pp-espresso,#1E1813);cursor:pointer">
          <input type="checkbox" data-field="regMarketingTexts" ${checked(s.regMarketingTexts)} style="width:16px;height:16px;accent-color:var(--pp-accent,#B8623E)">
          Receive promotional texts
        </label>
        <button class="pp-btn-primary${s.regLoading ? ' is-loading' : ''}"
          data-action="register-submit" ${canSubmit ? '' : 'disabled'}>
          ${s.regLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </div>
    </div>`;
}

// ── DASHBOARD ──────────────────────────────────────────────────────────────

type NavPill = { pill?: string; pillTone?: 'accent' | 'green' | 'amber' | 'red' };

/** The home card's one line about today: a green light or a red one, never a
 *  count or a time (Tim, 2026-10-13 — '100 openings' was one start per provider
 *  per 5 min, and 'about 3 hours free' was more than anyone needs). */
export function openingsLine(open: boolean): string {
  return open
    ? 'Openings available'
    : 'No openings currently available \u2014 please reach out to see if we can accommodate you today';
}

/** '16:30' → '4:30 PM'; anything already in 12-hour form (or blank) passes through. */
export function time12(t: string | null | undefined): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || '').trim());
  if (!m) return String(t || '');
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** Whole days from today to a YYYY-MM-DD date (negative = past), null without one. */
export function daysUntil(dateStr: string | null | undefined, today: Date = new Date()): number | null {
  if (!dateStr) return null;
  const t = new Date(today.toISOString().split('T')[0] + 'T00:00:00Z').getTime();
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00Z').getTime();
  if (!Number.isFinite(d)) return null;
  return Math.round((d - t) / 86400000);
}

/** 'Expires Mar 3, 2028' / 'Expires in 12 days' / 'Expired Jan 2, 2026', with a tone. */
export function expiryLine(dateStr: string | null | undefined, soonDays: number, today: Date = new Date()): { text: string; tone: 'ok' | 'amber' | 'red' } | null {
  const d = daysUntil(dateStr, today);
  if (d === null) return null;
  if (d < 0) return { text: `Expired ${formatDate(String(dateStr).slice(0, 10))}`, tone: 'red' };
  if (d === 0) return { text: 'Expires today', tone: 'red' };
  if (d <= soonDays) return { text: `Expires in ${d} day${d === 1 ? '' : 's'} (${formatDate(String(dateStr).slice(0, 10))})`, tone: 'amber' };
  return { text: `Expires ${formatDate(String(dateStr).slice(0, 10))}`, tone: 'ok' };
}

/** What each home card says at a glance — only when there is something to
 *  say (a card with nothing behind it stays plain). Reads the wallet and
 *  packages the dashboard prefetches; before they arrive, nothing. */
export function dashboardIndicators(s: {
  wallet?: { giftCards?: { currentBalance: number; status: string; daysToExpiry?: number | null }[]; promotions?: { status: string }[]; accountBalance?: number } | null;
  portalPackages?: { qtyRemaining: number; expirationDate?: string | null }[] | null;
  portalBookings?: { date: string; status: string }[] | null;
  upcomingBookingsCount?: number;
  activeMembershipsCount?: number;
  memberships?: { status: string }[] | null;
  portalSections?: { accountBalance: boolean };
  client?: { accountBalance?: number } | null;
}): { bookings: NavPill; giftCards: NavPill; packages: NavPill; memberships: NavPill; wallet: NavPill } {
  const out = { bookings: {} as NavPill, giftCards: {} as NavPill, packages: {} as NavPill, memberships: {} as NavPill, wallet: {} as NavPill };
  const up = s.upcomingBookingsCount || 0;
  if (up > 0) out.bookings = { pill: `${up} upcoming`, pillTone: 'accent' };

  const gcs = (s.wallet?.giftCards || []).filter(g => g.status !== 'cancelled' && g.status !== 'expired' && g.currentBalance > 0);
  if (gcs.length) {
    const total = gcs.reduce((sum, g) => sum + g.currentBalance, 0);
    const soon = gcs.some(g => typeof g.daysToExpiry === 'number' && g.daysToExpiry >= 0 && g.daysToExpiry <= 90);
    out.giftCards = { pill: `${formatCurrency(total)} on ${gcs.length === 1 ? '1 card' : gcs.length + ' cards'}${soon ? ' · expiring soon' : ''}`, pillTone: soon ? 'amber' : 'green' };
  }

  const pkgs = (s.portalPackages || []).filter(p => p.qtyRemaining > 0);
  if (pkgs.length) {
    const left = pkgs.reduce((sum, p) => sum + p.qtyRemaining, 0);
    const soonest = pkgs.map(p => daysUntil(p.expirationDate)).filter((d): d is number => d !== null && d >= 0).sort((a, b) => a - b)[0];
    const soon = soonest !== undefined && soonest <= 30;
    out.packages = { pill: `${left} session${left === 1 ? '' : 's'} left${soon ? ` · expires in ${soonest} day${soonest === 1 ? '' : 's'}` : ''}`, pillTone: soon ? 'amber' : 'green' };
  }

  const mem = s.activeMembershipsCount || 0;
  const pastDue = (s.memberships || []).some(m => m.status === 'past_due' || m.status === 'unpaid');
  if (pastDue) out.memberships = { pill: 'Payment needed', pillTone: 'red' };
  else if (mem > 0) out.memberships = { pill: mem === 1 ? 'Active' : `${mem} active`, pillTone: 'green' };

  // The Wallet pill carries the balance AND the offers (the summary tiles are
  // gone, so this is where the balance lives): 'Owes $24.00 · 1 offer',
  // '$125.00 credit · 2 offers', '2 offers'.
  const showBalance = s.portalSections?.accountBalance !== false;
  const balance = s.wallet?.accountBalance ?? s.client?.accountBalance ?? 0;
  const promos = (s.wallet?.promotions || []).filter(p => p.status === 'active').length;
  const offerText = promos > 0 ? `${promos} offer${promos === 1 ? '' : 's'}` : '';
  const parts: string[] = [];
  let tone: NavPill['pillTone'] = 'amber';
  if (showBalance && balance < 0) { parts.push(`Owes ${formatCurrency(-balance)}`); tone = 'red'; }
  else if (showBalance && balance > 0) { parts.push(`${formatCurrency(balance)} credit`); tone = 'green'; }
  if (offerText) parts.push(offerText);
  if (parts.length) out.wallet = { pill: parts.join(' · '), pillTone: tone };
  return out;
}

/** 'Add <spa> to your home screen': Chrome's one-tap install, or on an
 *  iPhone the Share → Add to Home Screen steps. Dismissable, per device. */
export function renderInstallBanner(s: { installOffer: 'prompt' | 'ios' | null; installHelpOpen: boolean; spaName?: string }): string {
  const offer = s.installOffer;
  if (!offer) return '';
  const spa = s.spaName ? esc(s.spaName) : 'us';
  const steps = offer === 'ios' && s.installHelpOpen ? `
      <ol class="pp-install-steps">
        <li>Tap the <b>Share</b> button at the bottom of Safari <span class="pp-install-share" aria-hidden="true">⎙</span></li>
        <li>Choose <b>Add to Home Screen</b></li>
        <li>Tap <b>Add</b></li>
      </ol>` : '';
  return `
    <div class="pp-install-banner" role="region" aria-label="Install the app">
      <button class="pp-install-close" data-action="pwa-dismiss" aria-label="Not now">×</button>
      <div class="pp-install-title">Add ${spa} to your home screen</div>
      <div class="pp-install-desc">One tap to your appointments, receipts and more — no app store needed.</div>
      <button class="pp-btn-primary pp-install-btn" data-action="pwa-install">${offer === 'prompt' ? 'Install' : (s.installHelpOpen ? 'Hide steps' : 'Show me how')}</button>
      ${steps}
    </div>`;
}

function renderDashboard(s: PortalState): string {
  // First load: one loading screen, then everything at once (see dashboardHolding).
  if (s.dashboardHolding) return renderLoading();
  const name = s.client?.firstName || 'there';

  const sections = s.portalSections;
  const showMemberships = sections.memberships && !s.membershipsSilent;
  // The summary tiles are gone (Tim, 2026-10-12): the numbers ride on the
  // cards as pills — '1 upcoming', '$125.00 credit', 'Payment needed'.
  const stats = '';
  // One compact line under the name: client since · where we are · how to
  // reach us — each a tap (maps, tel:, sms:) (Tim, 2026-11-02).
  const sinceTxt = s.client?.clientSince ? `<span class="pp-client-since">Client since ${esc(formatDate(s.client.clientSince))}</span>` : '';
  const contactTxt = contactStrip(s.businessPhone, s.businessPhoneActions, s.businessAddress);
  const since = contactTxt ? `<div class="pp-welcome-meta">${contactTxt}</div>` : '';

  // The next visit, right at the top — it used to live two taps away.
  const next = s.nextAppointment;
  const nextCard = next ? (() => {
    const it = next.items[0];
    const when = `${formatDate(next.date)}${it?.startTime ? ` · ${esc(time12(it.startTime))}` : ''}`;
    const what = next.items.map(i => esc(i.serviceName)).join(' + ');
    const who = it?.staffName ? ` with ${esc(it.staffName)}` : '';
    const resched = next.selfService?.reschedule?.mode && next.selfService.reschedule.mode !== 'none'
      ? `<button class="pp-btn-outline pp-next-btn" data-action="client-reschedule-booking" data-id="${next.id}">${next.selfService.reschedule.mode === 'request' ? 'Request to reschedule' : 'Reschedule'}</button>` : '';
    return `
      <div class="pp-next-card">
        <div class="pp-next-label">Your next visit</div>
        <div class="pp-next-when">${when}</div>
        <div class="pp-next-what">${what}${who}</div>
        <div class="pp-next-actions">
          <button class="pp-btn-primary pp-next-btn" data-action="view-booking" data-id="${next.id}">Details</button>
          ${resched}
        </div>
      </div>`;
  })() : '';

  const installBanner = renderInstallBanner(s);

  // A deposit still owed: the hold's clock, and the deposit step a tap away.
  const pd = s.pendingDepositBooking;
  const depositBanner = pd ? (() => {
    const it = pd.items[0];
    const exp = String(pd.deposit?.payExpiresAt || '');
    const amt = typeof pd.deposit?.depositAmount === 'number' ? formatCurrency(pd.deposit.depositAmount) : '';
    return `
      <div class="pp-home-banner pp-home-banner-red" data-hold-expires="${esc(exp)}">
        <div class="pp-home-banner-title">Deposit due${amt ? ` · ${amt}` : ''}</div>
        <div class="pp-deposit-banner-what">${esc(it?.serviceName || 'Your appointment')} · ${esc(formatDate(pd.date))}${it?.startTime ? ` at ${esc(time12(it.startTime))}` : ''}</div>
        <div class="pp-deposit-banner-clock">Your time is held for <b class="pp-bk-hold-clock">${esc(holdCountdown(exp))}</b></div>
        <button class="pp-btn-primary pp-deposit-banner-btn" data-action="portal-pay-deposit" data-id="${pd.id}">Pay deposit</button>
      </div>`;
  })() : '';

  // Same-day openings right now → the 'Open today' surface inside the portal.
  // Not for someone already coming in today (Tim, 2026-10-13).
  const sd = s.sameDayToday;
  const visitingToday = (s.portalBookings || []).some(b => b.date === s.todayStr && !NOT_UPCOMING.includes(b.status));
  const todayCard = sd && !visitingToday ? (sd.open ? `
      <button class="pp-today-card" data-action="nav-today">
        <div class="pp-today-card-main">
          <div class="pp-today-card-title">Open today</div>
          <div class="pp-today-card-desc"><span class="pp-today-light pp-today-light-green" aria-hidden="true"></span>${openingsLine(true)}</div>
        </div>
        <div class="pp-nav-card-chevron">${ICON_CHEVRON}</div>
      </button>` : `
      <div class="pp-today-card pp-today-card-closed">
        <div class="pp-today-card-main">
          <div class="pp-today-card-title">Open today</div>
          <div class="pp-today-card-desc"><span class="pp-today-light pp-today-light-red" aria-hidden="true"></span>${openingsLine(false)}</div>
        </div>
      </div>`) : '';

  // Forms the spa is waiting on, before the visit.
  const todo = s.formsToComplete;
  const formsBanner = renderFormsToComplete(s, todo,
    todo.length === 1 ? 'A form to complete before your visit' : `${todo.length} forms to complete before your visit`, 4);
  // An intake form over a year old: a nudge, never a block.
  const stale = s.staleForms;
  const staleBanner = !todo.length && stale.length ? `
      <div class="pp-home-banner">
        <div class="pp-home-banner-title">Is this still right?</div>
        ${stale.slice(0, 3).map(f => `<button class="pp-home-banner-row" data-action="portal-refresh-form" data-id="${f.templateId}" ${disabled(s.portalFormBusy)}><span>Your ${esc(f.templateName)} is over a year old</span><b>Update it →</b></button>`).join('')}
      </div>` : '';

  const ICON_CHAT = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>`;

  const ind = dashboardIndicators(s);
  type CardDef = { icon: string; label: string; action: string; desc: string; pill?: string; pillTone?: 'accent' | 'green' | 'amber' | 'red' };
  const cardDefs: CardDef[] = [
    { icon: ICON_CALENDAR, label: 'Bookings', action: 'nav-bookings', desc: 'View upcoming & past', ...ind.bookings },
    { icon: ICON_RECEIPT,   label: 'Receipts', action: 'nav-sales',    desc: 'Sales & receipts' },
    { icon: ICON_GIFT,      label: 'Gift Cards', action: 'nav-giftcards', desc: 'Cards & balances', ...ind.giftCards },
    ...(sections.packages ? [{ icon: ICON_PACKAGE, label: 'Packages', action: 'nav-packages', desc: 'Your packages', ...ind.packages }] : []),
    // Only when there is something to show: a plan on sale, or one they hold.
    // The card used to render for a spa with nothing to sell, leading to
    // "No membership plans available at this time."
    ...(showMemberships ? [
      { icon: ICON_MEMBER, label: 'Memberships', action: 'nav-memberships', desc: 'Plans & billing', ...ind.memberships },
    ] : []),
    { icon: ICON_WALLET,    label: 'Wallet', action: 'nav-wallet',     desc: sections.accountBalance ? 'Balance & cards' : 'Cards & offers', ...ind.wallet },
    { icon: ICON_FORM,      label: 'Forms',  action: 'nav-forms',     desc: 'Submitted forms', ...(todo.length ? { pill: `${todo.length} to complete`, pillTone: 'amber' as const } : {}) },
    { icon: ICON_RECURRING, label: 'Recurring', action: 'nav-recurring', desc: 'Manage recurring appts' },
    // Their own notes & preferences: on their record and every appointment.
    { icon: ICON_CHAT, label: 'My preferences', action: 'nav-preferences', desc: 'Pressure, allergies, anything we should know',
      ...(s.client?.portalNotes ? { pill: 'On file', pillTone: 'green' as const } : {}) },
  ];
  if (s.aiConciergeEnabled || s.webChatEnabled) {
    cardDefs.push({ icon: ICON_CHAT, label: s.aiConciergeEnabled ? 'AI Assistant' : 'Chat', action: 'nav-ai-concierge', desc: s.aiConciergeEnabled ? 'Ask questions or book' : 'Send us a message' });
  }
  // A message to the spa, answered by text — the Inbox thread the website's
  // 'get back to me' form uses (Tim, 2026-10-12).
  cardDefs.push({ icon: ICON_CHAT, label: 'Send us a message', action: 'nav-message', desc: "We'll text you back" });
  const cards = cardDefs.map(c => `
    <button class="pp-nav-card" data-action="${c.action}">
      <div class="pp-nav-card-icon">${c.icon}</div>
      <div class="pp-nav-card-info">
        <div class="pp-nav-card-label">${c.label}</div>
        <div class="pp-nav-card-desc">${c.desc}</div>
      </div>
      ${c.pill ? `<span class="pp-nav-pill pp-nav-pill-${c.pillTone || 'accent'}">${esc(c.pill)}</span>` : ''}
      <div class="pp-nav-card-chevron">${ICON_CHEVRON}</div>
    </button>
  `).join('');

  const bookForOtherBtn = s.showBookForSomeoneElse
    ? `<button class="pp-btn-outline" data-action="book-for-other">Book for Someone Else</button>`
    : '';
  // A group visit (me + others): the public flow, which knows how to book
  // several people with their own services, run for the signed-in client.
  const bookGroupBtn = s.allowGroupBookings && s.maxGroupSize >= 2
    ? `<button class="pp-btn-outline" data-action="portal-book-group">Book for Me &amp; Someone Else</button>`
    : '';
  const usual = s.usualBooking;
  const usualBtn = usual ? `<button class="pp-btn-primary pp-usual-btn" data-action="portal-usual-rebook">Book your usual<span class="pp-usual-sub">${esc(usual.items[0].serviceName)}${usual.items[0].staffName ? ` with ${esc(usual.items[0].staffName)}` : ''}</span></button>` : '';
  const bookNowBtn = `
    <div class="pp-book-btns">
      ${usualBtn}
      <button class="${usual ? 'pp-btn-outline' : 'pp-btn-primary'}" data-action="book-now">${usual ? 'Book something else' : 'Instant Book'}</button>
      ${bookGroupBtn}
      ${bookForOtherBtn}
    </div>`;

  const multiNote = s.multiAccountNote
    ? `<div style="margin-bottom:16px;padding:10px 14px;background:#e8f4fd;border:1px solid #b6d9f0;border-radius:8px;font-size:13px;color:#1a5276;line-height:1.4">
        <strong>Tip:</strong> ${esc(s.multiAccountNote)}
      </div>`
    : '';

  const referralSection = s.referralEnabled && s.referralCode ? `
    <div class="pp-referral-section">
      <div class="pp-referral-title">Refer a Friend</div>
      <div class="pp-referral-desc">Share your link and you'll both earn rewards!</div>
      ${s.referralShareUrl ? `
      <div class="pp-referral-code-row">
        <span class="pp-referral-code pp-referral-link">${esc(s.referralShareUrl)}</span>
        <button class="pp-referral-copy" data-action="copy-referral-link" title="Copy link">${ICON_COPY}</button>
      </div>
      <div class="pp-referral-btns">
        ${typeof navigator !== 'undefined' && 'share' in navigator ? `<button class="pp-btn-outline pp-referral-share" data-action="share-referral-link">Share my link</button>` : ''}
        <button class="pp-btn-outline pp-referral-share" data-action="toggle-referral-qr">${s.referralQrOpen ? 'Hide QR code' : 'Show QR code'}</button>
      </div>
      ${s.referralQrOpen ? `<div class="pp-referral-qr">${s.referralQrUrl ? `<img src="${esc(s.referralQrUrl)}" alt="QR code for your referral link" width="180" height="180"><div class="pp-referral-desc">A friend points their camera at this to book with your link.</div><a class="pp-link-btn" href="${esc(s.referralQrUrl)}" download="referral-${esc(s.referralCode)}.png">Save the image</a>` : '<div class="pp-referral-desc">Loading…</div>'}</div>` : ''}
      <div class="pp-referral-desc" style="margin-top:8px">Or give them your code:</div>` : ''}
      <div class="pp-referral-code-row">
        <span class="pp-referral-code">${esc(s.referralCode)}</span>
        <button class="pp-referral-copy" data-action="copy-referral" title="Copy code">${ICON_COPY}</button>
      </div>
      <div class="pp-referral-reward-desc">
        <span>Your friend gets: <strong>${esc(s.referralDiscountLabel)}</strong></span><br/>
        <span>You get: <strong>${esc(s.referralRewardDescription)}</strong></span>
      </div>
      ${s.referralTerms.length ? `<ul class="pp-referral-terms">${s.referralTerms.map(t => `<li>${esc(t)}</li>`).join('')}</ul>` : ''}
      <div class="pp-referral-stats">
        <span><b>${s.referralCount}</b> ${s.referralCount === 1 ? 'friend' : 'friends'} referred</span>
        <span><b>${formatCurrency(s.referralEarned)}</b> earned${s.referralPending ? ` · ${s.referralPending} pending` : ''}</span>
      </div>
    </div>` : '';

  const calSyncSection = s.calSyncEnabled ? `
    <div class="pp-referral-section">
      <div class="pp-referral-title">Calendar Sync</div>
      <div class="pp-referral-desc">Add your appointments to Google Calendar, Apple Calendar, Outlook, or any app that supports iCal feeds.</div>
      ${s.calSyncFeedUrl ? `
        <div class="pp-referral-code-row">
          <span class="pp-referral-code" style="font-size:11px;word-break:break-all">${esc(s.calSyncFeedUrl)}</span>
          <button class="pp-referral-copy" data-action="copy-cal-sync" title="Copy URL">${ICON_COPY}</button>
        </div>
        <a class="pp-btn-primary pp-cal-subscribe" href="${esc(s.calSyncFeedUrl.replace(/^https?:/, 'webcal:'))}">Subscribe in my calendar app</a>
        <div class="pp-referral-desc" style="margin-top:6px">On an iPhone or Mac that opens Calendar. In Google Calendar choose <em>Other calendars → From URL</em> and paste the link above. Appointments you book, move or cancel update on their own.</div>
        <div style="display:flex;gap:8px;margin-top:8px">
          <button class="pp-btn-outline" data-action="generate-cal-sync" style="flex:1;font-size:13px;padding:8px">Regenerate</button>
          <button class="pp-btn-outline" data-action="revoke-cal-sync" style="flex:1;font-size:13px;padding:8px;color:#c0392b;border-color:#c0392b">Remove</button>
        </div>
      ` : `
        <button class="pp-btn-primary" data-action="generate-cal-sync" style="margin-top:8px;width:100%"${s.calSyncLoading ? ' disabled' : ''}>
          ${s.calSyncLoading ? 'Generating...' : 'Generate Calendar Feed URL'}
        </button>
      `}
    </div>` : '';

  const loyaltySection = s.loyaltyEnabled && !s.loyaltyLoading ? (() => {
    const pts = s.loyaltyPoints;
    const tiers = s.loyaltyAvailableTiers || [];
    const nextTier = s.loyaltyNextTier;
    const toNext = s.loyaltyPointsToNextTier;

    let progressHtml = '';
    if (nextTier && nextTier.pointsCost > 0) {
      const pct = Math.min(100, Math.round((pts / nextTier.pointsCost) * 100));
      progressHtml = `
        <div class="pp-loyalty-progress-wrap">
          <div class="pp-loyalty-progress-label">
            <span>${pts.toLocaleString()} / ${nextTier.pointsCost.toLocaleString()} pts</span>
            <span>${esc(nextTier.name)}</span>
          </div>
          <div class="pp-loyalty-progress-bar">
            <div class="pp-loyalty-progress-fill" style="width:${pct}%"></div>
          </div>
          <div class="pp-loyalty-progress-hint">${toNext.toLocaleString()} more points to ${esc(nextTier.name)}</div>
        </div>`;
    }

    let tiersHtml = '';
    if (tiers.length > 0) {
      tiersHtml = `
        <div class="pp-loyalty-tiers-title">Available Rewards</div>
        <div class="pp-loyalty-tiers">
          ${tiers.map(t => `
            <div class="pp-loyalty-tier ${pts >= t.pointsCost ? 'pp-loyalty-tier--available' : 'pp-loyalty-tier--locked'}">
              <div class="pp-loyalty-tier-name">${esc(t.name)}</div>
              <div class="pp-loyalty-tier-cost">${t.pointsCost.toLocaleString()} pts</div>
              ${t.description ? `<div class="pp-loyalty-tier-desc">${esc(t.description)}</div>` : ''}
            </div>
          `).join('')}
        </div>`;
    }

    return `
      <div class="pp-referral-section pp-loyalty-section">
        <div class="pp-referral-title">Loyalty Points</div>
        <div class="pp-loyalty-balance">
          <span class="pp-loyalty-pts">${pts.toLocaleString()}</span>
          <span class="pp-loyalty-pts-label">points</span>
        </div>
        ${progressHtml}
        ${tiersHtml}
      </div>`;
  })() : '';

  const punchCardSection = s.punchCards.length > 0 && !s.punchCardsLoading ? (() => {
    const cardsHtml = s.punchCards.map(pc => {
      const pct = Math.min(100, Math.round((pc.currentPunches / pc.punchesNeeded) * 100));
      const dots = Array.from({ length: pc.punchesNeeded }, (_, i) =>
        `<span class="pp-punch-dot ${i < pc.currentPunches ? 'pp-punch-dot--filled' : ''}"></span>`
      ).join('');
      const badge = pc.status === 'completed'
        ? '<span class="pp-punch-complete-badge">Completed!</span>'
        : '';
      return `
        <div class="pp-punch-card">
          <div class="pp-punch-card-header">
            <span class="pp-punch-card-name">${esc(pc.templateName)}</span>
            ${badge}
          </div>
          ${pc.targetName ? `<div class="pp-punch-card-target">${esc(pc.targetName)}</div>` : ''}
          <div class="pp-punch-dots">${dots}</div>
          <div class="pp-punch-card-progress">
            <div class="pp-punch-bar">
              <div class="pp-punch-bar-fill" style="width:${pct}%"></div>
            </div>
            <span class="pp-punch-card-count">${pc.currentPunches}/${pc.punchesNeeded}</span>
          </div>
        </div>`;
    }).join('');

    return `
      <div class="pp-referral-section pp-punch-section">
        <div class="pp-referral-title">Punch Cards</div>
        ${cardsHtml}
      </div>`;
  })() : '';

  return `
    <div class="pp-dashboard">
      ${multiNote}
      <div class="pp-welcome-row"><div class="pp-welcome">Welcome back, ${esc(name)}</div>${sinceTxt}</div>
      ${since}
      ${depositBanner}
      ${s.reviewPromptDue ? renderReviewPrompt(s, false) : ''}
      ${renderOfferBanners(s)}
      ${installBanner}
      ${todayCard}
      ${nextCard}
      ${formsBanner}
      ${staleBanner}
      ${stats}
      <div class="pp-nav-grid">${cards}</div>
      ${bookNowBtn}
      ${loyaltySection}
      ${punchCardSection}
      ${referralSection}
      ${calSyncSection}
    </div>`;
}

// ── BOOKINGS ───────────────────────────────────────────────────────────────

function renderBookings(s: PortalState): string {
  if (s.bookingsLoading) return renderLoading();
  if (!s.portalBookings || s.portalBookings.length === 0) {
    return renderEmpty('No bookings yet', 'Your booking history will appear here.');
  }

  const today = new Date().toISOString().split('T')[0];
  const upcoming = s.portalBookings.filter(b => b.date >= today && b.status !== 'cancelled' && b.status !== 'completed');
  const past = s.portalBookings.filter(b => b.date < today || b.status === 'cancelled' || b.status === 'completed');

  let html = '';
  if (upcoming.length > 0) {
    html += `<div class="pp-section-title">Upcoming</div>`;
    html += upcoming.map(b => renderBookingRow(b)).join('');
  }
  if (past.length > 0) {
    html += `<div class="pp-section-title">Past</div>`;
    html += past.map(b => renderBookingRow(b)).join('');
  }
  return html;
}

function renderBookingRow(b: Booking): string {
  const svcNames = b.items.map(it => it.serviceName).join(', ');
  const provNames = [...new Set(b.items.map(it => it.staffName))].join(', ');
  return `
    <button class="pp-list-item" data-action="view-booking" data-id="${b.id}">
      <div class="pp-list-item-main">
        <div class="pp-list-item-title">${esc(svcNames || 'Appointment')}</div>
        <div class="pp-list-item-sub">${esc(formatDate(b.date))} · ${esc(b.time)} · ${esc(provNames)}${b.asGuest ? ` · with ${esc(b.guestOf || 'a friend')}` : ''}</div>
      </div>
      <div class="pp-list-item-right">
        <span class="${statusBadgeClass(b.status)}">${esc(b.status)}</span>
        <span class="pp-list-chevron">${ICON_CHEVRON}</span>
      </div>
    </button>`;
}

// ── BOOKING DETAIL ─────────────────────────────────────────────────────────

function renderBookingDetail(s: PortalState): string {
  const b = s.selectedBooking;
  if (!b) return renderEmpty('Booking not found', '');

  // Several people on the appointment: say who each service is for.
  const people = new Set(b.items.map(it => it.guestIndex ?? 0));
  const personLabel = (it: typeof b.items[0]): string => {
    if (people.size < 2) return '';
    const mine = it.clientId != null && Number(it.clientId) === Number(s.clientId);
    if (mine || (!b.asGuest && (it.guestIndex ?? 0) === 0)) return 'You · ';
    return `${esc(it.guestName || it.clientName || `Guest ${it.guestIndex ?? 0}`)} · `;
  };
  const itemsHtml = b.items.map(it => `
      <div class="pp-detail-item">
        <div class="pp-detail-item-name">${personLabel(it)}${esc(it.serviceName)}</div>
        <div class="pp-detail-item-sub">${esc(it.staffName)} · ${it.durationMins} min</div>
        <div class="pp-detail-item-price">${formatCurrency(Number(it.totalPrice ?? (it as { price?: number }).price ?? 0))}</div>
      </div>`
  ).join('');

  // Cancel / reschedule — the SERVER says what this client may do (do it,
  // ask for it, or neither) and what a late change costs. A server that does
  // not send it yet falls back to the old hour settings.
  // A guest on someone else's appointment changes nothing about it.
  const isCancellable = !b.asGuest && (b.status === 'unconfirmed' || b.status === 'confirmed');
  const legacy = (hours: number): SelfServiceTerms => ({
    mode: isCancellable && hours > 0 && _withinCutoff(b.date, b.time, hours) ? 'direct' : 'none',
    insideBlackout: false, forfeitDeposit: false, chargeDeposit: false, chargeFull: false, feeAmount: 0, feeKind: '',
  });
  const none: SelfServiceTerms = { ...legacy(0), mode: 'none' };
  const cancelT = !isCancellable ? none : (b.selfService ? b.selfService.cancel : legacy(s.selfCancelHours));
  const reschedT = !isCancellable ? none : (b.selfService ? b.selfService.reschedule : legacy(s.selfRescheduleHours));

  let actionsHtml = '';
  let cutoffHtml = '';
  if (b.pendingRequest) {
    const pr = b.pendingRequest;
    const what = pr.kind === 'cancel'
      ? 'to cancel this appointment'
      : `to move this appointment to ${esc(formatDate(pr.requestedDate))} at ${esc(formatTime12(pr.requestedTime))}`;
    actionsHtml = `
      <div class="pp-detail-request">
        <strong>Request sent.</strong> You asked ${what}. Nothing has changed yet — we'll let you know as soon as we've looked at it.
        <button class="pp-btn pp-btn-outline" data-action="client-withdraw-request" data-id="${b.id}">Withdraw request</button>
      </div>`;
  } else {
    const btn = (t: SelfServiceTerms, kind: 'cancel' | 'reschedule') => {
      if (t.mode === 'none') return '';
      const asking = t.mode === 'request';
      if (kind === 'cancel') {
        const label = s.cancelLoading ? 'Working...' : (asking ? 'Request to cancel' : 'Cancel Booking');
        return `<button class="pp-btn pp-btn-outline-danger" data-action="client-cancel-booking" data-id="${b.id}"${s.cancelLoading ? ' disabled' : ''}>${label}</button>`;
      }
      return `<button class="pp-btn pp-btn-outline" data-action="client-reschedule-booking" data-id="${b.id}">${asking ? 'Request to reschedule' : 'Reschedule'}</button>`;
    };
    // Confirm (unconfirmed) and 'I'm here' (on the day) from the portal — the
    // same two things the texted links do (Tim, 2026-10-18).
    const busy = s.portalActionBusy;
    const confirmBtn = b.status === 'unconfirmed' && !b.asGuest
      ? `<button class="pp-btn pp-btn-primary" data-action="client-confirm-booking" data-id="${b.id}"${busy ? ' disabled' : ''}>${busy ? 'Working...' : 'Confirm appointment'}</button>`
      : '';
    const myRowsIn = b.asGuest && b.items.filter(it => Number(it.clientId) === Number(s.clientId)).every(it => (it as { status?: string }).status === 'waiting') && b.items.some(it => Number(it.clientId) === Number(s.clientId));
    const todayVisit = String(b.date || '').slice(0, 10) === s.todayStr
      && (b.asGuest ? !myRowsIn && b.status !== 'cancelled' && b.status !== 'completed' : (b.status === 'unconfirmed' || b.status === 'confirmed'));
    const hereBtn = todayVisit
      ? `<button class="pp-btn pp-btn-primary" data-action="client-checkin-booking" data-id="${b.id}"${busy ? ' disabled' : ''}>${busy ? 'Working...' : "I'm here"}</button>`
      : '';
    const guestNote = b.asGuest ? `<div class="pp-detail-request"><strong>You're a guest on this appointment.</strong> It was booked by ${esc(b.guestOf || 'someone else')} — any changes go through them.</div>` : '';
    const arrivedNote = (b.status === 'waiting' || myRowsIn) ? `<div class="pp-detail-request"><strong>You're checked in.</strong> We know you're here and will come and get you shortly.</div>` : '';
    // One-off calendar file for this visit (Tim, 2026-11-02) — no feed needed.
    const calBtn = !['cancelled', 'expired', 'noshow', 'completed'].includes(b.status) && b.date >= s.todayStr
      ? `<button class="pp-btn pp-btn-outline" data-action="add-to-calendar" data-id="${b.id}">Add to my calendar</button>`
      : '';
    const buttons = confirmBtn + hereBtn + btn(reschedT, 'reschedule') + btn(cancelT, 'cancel') + calBtn;
    // The file is this one visit; the feed keeps every future one in step.
    const calHint = calBtn && s.calSyncEnabled
      ? `<div class="pp-detail-cal-hint">"Add to my calendar" adds just this visit. To keep all your future appointments in your calendar automatically, use <button class="pp-link-btn" data-action="go-dashboard">Calendar Sync</button> on your account page.</div>`
      : '';
    if (buttons) actionsHtml = `${guestNote}${arrivedNote}<div class="pp-detail-actions">${buttons}</div>${calHint}`;
    else actionsHtml = guestNote + arrivedNote;
    // Forms the spa is still waiting on for THIS visit — the home screen's
    // prompt, repeated where the appointment is (Tim, 2026-10-18).
    const todo = s.formsForBooking(b.id);
    if (todo.length) {
      actionsHtml = renderFormsToComplete(s, todo,
        todo.length === 1 ? 'A form to complete before this visit' : `${todo.length} forms to complete before this visit`, 6, 'pp-detail-forms') + actionsHtml;
    }

    const notes: string[] = [];
    const reserved = (b.creditReservations || []).filter(r => r.status === 'reserved');
    if (reserved.length) {
      const r = reserved[0];
      notes.push(r.kind === 'promotion'
        ? `Your offer "${r.name}" is applied to this appointment${b.discountAmount ? ` — ${formatCurrency(b.discountAmount)} off` : ''}.`
        : `Booked with your ${r.kind} "${r.name}" — the session is reserved and applied at checkout.`);
    } else if (b.promoCode) {
      notes.push(`A discount is applied to this appointment${b.discountAmount ? ` — ${formatCurrency(b.discountAmount)} off` : ''}.`);
    }
    if (reschedT.linked) {
      notes.push(reschedT.mode === 'request'
        ? 'This appointment is linked with others in your group, so it moves together — send us a request and we will find a time that works for everyone.'
        : 'This appointment is linked with others in your group, so it moves together — call or text us to reschedule.');
    }
    if ((cancelT.mode === 'request' || reschedT.mode === 'request') && !reschedT.linked) {
      notes.push('Changes this close to the appointment need our OK — send a request and we will get right back to you.');
    }
    const cost = lateChangeNote(cancelT, 'Cancelling');
    const rcost = lateChangeNote(reschedT, 'Rescheduling');
    const refundNote = depositRefundNote(cancelT);
    if (cost) notes.push(cost);
    if (refundNote) notes.push(refundNote);
    if (rcost) notes.push(rcost);
    if (isCancellable && !buttons) notes.push('To change this appointment, please call or text us.');
    if (notes.length) cutoffHtml = `<div class="pp-detail-cutoff">${notes.map(esc).join('<br>')}</div>`;
  }

  return `
    <div class="pp-detail-header">
      <div class="pp-detail-conf">${esc(b.confirmationCode)}</div>
      <span class="${statusBadgeClass(b.status)}">${esc(b.status)}</span>
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-row">
        <span class="pp-detail-label">Date</span>
        <span class="pp-detail-value">${esc(formatDate(b.date))}</span>
      </div>
      <div class="pp-detail-row">
        <span class="pp-detail-label">Time</span>
        <span class="pp-detail-value">${esc(b.time)}</span>
      </div>
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-section-title">Services</div>
      ${itemsHtml}
    </div>
    ${b.notes ? `
    <div class="pp-detail-section">
      <div class="pp-detail-section-title">Notes</div>
      <div class="pp-detail-notes">${esc(b.notes)}</div>
    </div>` : ''}
    ${s.portalError ? `<div class="pp-error-msg">${esc(s.portalError)}</div>` : ''}
    ${cutoffHtml}
    ${actionsHtml}`;
}

function _withinCutoff(date: string, time: string, cutoffHours: number): boolean {
  try {
    // Parse the 12h time like "1:00 pm" or 24h time like "14:00"
    let hours = 0, mins = 0;
    const m12 = time.match(/^(\d+):(\d+)\s*(am|pm)$/i);
    if (m12) {
      hours = parseInt(m12[1]);
      mins = parseInt(m12[2]);
      const ap = m12[3].toLowerCase();
      if (ap === 'pm' && hours !== 12) hours += 12;
      if (ap === 'am' && hours === 12) hours = 0;
    } else {
      const parts = time.split(':');
      hours = parseInt(parts[0]);
      mins = parseInt(parts[1] || '0');
    }
    const [y, mo, d] = date.split('-').map(Number);
    const apptDate = new Date(y, mo - 1, d, hours, mins);
    const now = new Date();
    const hoursUntil = (apptDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntil >= cutoffHours;
  } catch {
    return false;
  }
}

// ── SALES ──────────────────────────────────────────────────────────────────

function renderSales(s: PortalState): string {
  if (s.salesLoading) return renderLoading();
  if (!s.sales || s.sales.length === 0) {
    return renderEmpty('No receipts yet', 'Your purchase history will appear here.');
  }

  return s.sales.map(sale => {
    return `
      <button class="pp-list-item" data-action="view-sale" data-id="${sale.id}">
        <div class="pp-list-item-main">
          <div class="pp-list-item-title">${esc(sale.saleNumber)}</div>
          <div class="pp-list-item-sub">${esc(formatDate(sale.date))}</div>
        </div>
        <div class="pp-list-item-right">
          <span class="pp-list-item-amount">${formatCurrency(sale.total)}</span>
          <span class="${statusBadgeClass(sale.status)}">${esc(sale.status)}</span>
          <span class="pp-list-chevron">${ICON_CHEVRON}</span>
        </div>
      </button>`;
  }).join('');
}

// ── SALE DETAIL ────────────────────────────────────────────────────────────

function renderSaleDetail(s: PortalState): string {
  if (s.saleDetailLoading) return renderLoading();
  const sale = s.selectedSale;
  if (!sale) return renderEmpty('Receipt not found', '');

  const items = sale.items || [];
  const itemsHtml = items.map(it => {
    const addonsHtml = it.addons && it.addons.length > 0
      ? it.addons.map(a => `<div class="pp-receipt-addon">+ ${esc(a.name)}<span>${formatCurrency(a.price)}</span></div>`).join('')
      : '';
    return `
      <div class="pp-receipt-item">
        <div class="pp-receipt-item-row">
          <span>${esc(it.name)}</span>
          <span>${formatCurrency(it.price)}</span>
        </div>
        ${it.staffName ? `<div class="pp-receipt-item-staff">${esc(it.staffName)}</div>` : ''}
        ${addonsHtml}
      </div>`;
  }).join('');

  const paymentLabel = sale.paymentMethod
    ? sale.paymentMethod.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
    : '—';

  // A closed sale carries the SAME receipt the texted/emailed one shows —
  // every line, discounts, tax, each payment, the verdict — rendered by the
  // server (trusted markup, from our own builder). The old itemisation here
  // listed lines, subtotal, tip and total only.
  if (sale.receiptHtml) {
    const pdfBtn = sale.receiptUrl
      ? `<a class="pp-btn-outline pp-receipt-pdf-btn" href="${esc(sale.receiptUrl)}" target="_blank" rel="noopener">${ICON_RECEIPT} Print / Save as PDF</a>`
      : '';
    const st = s.receiptEmailState;
    const emailBtn = s.client?.email
      ? `<button class="pp-btn-outline pp-receipt-pdf-btn" data-action="portal-email-receipt" ${disabled(st === 'sending' || st === 'sent')}>${st === 'sending' ? 'Sending…' : st === 'sent' ? '✓ Emailed' : 'Email me this receipt'}</button>
         ${s.receiptEmailNote ? `<div class="pp-muted-note" style="text-align:center;margin-top:6px">${esc(s.receiptEmailNote)}</div>` : ''}`
      : '';
    return `
    <div class="pp-detail-header">
      <div class="pp-detail-conf">${esc(sale.saleNumber)}</div>
      <span class="${statusBadgeClass(sale.status)}">${esc(sale.status)}</span>
    </div>
    <div class="pp-receipt-full">${sale.receiptHtml}</div>
    <div class="pp-receipt-actions">${pdfBtn}${emailBtn}</div>`;
  }

  return `
    <div class="pp-detail-header">
      <div class="pp-detail-conf">${esc(sale.saleNumber)}</div>
      <span class="${statusBadgeClass(sale.status)}">${esc(sale.status)}</span>
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-row">
        <span class="pp-detail-label">Date</span>
        <span class="pp-detail-value">${esc(formatDate(sale.date))}</span>
      </div>
      <div class="pp-detail-row">
        <span class="pp-detail-label">Payment</span>
        <span class="pp-detail-value">${esc(paymentLabel)}</span>
      </div>
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-section-title">Items</div>
      ${itemsHtml}
    </div>
    <div class="pp-receipt-totals">
      <div class="pp-receipt-total-row">
        <span>Subtotal</span>
        <span>${formatCurrency(sale.subtotal)}</span>
      </div>
      ${sale.tip > 0 ? `
      <div class="pp-receipt-total-row">
        <span>Tip</span>
        <span>${formatCurrency(sale.tip)}</span>
      </div>` : ''}
      <div class="pp-receipt-total-row pp-receipt-grand-total">
        <span>Total</span>
        <span>${formatCurrency(sale.total)}</span>
      </div>
    </div>`;
}

// ── GIFT CARDS (portal view) ──────────────────────────────────────────────

function renderGiftCards(s: PortalState): string {
  if (s.walletLoading) return renderLoading();
  const gcs = s.wallet?.giftCards || [];
  const buyBtn = `
    <div class="pp-portal-action-row">
      <button class="pp-btn-primary" data-action="portal-buy-gift-card">${ICON_GIFT} Buy a gift card</button>
    </div>`;
  if (gcs.length === 0) {
    return buyBtn + renderEmpty('No gift cards', 'Gift cards linked to your account will appear here.');
  }

  return buyBtn + gcs.map(gc => `
    <div class="pp-gc-card">
      <div class="pp-gc-card-top">
        <div class="pp-gc-card-number">${esc(gc.cardNumber)}</div>
        <span class="${statusBadgeClass(gc.status)}">${esc(gc.status)}</span>
      </div>
      <div class="pp-gc-card-balance">
        <span class="pp-gc-balance-label">Balance</span>
        <span class="pp-gc-balance-value">${formatCurrency(gc.currentBalance)}</span>
      </div>
      <div class="pp-gc-card-meta">
        Original: ${formatCurrency(gc.originalBalance)} · Purchased ${esc(formatDate(gc.purchaseDate))}
      </div>
      ${(() => { const e = expiryLine(gc.expiresOn, 90, s.today); return e ? `<div class="pp-expiry pp-expiry-${e.tone}">${esc(e.text)}</div>` : '<div class="pp-expiry pp-expiry-ok">Never expires</div>'; })()}
    </div>
  `).join('');
}

// ── PACKAGES (portal view) ────────────────────────────────────────────────

/** Groups with a session left, from a package or membership row. */
export function bookableCreditGroups(groups: CreditGroup[] | undefined): CreditGroup[] {
  return (groups || []).filter(g => Number(g.remaining ?? 0) > 0);
}

/** 'Book with this package' buttons — one per group with sessions left
 *  (one button when there is one group). Nothing for an expired package or
 *  an inactive membership (Tim, 2026-10-14). */
function creditBookButtons(kind: 'package' | 'membership', id: number, groups: CreditGroup[] | undefined, disabledWhy = ''): string {
  const live = bookableCreditGroups(groups);
  if (!live.length || disabledWhy) return '';
  const one = live.length === 1;
  return `<div class="pp-credit-book">${live.map(g => `
    <button class="pp-btn pp-btn-accent pp-btn-sm" data-action="portal-book-credit" data-kind="${kind}" data-id="${id}" data-group="${esc(String(g.groupId))}">
      Book with this ${kind}${one || !g.name ? '' : ` · ${esc(String(g.name))}`}
    </button>`).join('')}</div>`;
}

/** An OFFER on the client's record is bookable while it is active, for
 *  services (not products), covers at least one service (not only add-ons),
 *  not expired and not already reserved by another booking (Tim, 2026-10-16). */
/** The wallet row's status chip: an offer that is spoken for says so (Tim,
 *  2026-10-31 — a reserved offer read like any other, and the client tried to
 *  book with it again). `null` = still available. */
export function promotionStatusChip(p: ClientPromotion): { kind: 'reserved' | 'used'; text: string } | null {
  if (p.status === 'redeemed') return { kind: 'used', text: 'Used' };
  if (p.reservedBookingId) {
    return { kind: 'reserved', text: p.reservedBookingDate ? `Reserved · ${formatDate(p.reservedBookingDate)} appointment` : 'Reserved for your upcoming appointment' };
  }
  return null;
}

export function promotionCanBook(p: ClientPromotion, today: string, now: number = Date.now()): boolean {
  if (p.status !== 'active' || p.appliesTo !== 'services') return false;
  if (p.reservedBookingId) return false;
  if (p.expiresAt && String(p.expiresAt).slice(0, 10) < today) return false;
  if (p.expiresAtDt && new Date(p.expiresAtDt).getTime() <= now) return false;
  if (p.startsAtDt && new Date(p.startsAtDt).getTime() > now) return false;
  // An offer on an ADD-ON alone (a free aromatherapy oil on any massage) is
  // bookable too: the menu narrows to its terms' services, the add-on is free.
  if (p.scope === 'specific' && !(p.serviceIds || []).length && !(p.categoryIds || []).length && !(p.enhancementOptionIds || []).length) return false;
  return true;
}

/** The services an offer narrows the menu to: its own scope, or — for an
 *  add-on-only offer — the services its visit terms name. */
export function promotionMenuScope(p: ClientPromotion): { serviceIds: number[]; categoryIds: (number | string)[] } {
  if (p.scope === 'specific' && ((p.serviceIds || []).length || (p.categoryIds || []).length)) {
    return { serviceIds: (p.serviceIds || []).map(Number), categoryIds: p.categoryIds || [] };
  }
  const t = p.terms || {};
  return { serviceIds: (t.serviceIds || []).map(Number), categoryIds: t.categoryIds || [] };
}

/** The offers pushed to this client's portal (Tim, 2026-10-18): live, not
 *  lapsed, not yet reserved by a booking — newest first, several allowed. */
export function portalOffers(promos: ClientPromotion[] | undefined, now: number = Date.now()): ClientPromotion[] {
  return (promos || [])
    .filter(p => p.showInPortal && p.status === 'active' && !p.reservedBookingId
      && !(p.expiresAtDt && new Date(p.expiresAtDt).getTime() <= now))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

/** What the offer gives, in the client's words. */
export function offerRewardLabel(p: ClientPromotion): string {
  if (p.rewardKind === 'free_service') return 'A free service';
  if (p.rewardKind === 'free_enhancement') return 'A free enhancement';
  return p.discountType === 'percent' ? `${p.discountValue}% off` : `${formatCurrency(p.discountValue)} off`;
}

/** The offer's conditions in one line for the banner — 'Minimum $100 ·
 *  Ends Sat, Nov 1 at 11:59 PM' (Tim, 2026-10-31: the countdown alone never
 *  said WHEN it ends, and the minimum only showed on the Wallet card). '' when
 *  there are none. A date-only expiry reads 'Ends Sat, Nov 1'. */
export function offerConditions(p: ClientPromotion): string {
  const bits: string[] = [];
  const min = Number(p.minSpend) || 0;
  if (min > 0) bits.push(`Minimum ${Number.isInteger(min) ? `$${min.toLocaleString('en-US')}` : formatCurrency(min)}`);
  if (p.expiresAtDt) {
    const d = new Date(String(p.expiresAtDt));
    if (!isNaN(d.getTime())) {
      const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      bits.push(`Ends ${day} at ${time}`);
    }
  } else if (p.expiresAt) {
    const d = new Date(`${String(p.expiresAt).slice(0, 10)}T12:00:00`);
    if (!isNaN(d.getTime())) bits.push(`Ends ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`);
  }
  return bits.join(' · ');
}

/** "3 days 04:12:09" / "04:12:09" / "12:09" — the banner's countdown; '' once lapsed. */
export function offerCountdown(expiresAt: string, now: number = Date.now()): string {
  const t = new Date(expiresAt).getTime();
  if (!t || isNaN(t)) return '';
  let secs = Math.floor((t - now) / 1000);
  if (secs <= 0) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const days = Math.floor(secs / 86400); secs -= days * 86400;
  const h = Math.floor(secs / 3600); secs -= h * 3600;
  const m = Math.floor(secs / 60); const sec = secs - m * 60;
  const clock = `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return days ? `${days} day${days === 1 ? '' : 's'} ${clock}` : clock;
}

/** Does this offer plausibly cover an item of the booking? Mirrors the
 *  server's promotion_covers as far as the portal can see (a category-scoped
 *  offer is offered and the server has the last word). */
export function offerCoversBooking(p: ClientPromotion, b: Booking): boolean {
  // Visit terms naming services: the appointment must be for one of them (a
  // category the portal cannot see is allowed; the server has the last word).
  const tsids = (p.terms?.serviceIds || []).map(Number);
  if (tsids.length && !b.items.some(it => tsids.includes(Number(it.serviceId)))) return false;
  if (p.scope !== 'specific') return true;
  const sids = (p.serviceIds || []).map(Number);
  if (sids.length && b.items.some(it => sids.includes(Number(it.serviceId)))) return true;
  if ((p.categoryIds || []).length) return true;
  const eids = (p.enhancementOptionIds || []).map(String);
  return eids.length > 0 && b.items.some(it => ((it as { addonIds?: unknown[] }).addonIds || []).some(a => eids.includes(String(a))));
}

/** The upcoming appointments an offer could be applied to: live, ahead,
 *  not already carrying an offer or a session, and covered. */
export function offerApplicableBookings(p: ClientPromotion, bookings: Booking[] | null, today: string): Booking[] {
  const dead = ['cancelled', 'completed', 'expired', 'noshow', 'no-show', 'waitlist', 'merged'];
  return (bookings || []).filter(b => !b.asGuest && b.date >= today && !dead.includes(b.status)
    && !b.promoCode && !(b.creditReservations || []).some(r => r.status === 'reserved')
    && offerCoversBooking(p, b) && meetsMinimum(p, b));
}

/** A minimum spend is checked against the SERVICE the offer is used on
 *  (credit_reservations._validate_promotion) — so an appointment where no
 *  service reaches it is not offered 'Apply' only to be refused. An item with
 *  no price known, or an add-on-only offer, is left to the server. */
function meetsMinimum(p: ClientPromotion, b: Booking): boolean {
  const min = Number(p.minSpend) || 0;
  if (min <= 0 || (p.enhancementOptionIds || []).length) return true;
  const priced = b.items.filter(it => typeof it.totalPrice === 'number');
  if (!priced.length) return true;
  return priced.some(it => it.totalPrice >= min);
}

function renderOfferBanners(s: PortalState): string {
  const offers = portalOffers(s.wallet?.promotions);
  if (!offers.length) return '';
  return offers.map(p => {
    const headline = p.label;
    const exp = p.expiresAtDt ? String(p.expiresAtDt) : (p.expiresAt ? `${p.expiresAt}T23:59:59` : '');
    const clock = exp ? `<div class="pp-offer-clock">Expires in <b class="pp-offer-countdown">${esc(offerCountdown(exp))}</b></div>` : '';
    const conditions = offerConditions(p);
    const terms = (conditions ? `<div class="pp-offer-terms pp-offer-conditions">${esc(conditions)}</div>` : '')
      + (p.termsLabel ? `<div class="pp-offer-terms">${esc(p.termsLabel)}</div>` : '');
    const startsLater = p.startsAtDt && new Date(p.startsAtDt).getTime() > Date.now();
    const startsNote = startsLater ? `<div class="pp-offer-terms">Available from ${esc(formatDate(String(p.startsAtDt).slice(0, 10)))}</div>` : '';
    const bookBtn = promotionCanBook(p, s.todayStr)
      ? `<button class="pp-btn pp-btn-primary pp-btn-sm" data-action="portal-book-credit" data-kind="promotion" data-id="${p.id}" data-group="">Book with this offer</button>` : '';
    const applyTo = startsLater ? [] : offerApplicableBookings(p, s.portalBookings, s.todayStr).slice(0, 3);
    const applyBtns = applyTo.map(b => `<button class="pp-btn pp-btn-outline pp-btn-sm" data-action="portal-apply-offer" data-id="${p.id}" data-booking="${b.id}" ${disabled(s.portalActionBusy)}>Apply to my ${esc(formatDate(b.date))} appointment</button>`).join('');
    return `
      <div class="pp-home-banner pp-offer-banner" data-offer-expires="${esc(exp)}">
        <div class="pp-offer-reward">${esc(offerRewardLabel(p))}</div>
        <div class="pp-home-banner-title">${esc(headline)}</div>
        ${p.portalMessage ? `<div class="pp-offer-msg">${esc(p.portalMessage)}</div>` : ''}
        ${clock}${startsNote}${terms}
        ${bookBtn || applyBtns ? `<div class="pp-offer-actions">${bookBtn}${applyBtns}</div>` : ''}
      </div>`;
  }).join('');
}

export function packageExpired(pkg: ClientPackage, today: string): boolean {
  return !!pkg.expirationDate && String(pkg.expirationDate).slice(0, 10) < today;
}

export function membershipCanBook(m: ClientMembership): boolean {
  return (m.status === 'active' || m.status === 'cancelling') && (m.planType || 'services') === 'services';
}

function renderPortalPackages(s: PortalState): string {
  // Show confirm/add-card step if in portal purchase flow for packages
  if (s.portalPurchaseStep && s.portalPurchaseType === 'package') {
    return renderPortalPurchaseConfirm(s);
  }

  if (s.packagesLoading || (!s.bookingDataLoaded && s.bookingDataLoading)) return renderLoading();

  const purchased = s.portalPackages || [];
  const available = s.packages.filter(t => !t.onlyDirectLink);

  if (purchased.length === 0 && available.length === 0) {
    return renderEmpty('No packages', 'Packages you purchase will appear here.');
  }

  let html = '';

  // Purchased packages
  if (purchased.length > 0) {
    html += `<div class="pp-section-label">Your Packages</div>`;
    html += purchased.map(pkg => `
      <div class="pp-pkg-card">
        <div class="pp-pkg-card-header">
          <div class="pp-pkg-card-name">${esc(pkg.name)}</div>
        </div>
        <div class="pp-pkg-card-meta">
          Purchased ${esc(formatDate(pkg.purchaseDate))}
          ${pkg.sharedFrom ? ` · Shared by ${esc(pkg.sharedFrom.clientName)}` : ''}
          ${!pkg.sharedFrom && pkg.sharedClients?.length ? ` · Shared with ${esc(pkg.sharedClients.map(c => c.clientName).join(', '))}` : ''}
        </div>
        <div class="pp-pkg-group">
          <div class="pp-pkg-group-name">Remaining sessions</div>
          <div class="pp-pkg-group-progress">
            <span class="pp-pkg-group-count">${pkg.qtyRemaining} remaining</span>
          </div>
        </div>
        ${(() => { const e = expiryLine(pkg.expirationDate, 30, s.today); return e ? `<div class="pp-expiry pp-expiry-${e.tone}">${esc(e.text)}${e.tone !== 'red' && pkg.qtyRemaining > 0 ? ` · ${pkg.qtyRemaining} session${pkg.qtyRemaining === 1 ? '' : 's'} left` : ''}</div>` : ''; })()}
        ${creditBookButtons('package', pkg.id, pkg.groups, packageExpired(pkg, s.todayStr) ? 'expired' : '')}
      </div>`
    ).join('');
  }

  // Available packages for purchase
  if (available.length > 0) {
    html += `<div class="pp-section-label" style="margin-top:var(--sp-md)">Available Packages</div>`;
    html += available.map(t => `
      <div class="pp-pkg-card pp-pkg-card-available">
        <div class="pp-pkg-card-header">
          <div class="pp-pkg-card-name">${esc(t.name)}</div>
          <div class="pp-pkg-card-price">${formatCurrency(t.totalPrice)}</div>
        </div>
        ${t.shortDescription ? `<div class="pp-pkg-card-meta">${esc(t.shortDescription)}</div>` : ''}
        <button class="pp-btn pp-btn-accent pp-btn-sm" data-action="portal-buy-pkg" data-id="${t.id}" style="margin-top:var(--sp-sm)">Purchase</button>
      </div>`
    ).join('');
  }

  return html;
}

// ── MEMBERSHIPS ────────────────────────────────────────────────────────────

function renderMemberships(s: PortalState): string {
  // Show confirm/add-card step if in portal purchase flow for memberships
  if (s.portalPurchaseStep && s.portalPurchaseType === 'membership') {
    return renderPortalPurchaseConfirm(s);
  }

  if (s.membershipsLoading || (!s.bookingDataLoaded && s.bookingDataLoading)) return renderLoading();

  const enrolled = s.memberships || [];
  const available = s.membershipPlans;
  // Don't show plans the client is already actively enrolled in
  const enrolledPlanIds = new Set(enrolled.filter(m => m.status === 'active' || m.status === 'paused').map(m => m.planId));
  const availablePlans = available.filter(p => !enrolledPlanIds.has(p.id));

  if (enrolled.length === 0 && availablePlans.length === 0) {
    return renderEmpty('No memberships', 'Your memberships will appear here.');
  }

  let html = '';

  if (enrolled.length > 0) {
    html += `<div class="pp-section-label">Your Memberships</div>`;
    html += enrolled.map(m => `
      <div class="pp-membership-card">
        <div class="pp-membership-header">
          <div class="pp-membership-name">${esc(m.planName)}</div>
          <span class="${statusBadgeClass(m.status)}">${esc(m.status)}</span>
        </div>
        <div class="pp-membership-details">
          <div class="pp-membership-row">
            <span>Price</span>
            <span>${formatCurrency(m.price)}/${_unitAbbrev((m as { billingUnit?: string }).billingUnit)}</span>
          </div>
          <div class="pp-membership-row">
            <span>Started</span>
            <span>${esc(formatDate(m.startDate))}</span>
          </div>
          ${m.nextBillingDate ? `
          <div class="pp-membership-row">
            <span>Next billing</span>
            <span>${esc(formatDate(m.nextBillingDate))}</span>
          </div>` : ''}
        </div>
        ${(() => {
          if (!m.nextBillingDate || !['active', 'past_due', 'unpaid'].includes(m.status)) return '';
          const card = (s.wallet?.creditCards || []).find(c => c.isDefault) || (s.wallet?.creditCards || [])[0];
          const cardText = card ? `your ${esc(card.brand)} ending ${esc(card.last4)}` : 'your card on file';
          return `<div class="pp-membership-charge">${formatCurrency(m.price)} will be charged to ${cardText} on ${esc(formatDate(m.nextBillingDate))}.</div>`;
        })()}
        ${creditBookButtons('membership', m.id, m.groups, membershipCanBook(m) ? '' : 'inactive')}
      </div>
    `).join('');
  }

  if (availablePlans.length > 0) {
    html += `<div class="pp-section-label" style="margin-top:var(--sp-md)">Available Plans</div>`;
    html += availablePlans.map(p => {
      const interval = p.billingInterval === 1 ? p.billingUnit : `${p.billingInterval} ${p.billingUnit}s`;
      const perks = membershipPerks(p);
      return `
      <div class="pp-membership-card pp-membership-card-available">
        <div class="pp-membership-header">
          <div class="pp-membership-name">${esc(p.name)}</div>
          <div class="pp-pkg-card-price">${formatCurrency(p.price)}/${esc(interval)}</div>
        </div>
        ${p.description ? `<div class="pp-pkg-card-meta">${esc(p.description)}</div>` : ''}
        ${perks.length ? `<div class="pp-pkg-card-meta" style="color:var(--sage)">${perks.join(' · ')}</div>` : ''}
        ${membershipProviderNote(p) ? `<div class="pp-pkg-card-meta">${esc(membershipProviderNote(p))}</div>` : ''}
        <button class="pp-btn pp-btn-accent pp-btn-sm" data-action="portal-enroll-mem" data-id="${p.id}" style="margin-top:var(--sp-sm)">Enroll</button>
      </div>`;
    }).join('');
  }

  return html;
}

// ── PORTAL PURCHASE CONFIRM / ADD CARD ──────────────────────────────────────

function renderPortalPurchaseConfirm(s: PortalState): string {
  if (s.portalPurchaseStep === 'add-card') {
    return renderPortalAddCard(s);
  }

  const isPackage = s.portalPurchaseType === 'package';
  const templateId = s.portalPurchaseTemplateId;
  let name = '';
  let price = 0;
  let requireAgreement = false;
  let agreementText = '';
  let billingUnit = 'month';

  if (isPackage) {
    const tmpl = s.packages.find(t => t.id === templateId);
    if (tmpl) { name = tmpl.name; price = tmpl.totalPrice; requireAgreement = tmpl.requireAgreement; agreementText = tmpl.agreementText; }
  } else {
    const plan = s.membershipPlans.find(p => p.id === templateId);
    if (plan) { name = plan.name; price = plan.price; requireAgreement = plan.requireAgreement; agreementText = plan.agreementText; billingUnit = plan.billingUnit || 'month'; }
  }

  const actionLabel = isPackage ? 'Confirm Purchase' : 'Confirm Enrollment';
  const isProcessing = s.portalPurchaseStep === 'processing';

  // Check for valid card on file
  const cards = s.wallet?.creditCards || [];
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const validCard = cards.find(c => c.expYear > currentYear || (c.expYear === currentYear && c.expMonth >= currentMonth));

  if (!validCard && !isProcessing) {
    // No valid card — prompt to add one
    return renderPortalAddCard(s);
  }

  // Agreement section
  let agreementHtml = '';
  if (requireAgreement) {
    agreementHtml = `
      <div class="pp-portal-agreement">
        <label class="pp-portal-agreement-label">
          <input type="checkbox" data-action="portal-toggle-agreement" ${checked(s.portalPurchaseAgreed)} />
          I agree to the following:
        </label>
        <div class="pp-portal-agreement-text">${renderTermsBody(agreementText)}</div>
      </div>`;
  }

  const canConfirm = (!requireAgreement || s.portalPurchaseAgreed) && !isProcessing;

  // Success state
  if (s.portalPurchaseSuccess) {
    return `
      <div class="pp-portal-confirm">
        <div class="pp-portal-confirm-icon">✓</div>
        <div class="pp-portal-confirm-title">${isPackage ? 'Package Purchased!' : 'Membership Enrolled!'}</div>
        <p style="margin:var(--sp-sm) 0;color:var(--muted)">Your ${isPackage ? 'package' : 'membership'} is now active.</p>
        <button class="pp-btn pp-btn-accent" data-action="portal-purchase-done" style="margin-top:var(--sp-md)">Done</button>
      </div>`;
  }

  return `
    <div class="pp-portal-confirm">
      <div class="pp-portal-confirm-title">${isPackage ? 'Confirm Purchase' : 'Confirm Enrollment'}</div>

      <div class="pp-portal-confirm-item">
        <div class="pp-portal-confirm-name">${esc(name)}</div>
        <div class="pp-portal-confirm-price">${formatCurrency(price)}${!isPackage ? `/${_unitAbbrev(billingUnit)}` : ''}</div>
      </div>

      ${agreementHtml}

      ${validCard ? `
      <div class="pp-portal-confirm-card">
        <div class="pp-portal-confirm-card-label">Payment method</div>
        <div class="pp-portal-confirm-card-value">${esc(validCard.brand)} ····${esc(validCard.last4)} — exp ${String(validCard.expMonth).padStart(2, '0')}/${String(validCard.expYear).slice(-2)}</div>
        <div class="pp-portal-confirm-card-note">Your card on file will be charged ${formatCurrency(price)}</div>
      </div>` : ''}

      ${s.portalPurchaseError ? `<div class="pp-error-msg">${esc(s.portalPurchaseError)}</div>` : ''}

      <div class="pp-portal-confirm-actions">
        <button class="pp-btn pp-btn-accent" data-action="portal-purchase-confirm" ${disabled(!canConfirm)}>
          ${isProcessing ? 'Processing...' : actionLabel}
        </button>
        <button class="pp-btn pp-btn-ghost" data-action="portal-purchase-cancel" ${disabled(isProcessing)}>Cancel</button>
      </div>
    </div>`;
}

function renderPortalAddCard(s: PortalState): string {
  const isProcessing = s.portalPurchaseStep === 'processing';

  let cardInputHtml: string;
  if (s.stripePublishableKey) {
    cardInputHtml = `
      <div class="pp-gc-card-field-wrap" style="padding:10px 12px;margin-bottom:var(--sp-sm)">
        <div id="pp-stripe-card-element-portal" style="min-height:20px"></div>
      </div>`;
  } else {
    cardInputHtml = `
      <div class="pp-gc-card-row">
        <input class="pp-gc-card-input pp-gc-card-number" type="text" placeholder="Card number" data-field="cardNumber" maxlength="19" inputmode="numeric" />
      </div>
      <div class="pp-gc-card-row" style="display:flex;gap:var(--sp-sm)">
        <input class="pp-gc-card-input pp-gc-card-expiry" type="text" placeholder="MM/YY" data-field="expiry" maxlength="5" inputmode="numeric" style="flex:1" />
        <input class="pp-gc-card-input pp-gc-card-cvc" type="text" placeholder="CVC" data-field="cvc" maxlength="4" inputmode="numeric" style="flex:1" />
      </div>`;
  }

  return `
    <div class="pp-portal-confirm">
      <div class="pp-portal-confirm-title">Add Payment Method</div>
      <div class="pp-portal-confirm-card-note" style="margin-bottom:var(--sp-md)">A card on file is required to complete this ${s.portalPurchaseType === 'package' ? 'purchase' : 'enrollment'}.</div>

      ${cardInputHtml}

      ${s.portalPurchaseError ? `<div class="pp-error-msg">${esc(s.portalPurchaseError)}</div>` : ''}

      <div class="pp-portal-confirm-actions" style="margin-top:var(--sp-md)">
        <button class="pp-btn pp-btn-accent" data-action="portal-add-card-submit" ${disabled(isProcessing)}>
          ${isProcessing ? 'Saving...' : 'Save & Continue'}
        </button>
        <button class="pp-btn pp-btn-ghost" data-action="portal-purchase-cancel" ${disabled(isProcessing)}>Cancel</button>
      </div>
    </div>`;
}

// ── WALLET ─────────────────────────────────────────────────────────────────

function renderWallet(s: PortalState): string {
  if (s.walletLoading) return renderLoading();
  if (!s.wallet) return renderEmpty('Wallet', 'Unable to load wallet data.');

  const balance = s.wallet.accountBalance;
  const history = s.wallet.balanceHistory;
  const cards = s.wallet.creditCards;

  const historyHtml = history.length > 0
    ? history.map(h => `
        <div class="pp-wallet-history-item">
          <div class="pp-wallet-history-main">
            <div class="pp-wallet-history-desc">${esc(h.details)}${h.note ? ' — ' + esc(h.note) : ''}</div>
            <div class="pp-wallet-history-date">${esc(formatDate(h.date))}</div>
          </div>
          <div class="pp-wallet-history-amount ${h.amount >= 0 ? 'positive' : 'negative'}">
            ${h.amount >= 0 ? '+' : ''}${formatCurrency(h.amount)}
          </div>
        </div>
      `).join('')
    : '<div class="pp-empty-small">No transactions yet</div>';

  const busy = s.walletCardBusy;
  const cardsHtml = cards.length > 0
    ? cards.map(c => `
        <div class="pp-wallet-card${c.isDefault ? ' is-default' : ''}">
          <div class="pp-wallet-card-main">
            <div class="pp-wallet-card-line">
              <span class="pp-wallet-card-brand">${esc(c.brand)}</span>
              <span class="pp-wallet-card-number">····${esc(c.last4)}</span>
              <span class="pp-wallet-card-exp">${String(c.expMonth).padStart(2, '0')}/${c.expYear}</span>
              ${c.isDefault ? '<span class="pp-badge pp-badge-green">Default</span>' : ''}
            </div>
            <div class="pp-wallet-card-actions">
              ${c.isDefault ? '' : `<button class="pp-link-btn" data-action="wallet-card-default" data-id="${c.id}" ${disabled(busy)}>Make default</button>`}
              <button class="pp-link-btn pp-link-btn-danger" data-action="wallet-card-remove" data-id="${c.id}" ${disabled(busy)}>Remove</button>
            </div>
          </div>
        </div>
      `).join('')
    : '<div class="pp-empty-small">No cards on file</div>';

  const addCardHtml = s.walletAddCardOpen
    ? `<div class="pp-wallet-add-card">
        ${s.stripePublishableKey
          ? `<div class="pp-gc-card-field-wrap" style="padding:10px 12px"><div id="pp-stripe-card-element-walletadd" style="min-height:20px"></div></div>`
          : `<div class="pp-gc-card-row"><input class="pp-gc-card-input pp-gc-card-number" type="text" placeholder="Card number" data-field="cardNumber" maxlength="19" inputmode="numeric" /></div>
             <div class="pp-gc-card-row" style="display:flex;gap:var(--sp-sm)">
               <input class="pp-gc-card-input pp-gc-card-expiry" type="text" placeholder="MM/YY" data-field="expiry" maxlength="5" inputmode="numeric" style="flex:1" />
               <input class="pp-gc-card-input pp-gc-card-cvc" type="text" placeholder="CVC" data-field="cvc" maxlength="4" inputmode="numeric" style="flex:1" />
             </div>`}
        <div class="pp-wallet-add-card-btns">
          <button class="pp-btn-primary" data-action="wallet-add-card-submit" ${disabled(busy)}>${busy ? 'Saving…' : 'Save card'}</button>
          <button class="pp-btn-outline" data-action="wallet-add-card-cancel" ${disabled(busy)}>Cancel</button>
        </div>
      </div>`
    : `<div class="pp-wallet-add-card-row"><button class="pp-btn-outline" data-action="wallet-add-card-open">+ Add a card</button></div>`;
  const cardErr = s.walletCardError ? `<div class="pp-error-msg" style="margin:0 20px 8px">${esc(s.walletCardError)}</div>` : '';

  const promos = (s.wallet.promotions || []).filter(p => p.status === 'active' || p.status === 'redeemed');
  const promosHtml = promos.length > 0
    ? promos.map(p => {
        const discountLabel = offerRewardLabel(p);
        const scopeLabel = p.appliesTo === 'products' ? 'products' : 'services';
        const detailParts: string[] = [];
        detailParts.push(p.scope === 'specific' ? `Specific ${scopeLabel}` : `All ${scopeLabel}`);
        if (p.minSpend > 0) detailParts.push(`Min spend ${formatCurrency(p.minSpend)}`);
        if (p.expiresAt) detailParts.push(`Expires ${formatDate(p.expiresAt)}`);
        if (p.termsLabel) detailParts.push(p.termsLabel);
        const status = promotionStatusChip(p);
        const bookBtn = promotionCanBook(p, s.todayStr)
          ? `<div class="pp-credit-book"><button class="pp-btn pp-btn-accent pp-btn-sm" data-action="portal-book-credit" data-kind="promotion" data-id="${p.id}" data-group="">Book with this offer</button></div>`
          : '';
        return `
          <div class="pp-promo-item${status ? ` pp-promo-item--${status.kind}` : ''}">
            <div class="pp-promo-main">
              <div class="pp-promo-label">${esc(p.label)}${status ? ` <span class="pp-promo-status pp-promo-status--${status.kind}">${esc(status.text)}</span>` : ''}</div>
              <div class="pp-promo-detail">${detailParts.map(esc).join(' · ')}</div>
              ${p.note ? `<div class="pp-promo-note">${esc(p.note)}</div>` : ''}
              ${bookBtn}
            </div>
            <div class="pp-promo-discount">${esc(discountLabel)}</div>
          </div>`;
      }).join('')
    : '';

  const promosSection = promos.length > 0
    ? `<div class="pp-section-title">Promotions</div><div class="pp-promo-list">${promosHtml}</div>`
    : '';

  const owes = balance < 0;
  const payBtn = s.balancePaymentsEnabled
    ? `<button class="pp-btn-primary pp-wallet-pay-btn" data-action="nav-wallet-pay">${owes ? `Pay ${formatCurrency(-balance)} owed` : 'Add funds'}</button>`
    : '';
  const balanceSection = s.portalSections.accountBalance
    ? `<div class="pp-wallet-balance-card${owes ? ' is-owed' : ''}">
        <div class="pp-wallet-balance-label">${owes ? 'Balance owed' : 'Account Balance'}</div>
        <div class="pp-wallet-balance-value">${formatCurrency(owes ? -balance : balance)}</div>
        ${owes ? '<div class="pp-wallet-balance-note">This is what your account owes us.</div>' : ''}
        ${payBtn}
      </div>
      <div class="pp-section-title">Transaction History</div>
      <div class="pp-wallet-history">${historyHtml}</div>`
    : '';

  return `
    ${balanceSection}
    ${promosSection}
    <div class="pp-section-title">Payment Methods</div>
    ${cardErr}
    <div class="pp-wallet-cards">${cardsHtml}</div>
    ${addCardHtml}`;
}

/** 'My preferences': the client's own notes, saved on their record and shown
 *  to the team on every appointment they book. */
function renderPortalPreferences(s: PortalState): string {
  const unchanged = s.prefsDraft.trim() === (s.client?.portalNotes || '');
  return `
    ${renderSmsStoppedBanner(s)}
    <div class="pp-wallet-pay">
      <div class="pp-section-title" style="padding-left:0">My preferences</div>
      <div class="pp-wallet-pay-note">Anything you'd like us to know every time you visit — pressure, allergies, areas to avoid, a quiet room, how you take your tea. The team sees this on your record and on each appointment you book.</div>
      <textarea class="pp-f-input pp-message-box" data-field="prefsDraft" rows="7" maxlength="1000" placeholder="e.g. Firm pressure on the shoulders, go easy on the lower back. Allergic to lavender.">${esc(s.prefsDraft)}</textarea>
      <div class="pp-muted-note" style="margin-top:4px">${s.prefsDraft.length}/1000</div>
      ${s.prefsError ? `<div class="pp-error-msg">${esc(s.prefsError)}</div>` : ''}
      ${s.prefsSaved ? '<div class="pp-prefs-saved">✓ Saved — the team will see this on your next visit.</div>' : ''}
      <button class="pp-btn-primary" data-action="prefs-save" ${disabled(s.prefsSaving || unchanged)} style="margin-top:14px">${s.prefsSaving ? 'Saving…' : 'Save'}</button>
    </div>
    ${renderMarketingPrefs(s)}
    ${renderPortalTags(s)}
    ${renderPortalDetails(s)}
    ${s.reviewPrompt ? renderReviewPrompt(s, true) : ''}`;
}

/** Tags the spa lets clients switch themselves (Tim, 2026-11-02): one toggle
 *  per tag, grouped under the spa's own headings ('Pressure': Deep / Light;
 *  'Offers': …); tags with no group sit under a neutral heading. An
 *  exclusive group reads like a choice: switching one on switches the rest off. */
export function renderPortalTags(s: PortalState): string {
  const groups = s.portalTagGroups;
  if (!groups.length) return '';
  const busy = s.portalTagsBusy;
  const block = (g: PortalTagGroup) => `
      <div class="pp-section-title" style="padding-left:0;margin-top:18px">${esc(g.label || 'Also about me')}</div>
      ${g.exclusive ? '<div class="pp-wallet-pay-note">Pick one.</div>' : ''}
      ${g.tags.map(t => `
      <label class="pp-switch-row">
        <span class="pp-switch-text"><span>${esc(t.label)}</span>${t.description ? `<small>${esc(t.description)}</small>` : ''}</span>
        <input type="checkbox" class="pp-switch-input" data-action="portal-tag-toggle" data-id="${t.id}" ${checked(t.on)} ${disabled(busy)}>
        <span class="pp-switch" aria-hidden="true"></span>
      </label>`).join('')}`;
  return `
    <div class="pp-wallet-pay pp-portal-tags" style="margin-top:18px">
      ${groups.map(block).join('')}
      ${s.portalTagsError ? `<div class="pp-error-msg">${esc(s.portalTagsError)}</div>` : ''}
    </div>`;
}

/** 'Leave us a review' (Tim, 2026-11-02): a dismissible banner on the home
 *  screen — Leave a review (opens the spa's page on Google / Yelp / …),
 *  Remind me later (hidden for the configured days), I've already left a
 *  review (hidden for good, noted on the record) — and the same thing at
 *  the bottom of My preferences, where it can always be found again. */
export function renderReviewPrompt(s: PortalState, onPrefs: boolean): string {
  const rp = s.reviewPrompt;
  if (!rp) return '';
  const where = rp.host ? ` on ${esc(rp.host)}` : '';
  // The spa's own words when they wrote some (Settings → Online Booking →
  // Client Options → Message), else the built-in line.
  const custom = (rp.message || '').trim();
  const busy = s.reviewPromptBusy;
  const done = !!s.client?.reviewPromptDoneAt;
  if (onPrefs) {
    return `
    <div class="pp-wallet-pay pp-review-prefs" style="margin-top:18px">
      <div class="pp-section-title" style="padding-left:0">Leave us a review</div>
      ${done
        ? `<div class="pp-wallet-pay-note">Thank you for your review${where}! It means a lot to us.</div>
           <a class="pp-btn-outline pp-review-link" href="${esc(rp.url)}" target="_blank" rel="noopener">Open our page${where}</a>`
        : `<div class="pp-wallet-pay-note">${custom ? esc(custom) : `A few words${where} help others find us.`}</div>
           <a class="pp-btn-primary pp-review-link" href="${esc(rp.url)}" target="_blank" rel="noopener" data-action="review-open">Leave a review${where}</a>
           <button class="pp-link-btn pp-review-done" data-action="review-done"${busy ? ' disabled' : ''}>I've already left a review</button>`}
    </div>`;
  }
  return `
      <div class="pp-home-banner pp-review-banner" role="region" aria-label="Leave us a review">
        <button class="pp-install-close" data-action="review-later" aria-label="Remind me later"${busy ? ' disabled' : ''}>×</button>
        <div class="pp-home-banner-title">Enjoying ${esc(s.spaName)}?</div>
        <div class="pp-review-desc">${custom ? esc(custom) : `A few words${where} help others find us — it only takes a minute.`}</div>
        <div class="pp-review-actions">
          <a class="pp-btn-primary pp-review-link" href="${esc(rp.url)}" target="_blank" rel="noopener" data-action="review-open">Leave a review${where}</a>
          <button class="pp-btn-outline" data-action="review-later"${busy ? ' disabled' : ''}>Remind me later</button>
          <button class="pp-link-btn pp-review-done" data-action="review-done"${busy ? ' disabled' : ''}>I've already left a review</button>
        </div>
      </div>`;
}

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

/** 'My details' — birthday, gender, pronouns, socials, address — the
 *  client's own side of the record, saved with one button (Tim, 2026-11-02). */
export function renderPortalDetails(s: PortalState): string {
  const c = s.client;
  if (!c) return '';
  const d = s.detailsDraft;
  const v = (k: string) => esc(d[k] ?? '');
  const dirty = Object.keys(d).some(k => (d[k] ?? '') !== String((c as any)[k] ?? ''));
  const busy = s.detailsSaving;
  const input = (k: string, label: string, type = 'text', extra = '') => `
      <label class="pp-f-field">
        <span class="pp-f-label">${label}</span>
        <input class="pp-f-input" type="${type}" data-field="detail:${k}" value="${v(k)}"${extra}${busy ? ' disabled' : ''}>
      </label>`;
  const country = (d.country || 'US').toUpperCase();
  const stateField = country === 'US'
    ? `<label class="pp-f-field"><span class="pp-f-label">State</span>
        <select class="pp-f-input" data-field="detail:state"${busy ? ' disabled' : ''}>
          <option value="">Select</option>
          ${US_STATES.map(st => `<option value="${st}"${(d.state || '') === st ? ' selected' : ''}>${st}</option>`).join('')}
        </select></label>`
    : input('state', 'State / region');
  return `
    <div class="pp-wallet-pay" style="margin-top:18px">
      <div class="pp-section-title" style="padding-left:0">My details</div>
      <div class="pp-wallet-pay-note">Only you and the team see these.</div>
      <div class="pp-details-grid">
        ${input('birthday', 'Birthday', 'date')}
        <label class="pp-f-field"><span class="pp-f-label">Gender</span>
          <select class="pp-f-input" data-field="detail:gender"${busy ? ' disabled' : ''}>
            <option value=""${!d.gender ? ' selected' : ''}>Prefer not to say</option>
            <option value="female"${d.gender === 'female' ? ' selected' : ''}>Female</option>
            <option value="male"${d.gender === 'male' ? ' selected' : ''}>Male</option>
            <option value="non-binary"${d.gender === 'non-binary' ? ' selected' : ''}>Non-binary</option>
          </select></label>
        ${input('pronouns', 'Pronouns', 'text', ' placeholder="e.g. she/her" autocomplete="off"')}
      </div>
      <div class="pp-f-label pp-details-sub">Social media</div>
      <div class="pp-details-grid">
        ${input('instagram', 'Instagram', 'text', ' placeholder="@handle" autocapitalize="off" autocomplete="off"')}
        ${input('facebook', 'Facebook', 'text', ' placeholder="Name or link" autocomplete="off"')}
      </div>
      <div class="pp-f-label pp-details-sub">Address</div>
      ${input('address1', 'Address line 1', 'text', ' autocomplete="address-line1"')}
      ${input('address2', 'Address line 2', 'text', ' autocomplete="address-line2"')}
      <div class="pp-details-grid">
        ${input('city', 'City', 'text', ' autocomplete="address-level2"')}
        ${stateField}
        ${input('zip', 'Zip code', 'text', ' autocomplete="postal-code" inputmode="numeric"')}
        ${input('country', 'Country', 'text', ' autocomplete="country" placeholder="US"')}
      </div>
      ${s.detailsError ? `<div class="pp-error-msg">${esc(s.detailsError)}</div>` : ''}
      ${s.detailsSaved && !dirty ? '<div class="pp-prefs-saved">✓ Saved.</div>' : ''}
      <button class="pp-btn-primary" data-action="details-save" ${disabled(busy || !dirty)} style="margin-top:14px">${busy ? 'Saving…' : 'Save details'}</button>
    </div>`;
}

/** Their number replied STOP (Tim, 2026-11-02): nothing is texted to it —
 *  not reminders, not offers, not sign-in codes — until they text START.
 *  The switches below cannot undo a STOP; only the text can. */
export function renderSmsStoppedBanner(s: PortalState): string {
  const st = s.client?.smsStopped;
  if (!st) return '';
  const num = st.spaNumber;
  const digits = (num || '').replace(/[^\d+]/g, '');
  const startLink = digits ? `<a class="pp-btn-primary pp-stop-start" href="sms:${esc(digits)}?&body=START">Text START to ${esc(num)}</a>` : '';
  return `
    <div class="pp-home-banner pp-home-banner-red pp-stop-banner" role="alert">
      <div class="pp-home-banner-title">You've stopped all text messages from us</div>
      <div class="pp-stop-desc">Your number replied STOP, so we can't text you — no appointment reminders, no offers, and no sign-in codes. The switches below can't turn that back on: to get texts again, text <b>START</b> to ${num ? `<b>${esc(num)}</b>` : 'our number'}.</div>
      ${startLink}
    </div>`;
}

/** 'Notifications on this device' — web push from the installed portal
 *  (Tim, 2026-11-02). On an iPhone it only works once the app is on the
 *  home screen, so the row explains that first. */
export function renderPushRow(s: PortalState): string {
  const busy = s.pushBusy;
  const needsInstall = s.isIosBrowser && !s.runningAsApp;
  if (needsInstall) {
    return `
      <div class="pp-switch-row pp-switch-row-static">
        <span class="pp-switch-text"><span>Notifications on this phone</span><small>Add ${esc(s.spaName || 'us')} to your home screen first, then switch this on from the app.</small></span>
        <button type="button" class="pp-btn-outline pp-push-install" data-action="pwa-install">${s.installHelpOpen ? 'Hide steps' : 'Show me how'}</button>
      </div>
      ${s.installHelpOpen ? renderInstallBanner({ installOffer: 'ios', installHelpOpen: true, spaName: s.spaName }) : ''}`;
  }
  if (!s.pushSupported) {
    return `
      <div class="pp-switch-row pp-switch-row-static">
        <span class="pp-switch-text"><span>Notifications on this device</span><small>${s.runningAsApp ? 'Not available in this browser.' : 'Open your account page or add it to your home screen to turn these on.'}</small></span>
      </div>`;
  }
  return `
      <label class="pp-switch-row">
        <span class="pp-switch-text"><span>Notifications on this device</span><small>${s.pushEnabled ? `On${s.pushDevices > 1 ? ` · ${s.pushDevices} devices` : ''}` : 'Reminders and replies pop up here'}</small></span>
        <input type="checkbox" class="pp-switch-input" data-action="push-toggle" ${checked(s.pushEnabled)} ${disabled(busy)}>
        <span class="pp-switch" aria-hidden="true"></span>
      </label>
      ${s.pushEnabled ? `<div class="pp-push-test"><button type="button" class="pp-link-btn" data-action="push-test"${busy ? ' disabled' : ''}>Send a test notification</button>${s.pushNote ? ` <span class="pp-muted-note">${esc(s.pushNote)}</span>` : ''}</div>` : ''}
      ${s.pushError ? `<div class="pp-error-msg">${esc(s.pushError)}</div>` : ''}`;
}

/** How we reach them — toggles, saved the moment one is flipped (Tim,
 *  2026-11-02): texts and emails about their OWN appointments, then
 *  promotions by text / email. */
function renderMarketingPrefs(s: PortalState): string {
  const c = s.client;
  if (!c) return '';
  const row = (field: 'marketingTexts' | 'marketingEmails' | 'apptTexts' | 'apptEmails', label: string, on: boolean, hint = '') => `
      <label class="pp-switch-row">
        <span class="pp-switch-text"><span>${label}</span>${hint ? `<small>${hint}</small>` : ''}</span>
        <input type="checkbox" class="pp-switch-input" data-action="marketing-pref-toggle" data-pref="${field}" ${checked(on)} ${disabled(s.marketingPrefsSaving)}>
        <span class="pp-switch" aria-hidden="true"></span>
      </label>`;
  return `
    <div class="pp-wallet-pay" style="margin-top:18px">
      <div class="pp-section-title" style="padding-left:0">How we reach you</div>
      <div class="pp-wallet-pay-note">About your own appointments — booking confirmations, reminders, receipts.</div>
      ${row('apptTexts', 'Texts about my appointments', c.apptTexts !== false)}
      <div class="pp-sms-disclosure">${smsApptDisclosure(s.spaName)}</div>
      ${row('apptEmails', 'Emails about my appointments', c.apptEmails !== false)}
      ${renderPushRow(s)}
      <div class="pp-section-title" style="padding-left:0;margin-top:18px">Offers &amp; news from us</div>
      <div class="pp-wallet-pay-note">Promotions and offers only.</div>
      ${row('marketingTexts', 'Receive promotional and marketing texts', !!c.marketingTexts)}
      <div class="pp-sms-disclosure">${smsMarketingDisclosure(s.spaName)}</div>
      ${row('marketingEmails', 'Receive promotional and marketing emails', !!c.marketingEmails)}
      ${s.marketingPrefsError ? `<div class="pp-error-msg">${esc(s.marketingPrefsError)}</div>` : ''}
    </div>`;
}

/** 'Send us a message': a note to the spa; it lands in the Inbox on the
 *  client's own thread and the spa texts back. */
function renderPortalMessage(s: PortalState): string {
  const last4 = (s.client?.phone || '').replace(/\D/g, '').slice(-4);
  const name = s.client?.name || s.client?.firstName || '';
  if (s.messageSent) {
    return `
      <div class="pp-gc-success-wrap">
        <div class="pp-gc-success-icon">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <div class="pp-gc-success-title">Thanks — we have your message</div>
        <div class="pp-gc-success-msg">We'll get back to you shortly${last4 ? ` at your number ending in ${esc(last4)}` : ''}.</div>
        <button class="pp-gc-close-btn" data-action="portal-message-done">Back to my account</button>
      </div>`;
  }
  // The same form as the website's 'leave us a message' (Tim, 2026-11-02):
  // who we will reach, the message, how they want to be reached, the consent
  // line. Signed in, the name and number are the record's — no fields to type.
  const sending = s.messageSending;
  return `
    <div class="pp-wallet-pay">
      <div class="pp-section-title" style="padding-left:0">Send us a message</div>
      <div class="pp-bk-callback">
        <div class="pp-bk-callback-intro">Leave us a message and we will get back to you shortly!</div>
        <div class="pp-bk-callback-known">Hey <b>${esc(name)}</b>! We'll contact you back${last4 ? ` at your number ending in ${esc(last4)}` : ''}.</div>
        <label class="pp-bk-callback-field">
          <span class="pp-bk-callback-lbl">Message</span>
          <textarea class="pp-bk-callback-input pp-bk-callback-msg" data-field="messageText" rows="4" maxlength="1000" placeholder="Let us know how we can help!"${sending ? ' disabled' : ''}>${esc(s.messageText)}</textarea>
        </label>
        <div class="pp-bk-callback-pref-label">How should we reach you?</div>
        <div class="pp-bk-callback-prefs" role="radiogroup" aria-label="How should we reach you?">
          ${CALLBACK_PREFS.map(p => `<button type="button" class="pp-bk-callback-pref${s.callbackPreference === p.key ? ' selected' : ''}" role="radio" aria-checked="${s.callbackPreference === p.key}" data-action="pick-callback-pref" data-pref="${p.key}"${sending ? ' disabled' : ''}>${p.label}</button>`).join('')}
        </div>
        ${s.messageError ? `<div class="pp-bk-callback-err">${esc(s.messageError)}</div>` : ''}
        <div class="pp-bk-callback-consent">By submitting this form you agree to receive text messages from us about your request. Message and data rates may apply; reply STOP to opt out.</div>
        <button class="pp-bk-callback-btn" data-action="portal-message-submit"${!s.messageText.trim() || sending ? ' disabled' : ''}>${sending ? 'Sending…' : 'Send'}</button>
      </div>
    </div>`;
}

/** 'Remove this card?' — the Wallet's own confirmation, which says what the
 *  card is doing for them (default, a membership billed to it) before it goes.
 *  A mis-tap used to meet only the browser's bare confirm(). */
export function walletRemoveModal(s: { walletRemoveCardId: number | null; walletCardBusy: boolean;
  wallet: { creditCards: { id: number; brand: string; last4: string; isDefault?: boolean }[] } | null;
  memberships?: { planName: string; status: string }[] | null }): string {
  if (s.walletRemoveCardId == null) return '';
  const card = (s.wallet?.creditCards || []).find(c => c.id === s.walletRemoveCardId);
  if (!card) return '';
  const others = (s.wallet?.creditCards || []).filter(c => c.id !== card.id);
  const billed = (s.memberships || []).filter(m => ['active', 'past_due', 'unpaid', 'cancelling'].includes(m.status));
  const notes: string[] = [];
  if (card.isDefault && others.length) notes.push(`This is your default card — ${esc(others[0].brand)} ····${esc(others[0].last4)} will be charged instead.`);
  if (card.isDefault && !others.length) notes.push('This is your only card on file.');
  if (billed.length && (card.isDefault || !others.length)) {
    notes.push(`Your ${billed.map(m => esc(m.planName)).join(' and ')} membership is billed to your card on file — the next payment will fail until you add another card.`);
  }
  if (!others.length) notes.push('Without a card on file you may be asked for a deposit the next time you book.');
  return `
    <div class="pp-modal-backdrop">
      <div class="pp-modal-card pp-leave-card">
        <div class="pp-modal-title">Remove ${esc(card.brand)} ····${esc(card.last4)}?</div>
        <div class="pp-modal-body">${notes.length ? notes.map(n => `<p>${n}</p>`).join('') : '<p>It will no longer be saved to your account.</p>'}</div>
        <div class="pp-leave-actions">
          <button class="pp-btn-outline" data-action="wallet-remove-cancel" ${disabled(s.walletCardBusy)} autofocus>Keep it</button>
          <button class="pp-btn-primary pp-btn-danger" data-action="wallet-remove-confirm" ${disabled(s.walletCardBusy)}>${s.walletCardBusy ? 'Removing…' : 'Remove card'}</button>
        </div>
      </div>
    </div>`;
}

/** Wallet → pay into the account balance: an amount (what is owed is
 *  prefilled), a card on file or a new one, Pay. */
function renderWalletPay(s: PortalState): string {
  if (s.walletLoading) return renderLoading();
  const owed = s.walletOwed;
  const done = s.walletPaySuccess;
  if (done) {
    const bal = done.accountBalance;
    return `
      <div class="pp-gc-success-wrap">
        <div class="pp-gc-success-icon">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <div class="pp-gc-success-title">Payment received</div>
        <div class="pp-gc-success-msg">${formatCurrency(done.amount)} was charged${done.cardLast4 ? ` to ${esc(done.cardBrand || 'your card')} ····${esc(done.cardLast4)}` : ''}.<br>
          ${bal < 0 ? `Your account still owes ${formatCurrency(-bal)}.` : `Your account balance is now ${formatCurrency(bal)}.`}<br>
          <span class="pp-muted-note">Receipt ${esc(done.saleNumber)} is under Receipts.</span></div>
        <button class="pp-gc-close-btn" data-action="wallet-pay-done">Back to wallet</button>
      </div>`;
  }
  const amount = s.walletPayAmountNum;
  const presets = owed > 0 ? [owed] : [25, 50, 100, 200];
  const presetBtns = presets.map(a => `<button class="pp-bk-group-btn${amount === a ? ' selected' : ''}" data-action="wallet-pay-preset" data-amount="${a}">${formatCurrency(a)}</button>`).join('');
  const cards = (s.wallet?.creditCards || []).filter(c => c.hasSavedCard !== false);
  const cardRows = cards.map(c => `
    <label class="pp-wallet-pay-card${s.walletPayCardId === c.id ? ' selected' : ''}" data-action="wallet-pay-card" data-id="${c.id}">
      <input type="radio" name="pp-wallet-pay-card" ${s.walletPayCardId === c.id ? 'checked' : ''}>
      <span>${esc(c.brand)} ····${esc(c.last4)}</span>
      ${c.isDefault ? '<span class="pp-badge pp-badge-green">Default</span>' : ''}
    </label>`).join('');
  const newCard = s.stripePublishableKey ? `
    <label class="pp-wallet-pay-card${s.walletPayCardId === 'new' ? ' selected' : ''}" data-action="wallet-pay-card" data-id="new">
      <input type="radio" name="pp-wallet-pay-card" ${s.walletPayCardId === 'new' ? 'checked' : ''}>
      <span>${cards.length ? 'Use a different card' : 'Pay with a card'}</span>
    </label>
    ${s.walletPayCardId === 'new' ? `
      <div class="pp-gc-card-field-wrap" style="padding:10px 12px;margin:8px 0"><div id="pp-stripe-card-element-wallet" style="min-height:20px"></div></div>
      <label class="pp-checkbox-row" style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--pp-espresso);cursor:pointer">
        <input type="checkbox" data-action="wallet-pay-toggle-save" ${s.walletPaySaveCard ? 'checked' : ''} style="width:16px;height:16px;accent-color:var(--pp-accent,#B8623E)">
        Keep this card on file
      </label>` : ''}` : (cards.length ? '' : '<div class="pp-empty-small">Online card payments are not set up — please call us.</div>');
  const canPay = amount > 0 && s.walletPayCardId !== null && !s.walletPaying && (s.walletPayCardId !== 'new' || !!s.stripePublishableKey);
  return `
    <div class="pp-wallet-pay">
      <div class="pp-section-title" style="padding-left:0">${owed > 0 ? 'Pay your balance' : 'Add funds to your account'}</div>
      ${owed > 0 ? `<div class="pp-wallet-pay-note">Your account owes ${formatCurrency(owed)}. Pay all of it, or part of it now.</div>`
                 : `<div class="pp-wallet-pay-note">Money you add is credit on your account, spent at checkout on your next visit.</div>`}
      <label class="pp-f-label">Amount</label>
      <div class="pp-wallet-pay-amount">
        <span class="pp-wallet-pay-currency">$</span>
        <input class="pp-f-input" type="text" inputmode="decimal" placeholder="0.00" value="${esc(s.walletPayAmount)}" data-field="walletPayAmount">
      </div>
      <div class="pp-bk-group-btns" style="margin:8px 0 16px">${presetBtns}</div>
      <label class="pp-f-label">Pay with</label>
      <div class="pp-wallet-pay-cards">${cardRows}${newCard}</div>
      ${s.walletPayError ? `<div class="pp-error-msg">${esc(s.walletPayError)}</div>` : ''}
      <button class="pp-btn-primary" data-action="wallet-pay-submit" ${disabled(!canPay)} style="margin-top:16px">
        ${s.walletPaying ? 'Processing…' : (amount > 0 ? `Pay ${formatCurrency(amount)}` : 'Pay')}
      </button>
    </div>`;
}

// ── FORMS ───────────────────────────────────────────────────────────────────

function renderForms(s: PortalState): string {
  if (s.formSubmissionsLoading) return renderLoading();
  if (!s.formSubmissions || s.formSubmissions.length === 0) {
    return renderEmpty('No forms', 'Forms you have submitted will appear here.');
  }

  return s.formSubmissions.map(f => `
    <button class="pp-list-item" data-action="view-form" data-id="${f.id}">
      <div class="pp-list-item-main">
        <div class="pp-list-item-title">${esc(f.templateName)}</div>
        <div class="pp-list-item-sub">${esc(f.submittedAt ? formatDate(f.submittedAt.split('T')[0]) : formatDate(f.createdAt.split('T')[0]))}</div>
      </div>
      <div class="pp-list-item-right">
        <span class="${formStatusBadge(f.status)}">${esc(f.status)}</span>
        <span class="pp-list-chevron">${ICON_CHEVRON}</span>
      </div>
    </button>`).join('');
}

function renderFormDetail(s: PortalState): string {
  if (s.formDetailLoading) return renderLoading();
  const f = s.selectedFormDetail;
  if (!f) return renderEmpty('Form not found', '');

  const fields = f.fields || [];
  const responses = f.responses || {};

  // Each answer under its question, full width — the two-column rows put a
  // long question beside a one-word answer and read as a jumble.
  let fieldsHtml = '';
  if (fields.length > 0) {
    fieldsHtml = fields.map(field => formAnswerHtml(field, responses[field.id])).join('');
  } else {
    fieldsHtml = Object.entries(responses).map(([key, val]) => formAnswerHtml({ id: key, type: 'text', label: key }, val)).join('');
  }

  return `
    <div class="pp-detail-header pp-form-head">
      <div>
        <div class="pp-detail-conf">${esc(f.templateName)}</div>
        <div class="pp-form-head-sub">Submitted ${esc(f.submittedAt ? formatDate(f.submittedAt.split('T')[0]) : formatDate(f.createdAt.split('T')[0]))}</div>
      </div>
      <span class="${formStatusBadge(f.status)}">${esc(f.status)}</span>
    </div>
    <div class="pp-form-answers">
      ${fieldsHtml || '<div class="pp-empty-small">No responses recorded</div>'}
    </div>
    ${f.reviewNotes ? `
    <div class="pp-detail-section">
      <div class="pp-detail-section-title">Review Notes</div>
      <div class="pp-detail-notes">${esc(f.reviewNotes)}</div>
    </div>` : ''}`;
}

/** One answer, as text. Mirrors the server's `_answer_cell`: Yes/No for a
 *  tick, the parts of a client field, 'Signed' for a signature, lists joined. */
export function formAnswerText(type: string, val: unknown): string {
  if (val === undefined || val === null || val === '') return '';
  if (type === 'signature' || type === 'drawn_signature') return val ? 'Signed' : '';
  if (type === 'markup_image') return val ? 'Marked up' : '';
  if (type === 'client' && typeof val === 'object' && !Array.isArray(val)) {
    const o = val as Record<string, string>;
    return [[o.firstName, o.lastName].filter(Boolean).join(' ').trim(), o.phone, o.email].filter(Boolean).join(' · ');
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.map(v => String(v)).join(', ');
  if (typeof val === 'object') return Object.entries(val as Record<string, unknown>).map(([k, v]) => `${k}: ${v}`).join(', ');
  return String(val);
}

/** A question with its answer. A checkbox list shows every option with its
 *  own Yes / No; headings and paragraphs are shown as such; an unanswered
 *  question shows a dash. */
export function formAnswerHtml(field: { id: string; type: string; label?: string; options?: Array<string | { value: string; label: string }>; text?: string }, val: unknown): string {
  const t = field.type || 'text';
  if (t === 'heading' || t === 'section') return `<div class="pp-form-q-heading">${esc(field.label || String(field.text || ''))}</div>`;
  if (t === 'paragraph' || t === 'notice') return '';
  const label = field.label || field.id;
  if (t === 'checklist' || t === 'checkboxes' || t === 'multi_select') {
    const picked = Array.isArray(val) ? val.map(v => String(v)) : (val ? [String(val)] : []);
    const opts = (field.options || []).map(o => typeof o === 'string' ? o : (o.label || o.value));
    const rows = opts.map(o => ({ o, on: picked.includes(o) }));
    for (const p of picked) if (!opts.includes(p)) rows.push({ o: p, on: true });
    if (rows.length && val !== undefined && val !== null) {
      return `
      <div class="pp-form-q">
        <div class="pp-form-q-label">${esc(label)}</div>
        ${rows.map(r => `<div class="pp-form-q-opt ${r.on ? 'is-yes' : 'is-no'}"><span>${esc(r.o)}</span><b>${r.on ? 'Yes' : 'No'}</b></div>`).join('')}
      </div>`;
    }
  }
  const text = formAnswerText(t, val);
  const yesNo = t === 'checkbox' || t === 'acknowledgment' || typeof val === 'boolean';
  return `
      <div class="pp-form-q">
        <div class="pp-form-q-label">${esc(label)}</div>
        <div class="pp-form-q-answer${text ? '' : ' is-empty'}${yesNo && text ? (text === 'Yes' ? ' is-yes' : ' is-no') : ''}">${text ? esc(text) : '—'}</div>
      </div>`;
}

// ── RECURRING SERIES ─────────────────────────────────────────────────────────

function _describePattern(rs: { patternType: string; intervalWeeks: number; daysOfWeek: string[]; dayOfMonth: number | null; nthWeekdays: number[]; weekdayForNth: string; startTime: string }): string {
  const time12 = (() => {
    const [h, m] = rs.startTime.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
  })();
  const dayNames: Record<string, string> = { MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday' };
  const days = rs.daysOfWeek.map(d => dayNames[d] || d).join(', ');

  switch (rs.patternType) {
    case 'weekly': return `Every ${days} at ${time12}`;
    case 'every_n_weeks': return `Every ${rs.intervalWeeks} weeks on ${days} at ${time12}`;
    case 'monthly_date': return `Monthly on the ${rs.dayOfMonth}${_ordSuffix(rs.dayOfMonth || 1)} at ${time12}`;
    case 'monthly_nth_weekday': {
      const nth = rs.nthWeekdays.map(n => `${n}${_ordSuffix(n)}`).join(' & ');
      return `${nth} ${dayNames[rs.weekdayForNth] || rs.weekdayForNth} of each month at ${time12}`;
    }
    default: return `At ${time12}`;
  }
}
function _ordSuffix(n: number): string {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
}

function renderRecurring(s: PortalState): string {
  if (s.recurringLoading) return renderLoading();
  if (!s.recurringSeries || s.recurringSeries.length === 0) {
    return renderEmpty('No recurring appointments', 'Recurring series you set up will appear here.');
  }

  return s.recurringSeries.map(rs => {
    const svcNames = rs.items.map((it: any) => it.serviceName).join(', ');
    const staffNames = [...new Set(rs.items.map((it: any) => it.staffName))].join(', ');
    const desc = _describePattern(rs);
    const statusBadge = rs.status === 'active'
      ? '<span class="pp-badge pp-badge-green">Active</span>'
      : rs.status === 'paused'
        ? '<span class="pp-badge pp-badge-amber">Paused</span>'
        : '<span class="pp-badge pp-badge-gray">Cancelled</span>';

    return `
    <button class="pp-list-item" data-action="view-recurring" data-id="${rs.id}">
      <div class="pp-list-item-main">
        <div class="pp-list-item-title">${esc(svcNames)}</div>
        <div class="pp-list-item-sub">${esc(desc)}</div>
        <div class="pp-list-item-sub" style="margin-top:2px">${esc(staffNames)}</div>
      </div>
      <div class="pp-list-item-right">
        ${statusBadge}
        <span class="pp-list-chevron">${ICON_CHEVRON}</span>
      </div>
    </button>`;
  }).join('');
}

function renderRecurringDetail(s: PortalState): string {
  if (s.seriesDetailLoading) return renderLoading();
  const rs = s.selectedSeriesDetail;
  if (!rs) return renderEmpty('Series not found', '');

  const svcNames = rs.items.map((it: any) => it.serviceName).join(', ');
  const staffNames = [...new Set(rs.items.map((it: any) => it.staffName))].join(', ');
  const desc = _describePattern(rs);
  const statusBadge = rs.status === 'active'
    ? '<span class="pp-badge pp-badge-green">Active</span>'
    : rs.status === 'paused'
      ? '<span class="pp-badge pp-badge-amber">Paused</span>'
      : '<span class="pp-badge pp-badge-gray">Cancelled</span>';

  const upcoming = rs.upcomingBookings || [];
  const upcomingHtml = upcoming.length > 0
    ? upcoming.map(b => {
        const dateStr = formatDate(b.date);
        const statusCls = b.status === 'needs_reschedule' ? 'pp-badge pp-badge-amber'
          : b.status === 'cancelled' ? 'pp-badge pp-badge-gray'
          : 'pp-badge pp-badge-green';
        const statusLabel = b.status === 'needs_reschedule' ? 'Needs Reschedule'
          : b.status.charAt(0).toUpperCase() + b.status.slice(1);
        const skipBtn = (b.status !== 'cancelled' && b.status !== 'completed')
          ? `<button class="pp-btn-text pp-btn-sm" data-action="skip-occurrence" data-series-id="${rs.id}" data-date="${b.date}">Skip</button>`
          : '';
        const modifiedTag = b.recurringModified ? ' <span style="font-size:11px;color:#888">(modified)</span>' : '';
        return `
        <div class="pp-detail-row" style="align-items:center">
          <div>
            <span class="pp-detail-value">${dateStr}</span>${modifiedTag}
          </div>
          <div style="display:flex;align-items:center;gap:8px">
            <span class="${statusCls}">${statusLabel}</span>
            ${skipBtn}
          </div>
        </div>`;
      }).join('')
    : '<div class="pp-empty-small">No upcoming occurrences</div>';

  const cancelBtn = rs.status === 'active'
    ? `<div style="margin-top:20px;text-align:center">
        <button class="pp-btn-danger" data-action="cancel-recurring" data-id="${rs.id}">Cancel Entire Series</button>
      </div>`
    : '';

  return `
    <div class="pp-detail-header">
      <div class="pp-detail-conf">${esc(svcNames)}</div>
      ${statusBadge}
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-row">
        <span class="pp-detail-label">Provider</span>
        <span class="pp-detail-value">${esc(staffNames)}</span>
      </div>
      <div class="pp-detail-row">
        <span class="pp-detail-label">Schedule</span>
        <span class="pp-detail-value">${esc(desc)}</span>
      </div>
      <div class="pp-detail-row">
        <span class="pp-detail-label">Start Date</span>
        <span class="pp-detail-value">${formatDate(rs.startDate)}</span>
      </div>
      ${rs.endDate ? `
      <div class="pp-detail-row">
        <span class="pp-detail-label">End Date</span>
        <span class="pp-detail-value">${formatDate(rs.endDate)}</span>
      </div>` : ''}
    </div>
    <div class="pp-detail-section">
      <div class="pp-detail-section-title">Upcoming Appointments</div>
      ${upcomingHtml}
    </div>
    ${cancelBtn}`;
}

// ── REBOOK PICK ──────────────────────────────────────────────────────────────

function renderRebookPick(s: PortalState): string {
  const recentHtml = s.recentBookings.map(b => {
    const svcNames = b.items.map(it => it.serviceName).join(', ');
    const provNames = [...new Set(b.items.map(it => it.staffName))].join(', ');
    const addonNames = b.items.flatMap(it => (it.addonIds || []).length > 0 ? ['with enhancements'] : []);
    const addonNote = addonNames.length > 0 ? ' · with enhancements' : '';
    return `
      <button class="pp-list-item" data-action="rebook-appointment" data-id="${b.id}">
        <div class="pp-list-item-main">
          <div class="pp-list-item-title">${esc(svcNames || 'Appointment')}</div>
          <div class="pp-list-item-sub">${esc(provNames)} · ${esc(formatDate(b.date))}${addonNote}</div>
        </div>
        <div class="pp-list-item-right">
          <span class="pp-list-chevron">${ICON_CHEVRON}</span>
        </div>
      </button>`;
  }).join('');

  return `
    <div class="pp-bk-wrap">
      <div class="pp-bk-step-title">Book an Appointment</div>
      ${s.recentBookings.length > 0 ? `
        <div class="pp-section-title">Rebook a Previous Appointment</div>
        ${recentHtml}
        <div class="pp-rebook-divider"><span>Or</span></div>
      ` : ''}
      <button class="pp-btn-outline pp-rebook-new-btn" data-action="goto-book-categories">
        Book New Appointment
      </button>
    </div>`;
}

// ── BOOKING — LOCATION SELECTION ──────────────────────────────────────────

function renderBkLocation(s: PortalState): string {
  if (s.bookingDataLoading) return renderLoading();

  const rows = s.bookingLocations.map(loc => `
    <button class="pp-bk-loc-row" data-action="pick-location" data-id="${loc.id}">
      <span class="pp-bk-loc-name">${esc(loc.name)}</span>
      <span class="pp-bk-loc-arrow">&#x203A;</span>
    </button>`).join('');

  return `
    <div class="pp-bk-location">
      <div class="pp-bk-loc-heading">Select a location:</div>
      ${rows}
    </div>`;
}

// ── BOOKING — CATEGORIES ──────────────────────────────────────────────────

function renderBkCategories(s: PortalState): string {
  const resumeNoticeHtml = s.resumeNotice ? `<div class="pp-bk-notice-block" style="margin-bottom:12px;">${esc(s.resumeNotice)}</div>` : '';
  if (s.bookingDataLoading) return renderLoading();

  // Group size selector (only when allowGroupBookings is on and no items yet)
  // Also gated by location restriction: if groupBookingLocationIds is non-empty,
  // the selected location must be in the list
  let groupSizeHtml = '';
  const locationAllowed = s.groupBookingLocationIds.length === 0
    || (s.selectedLocationId != null && s.groupBookingLocationIds.includes(s.selectedLocationId));
  if (s.allowGroupBookings && locationAllowed && s.items.length === 0 && s.entryMode === 'booking') {
    const btns = Array.from({ length: s.maxGroupSize }, (_, i) => i + 1).map(n => {
      const sel = s.groupSize === n ? ' selected' : '';
      return `<button class="pp-bk-group-btn${sel}" data-action="set-group-size" data-size="${n}">${n}</button>`;
    }).join('');
    groupSizeHtml = `
      <div class="pp-bk-group-selector">
        <div class="pp-bk-group-label">How many people are you booking for?</div>
        <div class="pp-bk-group-btns">${btns}</div>
      </div>`;
  }

  // Person heading for group bookings
  let personHeadingHtml = '';
  if (s.groupSize > 1) {
    const label = s.currentGuestIndex === 0 ? 'Your services' : `Person ${s.currentGuestIndex + 1} — Select services`;
    personHeadingHtml = `<div class="pp-bk-person-heading">${esc(label)}</div>`;
  }

  // Cart summary when adding another service
  let summaryHtml = '';
  if (s.items.length > 0) {
    // Filter items for current guest
    const currentItems = s.items.filter(it => (it.guestIndex ?? 0) === s.currentGuestIndex);
    const otherItems = s.items.filter(it => (it.guestIndex ?? 0) !== s.currentGuestIndex);

    const itemRows = currentItems.map((item) => {
      const idx = s.items.indexOf(item);
      const addonSub = item.selectedAddons.length
        ? `<div class="pp-bk-booked-sub">(${esc(item.selectedAddons.map(a => a.name).join(', '))})</div>`
        : '';
      return `
        <div class="pp-bk-booked-item">
          <div class="pp-bk-booked-info">
            <div class="pp-bk-booked-name">${esc(item.service.name)}</div>
            ${addonSub}
            <div class="pp-bk-booked-sub">with ${esc(item.provider.name)}</div>
          </div>
          <button class="pp-bk-remove-link" data-action="remove-item" data-idx="${idx}">Remove</button>
        </div>`;
    }).join('');

    // Show other guests' items as a read-only summary
    let otherSummaryHtml = '';
    if (otherItems.length > 0) {
      const byGuest = new Map<number, typeof otherItems>();
      for (const it of otherItems) {
        const gi = it.guestIndex ?? 0;
        if (!byGuest.has(gi)) byGuest.set(gi, []);
        byGuest.get(gi)!.push(it);
      }
      const sections = Array.from(byGuest.entries()).map(([gi, items]) => {
        const gLabel = gi === 0 ? 'Your services' : `Person ${gi + 1}`;
        const rows = items.map(it => `<div class="pp-bk-booked-sub">${esc(it.service.name)} with ${esc(it.provider.name)}</div>`).join('');
        return `<div class="pp-bk-group-other"><div class="pp-bk-group-other-label">${esc(gLabel)}</div>${rows}</div>`;
      }).join('');
      otherSummaryHtml = sections;
    }

    if (currentItems.length > 0) {
      summaryHtml = `
        <div class="pp-bk-cat-summary">
          <div class="pp-bk-cat-summary-header">
            <div class="pp-bk-section-label" style="padding:0">${s.groupSize > 1 && s.currentGuestIndex > 0 ? 'Person ' + (s.currentGuestIndex + 1) + '\'s service' : 'Your service'}</div>
            <button class="pp-btn-outline pp-btn-sm" data-action="no-more">Next</button>
          </div>
          ${itemRows}
          <div class="pp-bk-cat-summary-hint">Choose another service below, or click "Next" to continue.</div>
        </div>
        ${otherSummaryHtml}`;
    } else {
      summaryHtml = otherSummaryHtml;
    }
  }

  const filterPlan = s.filterPlan;
  const filterIds = filterPlan ? new Set(planServiceIds(filterPlan)) : null;
  // The credit narrows the menu while nothing is in the cart yet; once the
  // first service is picked, anything else may be added at the regular price
  // (Tim, 2026-11-01 — the portal offer flow could not add a second service).
  const narrow = s.items.length === 0;
  const creditIds = narrow && s.bookingCredit?.serviceIds.length ? new Set(s.bookingCredit.serviceIds) : null;
  const creditCatIds = narrow && !creditIds && s.bookingCredit?.categoryIds?.length ? new Set(s.bookingCredit.categoryIds.map(String)) : null;
  const cats = s.categories.filter(cat => !cat.hidden).map(cat => {
    const isExpanded = s.expandedCat !== null && String(s.expandedCat) === String(cat.id);
    let filteredSvcs = s.groupSize > 1 && s.groupBookingExcludedServiceIds.length > 0
      ? cat.services.filter(svc => !s.groupBookingExcludedServiceIds.includes(Number(svc.id)))
      : cat.services;
    // The membership filter narrows the menu itself.
    if (filterIds) filteredSvcs = filteredSvcs.filter(svc => filterIds.has(Number(svc.id)));
    // Booking with a package / membership session: only what it covers.
    if (creditIds) filteredSvcs = filteredSvcs.filter(svc => creditIds.has(Number(svc.id)));
    // An offer scoped to categories: only those categories' services.
    if (creditCatIds && !creditCatIds.has(String(cat.id))) filteredSvcs = [];
    const svcs = filteredSvcs.map(svc => {
      const priceLabel = svc.price > 0
        ? (svc.showPriceAsMinimum ? 'from $' : '$') + svc.price.toFixed(2)
        : 'Free';
      if (svc.promptToCall) {
        const phone = s.businessPhone || '';
        return `
      <div class="pp-bk-svc-item pp-bk-svc-call-only">
        <div class="pp-bk-svc-info">
          <div class="pp-bk-svc-name">${esc(svc.name)}</div>
          <div class="pp-bk-svc-call-msg">${phone ? `Call <a href="tel:${esc(phone)}">${esc(phone)}</a> to book` : 'Please call to book'}</div>
        </div>
        <div class="pp-bk-svc-price">${priceLabel}</div>
      </div>`;
      }
      const groupBadge = svc.capacity !== undefined && svc.capacity !== 1
        ? '<span class="pp-bk-svc-group-badge">Group</span>' : '';
      const memPill = membershipPill(serviceVerdict(s.eligibility, svc.id));
      return `
      <div class="pp-bk-svc-item" data-action="pick-service" data-svcid="${esc(svc.id)}">
        <div class="pp-bk-svc-info">
          <div class="pp-bk-svc-name">${esc(svc.name)}${groupBadge}</div>
          ${memPill ? `<div class="pp-mem-pill-row">${memPill}</div>` : ''}
          <button class="pp-bk-svc-more" data-action="open-modal" data-svcid="${esc(svc.id)}">more info</button>
        </div>
        <div class="pp-bk-svc-price">${priceLabel}</div>
      </div>`;
    }).join('');
    if (filteredSvcs.length === 0) return '';
    return `
      <div class="pp-bk-cat">
        <div class="pp-bk-cat-header${isExpanded ? ' expanded' : ''}" data-action="toggle-cat" data-catid="${esc(cat.id)}">
          <span class="pp-bk-cat-name">${esc(cat.name)}</span>
          <svg class="pp-bk-cat-chevron${isExpanded ? ' open' : ''}" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 3L9 7L5 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="pp-bk-svc-list" style="${isExpanded ? '' : 'display:none'}">
          ${svcs}
        </div>
      </div>`;
  }).join('');

  // Returning client button (booking entry mode only, no items yet) — hidden
  // when the client portal is off (there is no account to log in to).
  const rcBtn = (s.portalEnabled && s.entryMode === 'booking' && s.items.length === 0 && !s.clientId) ? `
    <div class="pp-bk-rc-intro">
      <button class="pp-bk-btn-returning" data-action="open-portal">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="5" r="3" stroke="currentColor" stroke-width="1.3"/>
          <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
        Returning client? Log in here
      </button>
    </div>` : '';

  // Gift card + package + membership buttons (booking entry mode only, hidden when no data)
  const pkgBtn = s.packages.length > 0 ? `
      <button class="pp-bk-btn-gift" data-action="buy-package">
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none"><rect x="2" y="4" width="13" height="2" rx="1" stroke="#1E1813" stroke-width="1.3"/><rect x="2" y="7.5" width="13" height="2" rx="1" stroke="#1E1813" stroke-width="1.3"/><rect x="2" y="11" width="13" height="2" rx="1" stroke="#1E1813" stroke-width="1.3"/></svg>
        Buy a package
      </button>` : '';
  const memBtn = (s.membershipPlans.length > 0 && !s.membershipsSilent) ? `
      <button class="pp-bk-btn-gift" data-action="buy-membership">
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none"><path d="M8.5 2C6.01 2 4 4.01 4 6.5C4 8.11 4.87 9.52 6.18 10.24C4.28 11.07 3 12.96 3 15.12V15.5H14V15.12C14 12.96 12.72 11.07 10.82 10.24C12.13 9.52 13 8.11 13 6.5C13 4.01 10.99 2 8.5 2Z" stroke="#1E1813" stroke-width="1.3" fill="none"/></svg>
        Join a membership
      </button>` : '';
  // Not inside the portal's group booking: those are the portal's own pages.
  const giftBtns = (s.entryMode === 'booking' && !s.portalGroupBooking) ? `
    <div class="pp-bk-gift-btns">
      <button class="pp-bk-btn-gift" data-action="open-gift-card">
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none"><rect x="2.5" y="6.5" width="12" height="8" rx="1" stroke="#1E1813" stroke-width="1.3"/><line x1="2.5" y1="9" x2="14.5" y2="9" stroke="#1E1813" stroke-width="1.3"/><line x1="8.5" y1="6.5" x2="8.5" y2="14.5" stroke="#1E1813" stroke-width="1.3"/><path d="M8.5 6.5C8.5 6.5 6.5 4 5.5 4C4.5 4 3.5 5 4.5 5.8C5.5 6.5 8.5 6.5 8.5 6.5Z" stroke="#1E1813" stroke-width="1.3"/><path d="M8.5 6.5C8.5 6.5 10.5 4 11.5 4C12.5 4 13.5 5 12.5 5.8C11.5 6.5 8.5 6.5 8.5 6.5Z" stroke="#1E1813" stroke-width="1.3"/></svg>
        Buy a gift card
      </button>
      ${pkgBtn}
      ${memBtn}
    </div>` : '';

  let aiBanner = '';
  if (s.aiConciergeEnabled && s.items.length === 0) {
    aiBanner = `<button class="pp-ai-banner" data-action="nav-ai-concierge">
      <span class="pp-ai-banner-icon">&#10024;</span>
      <span class="pp-ai-banner-text"><strong>Try our AI assistant</strong> — describe what you'd like and we'll find the best options</span>
      <span class="pp-ai-banner-arrow">&rarr;</span>
    </button>`;
  } else if (s.webChatEnabled && !s.aiConciergeEnabled && s.items.length === 0) {
    aiBanner = `<button class="pp-ai-banner" data-action="nav-ai-concierge">
      <span class="pp-ai-banner-icon">&#128172;</span>
      <span class="pp-ai-banner-text"><strong>Chat with us</strong> — have a question? Send us a message</span>
      <span class="pp-ai-banner-arrow">&rarr;</span>
    </button>`;
  }

  // A message carried into the menu (e.g. rebook couldn't find the old
  // service online) — shown once, at the top, instead of a silent redirect.
  const noticeHtml = s.bookingError ? `<div class="pp-bk-error" style="margin-bottom:12px">${esc(s.bookingError)}</div>` : '';
  // Nothing membership-shaped when there is nothing to say: no plans sold
  // online and no membership of the client's own.
  const memChips = s.membershipsSilent ? '' : membershipFilterChips(s);
  const memBar = membershipFilterBar(s);
  const memLegend = s.eligibility.ownedPlanIds.length ? membershipLegend('member') : '';
  // A filter that hides the whole menu explains itself rather than looking
  // like a spa with nothing to book.
  const filteredEmpty = s.filterPlan && !cats.trim() ? membershipFilterEmpty(s) : '';
  const creditBar = s.bookingCredit
    ? (s.items.length === 0 ? creditBanner(s)
      : `<div class="pp-bk-cat-summary-hint" style="margin-bottom:8px">Your ${s.bookingCredit.kind === 'promotion' ? 'offer' : s.bookingCredit.kind} ${s.bookingCredit.kind === 'promotion' ? 'applies to the service above' : 'covers the services it includes'}; anything else you add is at the regular price.</div>`)
    : '';
  return `${resumeNoticeHtml}
    ${creditBar}
    ${aiBanner}
    ${noticeHtml}
    ${rcBtn}
    ${groupSizeHtml}
    ${memBar}
    ${memChips}
    ${personHeadingHtml || '<div class="pp-bk-step-intro">Select a service:</div>'}
    ${memLegend}
    <div class="pp-bk-wrap">
      ${summaryHtml}
      ${filteredEmpty || cats}
    </div>
    ${giftBtns}`;
}

// ── BOOKING — AI CONCIERGE ──────────────────────────────────────────────

function renderBkAi(s: PortalState): string {
  const msgs = s.aiMessages.map((m, msgIdx) => {
    if (m.role === 'user') {
      return `<div class="pp-ai-msg pp-ai-msg--user"><div class="pp-ai-bubble">${esc(m.text)}</div></div>`;
    }
    if ((m as any).role === 'staff') {
      return `<div class="pp-ai-msg pp-ai-msg--staff"><div class="pp-ai-staff-label">Staff</div><div class="pp-ai-bubble">${esc(m.text)}</div></div>`;
    }
    let optionsHtml = '';
    if (m.options && m.options.length > 0) {
      optionsHtml = m.options.map((opt, idx) => {
        const itemLines = opt.items.map(it =>
          `<div class="pp-ai-opt-item">${esc(it.serviceName)} with ${esc(it.staffName)} <span class="pp-muted">${esc(it.startTime)}</span></div>`
        ).join('');
        const durH = Math.floor(opt.totalDuration / 60);
        const durM = opt.totalDuration % 60;
        const durStr = durH > 0 ? (durM > 0 ? `${durH}h ${durM}m` : `${durH}h`) : `${durM}m`;
        return `
          <div class="pp-ai-option-card">
            ${itemLines}
            <div class="pp-ai-opt-meta">
              <span>${esc(opt.displayDate)}</span>
              <span>$${opt.totalPrice.toFixed(2)}</span>
              <span>${esc(durStr)}</span>
            </div>
            ${(opt as any).suggestedUpgrades?.length ? `<div class="pp-ai-upgrades"><span class="pp-ai-addons-label">You might also like:</span>${((opt as any).suggestedUpgrades as any[]).map((u: any, ui: number) => `<button class="pp-ai-upgrade-chip" data-action="ai-pick-upgrade" data-opt-index="${idx}" data-upgrade-index="${ui}" data-service-id="${u.serviceId}" data-service-name="${esc(u.name)}" data-price="${u.price}" data-duration="${u.duration || 60}">${esc(u.name)} ($${u.price.toFixed(2)}) <span class="pp-ai-upgrade-reason">— ${esc(u.reason)}</span></button>`).join('')}</div>` : ''}
            ${(opt as any).suggestedAddons?.length ? `<div class="pp-ai-addons"><span class="pp-ai-addons-label">Popular enhancements:</span>${((opt as any).suggestedAddons as any[]).map((a: any, ai: number) => {
              const selected = (opt as any)._selectedAddonIds?.includes(a.optionId);
              return `<button class="pp-ai-addon-chip${selected ? ' pp-ai-addon-selected' : ''}" data-action="ai-toggle-addon" data-opt-index="${idx}" data-addon-index="${ai}" data-option-id="${a.optionId}" data-name="${esc(a.name)}" data-price="${a.price}" data-duration-add="${a.durationAdd || 0}">${selected ? '✓' : '+'} ${esc(a.name)} ($${a.price.toFixed(2)})</button>`;
            }).join('')}</div>` : ''}
            <button class="pp-btn-primary pp-ai-book-btn" data-action="ai-book-option" data-index="${idx}">Book This</button>
          </div>`;
      }).join('');
    }
    // Quick reply buttons — only on the last assistant message
    let quickRepliesHtml = '';
    if (m.role === 'assistant' && msgIdx === s.aiMessages.length - 1 && (m as any).quickReplies?.length) {
      quickRepliesHtml = `<div class="pp-ai-quick-replies">${((m as any).quickReplies as any[]).map((qr: any) =>
        `<button class="pp-ai-quick-reply" data-action="ai-quick-reply" data-value="${esc(qr.value)}">${esc(qr.label)}</button>`
      ).join('')}</div>`;
    }
    return `<div class="pp-ai-msg pp-ai-msg--assistant"><div class="pp-ai-bubble">${esc(m.text)}</div>${optionsHtml}${quickRepliesHtml}</div>`;
  }).join('');

  let welcome = '';
  if (s.aiMessages.length === 0) {
    if (s.aiConciergeEnabled) {
      const defaultChips = ['Massage this week', 'Facial this weekend', 'Massage + facial'];
      const chips = s.aiConciergeChips.length > 0 ? s.aiConciergeChips : defaultChips;
      const chipsHtml = chips.map(c =>
        `<button class="pp-ai-chip" data-action="ai-chip" data-text="${esc(c)}">${esc(c)}</button>`
      ).join('');
      welcome = `<div class="pp-ai-msg pp-ai-msg--assistant">
        <div class="pp-ai-bubble">Hi! Tell me what you'd like to book and I'll find the best options for you.</div>
      </div>
      <div class="pp-ai-chips">${chipsHtml}</div>`;
    } else {
      welcome = `<div class="pp-ai-msg pp-ai-msg--assistant">
        <div class="pp-ai-bubble">Hi! Send us a message and we'll get back to you shortly.</div>
      </div>`;
    }
  }

  const loading = s.aiLoading ? `
    <div class="pp-ai-msg pp-ai-msg--assistant">
      <div class="pp-ai-bubble pp-ai-loading"><span></span><span></span><span></span></div>
    </div>` : '';

  return `
    <div class="pp-ai-step">
    <div class="pp-ai-chat" id="pp-ai-chat">
      ${welcome}
      ${msgs}
      ${loading}
    </div>
    ${!(s as any).aiPhoneSubmitted && s.aiConversationId ? `
    <div class="pp-ai-phone-bar${(s as any).aiPhoneCollapsed ? ' collapsed' : ''}">
      <div class="pp-ai-phone-header" data-action="ai-phone-toggle">
        <span>&#128222; Leave your number for follow-up</span>
        <span class="pp-ai-phone-chevron">${(s as any).aiPhoneCollapsed ? '&#9660;' : '&#9650;'}</span>
      </div>
      ${!(s as any).aiPhoneCollapsed ? ((s as any).aiPhoneShowVerify ? (
        (s as any).aiPhoneOtpSent ? `<div class="pp-ai-phone-body" style="flex-direction:column;gap:6px">
          <div style="font-size:12px;color:var(--pp-muted)">Please enter the code we texted to your phone.</div>
          <div style="display:flex;gap:8px">
            <input type="text" class="pp-ai-phone-input" data-field="aiPhoneOtpCode" placeholder="6-digit code" maxlength="6" value="${esc((s as any).aiPhoneOtpCode || '')}" style="letter-spacing:4px;text-align:center" />
            <button class="pp-ai-phone-submit" data-action="ai-phone-otp-submit">Verify</button>
          </div>
          <div style="display:flex;gap:12px">
            ${(s as any).aiPhoneOtpError ? `<span style="font-size:12px;color:#e53e3e">${esc((s as any).aiPhoneOtpError)}</span>` : ''}
            <button class="pp-btn-link" style="font-size:11px;margin-left:auto" data-action="ai-phone-verify">Resend code</button>
          </div>
          <button class="pp-btn-link" style="font-size:12px" data-action="ai-phone-skip-verify">Skip verification</button>
        </div>` : `<div class="pp-ai-phone-body" style="flex-direction:column;gap:6px">
          <div style="font-size:12px;color:var(--pp-espresso)">Welcome back, ${esc((s as any).aiPhoneLookupName || '')}! Verify to pre-fill your details.</div>
          <div style="display:flex;gap:8px">
            <button class="pp-ai-phone-submit" data-action="ai-phone-verify">Send verification code</button>
            <button class="pp-btn-link" style="font-size:12px" data-action="ai-phone-skip-verify">Skip</button>
          </div>
        </div>`
      ) : `<div class="pp-ai-phone-body">
        <input type="tel" class="pp-ai-phone-input" data-field="aiPhone" placeholder="(555) 555-1234" value="${esc((s as any).aiPhone || '')}" />
        <button class="pp-ai-phone-submit" data-action="ai-phone-submit">Save</button>
      </div>`) : ''}
    </div>` : ''}
    <div class="pp-ai-input-bar">
      <input type="text" class="pp-ai-input" data-field="aiInput" placeholder="${s.aiConciergeEnabled ? 'e.g. massage Tuesday afternoon...' : 'Type a message...'}" value="${esc(s.aiInput)}" />
      <button class="pp-ai-send-btn" data-action="ai-send" ${s.aiLoading ? 'disabled' : ''}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
    ${s.aiConciergeEnabled ? `<div class="pp-ai-manual-link">
      <button class="pp-btn-link" data-action="ai-browse-manual">Or browse services manually &rarr;</button>
    </div>` : ''}
    ${s.aiConversationId ? `<div class="pp-ai-end-chat">
      <button class="pp-btn-link pp-ai-end-btn" data-action="ai-end-chat">End chat</button>
    </div>` : ''}
    </div>`;
}

// ── BOOKING — ENHANCEMENTS ────────────────────────────────────────────────

function renderBkEnhancements(s: PortalState): string {
  const svc = s.curr.service!;
  const groups = s.currEnhGroups.map(grp => {
    let ruleHtml = '';
    if (grp.maxSelections) ruleHtml = `<span class="pp-bk-enh-rule"> — pick up to ${grp.maxSelections}</span>`;
    else if (grp.selectionRule === 'single') ruleHtml = `<span class="pp-bk-enh-rule"> — pick one</span>`;

    const addons = grp.addOns.map(addon => {
      const on = s.isAddonOn(addon.id);
      const icon = on
        ? `<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        : `<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 2V8M2 5H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
      return `
        <div class="pp-bk-enh-item" data-action="toggle-addon" data-grpid="${esc(grp.id)}" data-addonid="${esc(addon.id)}">
          <button class="pp-bk-enh-toggle${on ? ' on' : ''}">${icon}</button>
          <div class="pp-bk-enh-info">
            <div class="pp-bk-enh-name">${esc(addon.name)}</div>
            <div class="pp-bk-enh-desc">${esc(addon.description)}</div>
            ${(() => {
              const pill = membershipPill(enhancementVerdict(s.eligibility, addon.id));
              return pill ? `<div class="pp-mem-pill-row">${pill}</div>` : '';
            })()}
          </div>
          <div class="pp-bk-enh-price">
            ${s.creditFreesAddon(addon.id)
              ? `<div><s style="opacity:.5">$${addon.price.toFixed(2)}</s> <b>Free</b></div><div class="pp-bk-enh-rule">with your offer</div>`
              : `<div>+ $${addon.price.toFixed(2)}</div>`}
            ${addon.durationAdd ? `<div>+ ${addon.durationAdd}min</div>` : ''}
          </div>
        </div>`;
    }).join('');

    const missing = s.enhShowMissing && s.missingRequiredEnhGroups.some(g => g.id === grp.id);
    return `
      <div class="pp-bk-enh-group${missing ? ' pp-bk-enh-group--missing' : ''}" data-enh-group="${esc(grp.id)}">
        <div class="pp-bk-enh-group-title">${esc(grp.name)}${ruleHtml}${grp.required ? ' <span class="pp-bk-enh-required">Required</span>' : ''}</div>
        ${missing ? `<div class="pp-f-error" role="alert">Please choose ${grp.selectionRule === 'single' ? 'one' : 'at least one'} to continue.</div>` : ''}
        ${addons}
      </div>`;
  }).join('');

  return `
    ${membershipFilterBar(s)}
    <div class="pp-bk-svc-bar">
      <div class="pp-bk-svc-bar-name">${esc(svc.name)}</div>
      <div class="pp-bk-svc-bar-right">
        <span class="pp-bk-svc-bar-price">$${s.curr.totalPrice.toFixed(2)}</span>
        <button class="pp-bk-link-btn" data-action="toggle-cat" data-catid="">Change</button>
      </div>
    </div>
    <div class="pp-bk-wrap">${groups}</div>`;
}

// ── BOOKING — PROVIDER ────────────────────────────────────────────────────

const _DOW_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

function renderBkProvider(s: PortalState): string {
  const svc = s.curr.service!;
  if (s.filterPlan && s.filteredProviders.length > 0
      && !s.filteredProviders.some(p => p.type?.startsWith('filter') || planCoversProvider(s.filterPlan!, p.id))) {
    return `
      <div class="pp-bk-svc-bar">
        <div class="pp-bk-svc-bar-name">${esc(svc.name)}</div>
        <span class="pp-bk-svc-bar-price">$${s.curr.totalPrice.toFixed(2)}</span>
      </div>
      ${membershipFilterBar(s)}
      ${membershipFilterEmpty(s)}`;
  }
  if (s.filteredProviders.length === 0) {
    return `
      <div class="pp-bk-svc-bar">
        <div class="pp-bk-svc-bar-name">${esc(svc.name)}</div>
        <span class="pp-bk-svc-bar-price">$${s.curr.totalPrice.toFixed(2)}</span>
      </div>
      <div class="pp-empty" style="text-align:center;padding:40px 20px;color:#888;">
        <p style="font-size:15px;margin:0 0 8px">No providers available</p>
        <p style="font-size:13px;margin:0">Please select a different date or service.</p>
      </div>`;
  }
  // With a membership filter pinned, only providers that plan covers are shown.
  const _fp = s.filterPlan;
  const provList = _fp
    ? s.providersForStep.filter(p => p.type?.startsWith('filter') || planCoversProvider(_fp, p.id))
    : s.providersForStep;
  const items = provList.map(p => {
    let avatarInner = '';
    if (p.type === 'filter-female') {
      avatarInner = 'F';
    } else if (p.type === 'filter-male') {
      avatarInner = 'M';
    } else if (p.type === 'filter-any') {
      avatarInner = `<svg width="20" height="14" viewBox="0 0 20 14" fill="none">
        <circle cx="6" cy="6" r="4" stroke="#5C4A3A" stroke-width="1.3"/>
        <circle cx="14" cy="6" r="4" stroke="#5C4A3A" stroke-width="1.3"/>
        <path d="M0.5 13C0.5 11 3 9.5 6 9.5" stroke="#5C4A3A" stroke-width="1.3" stroke-linecap="round"/>
        <path d="M19.5 13C19.5 11 17 9.5 14 9.5" stroke="#5C4A3A" stroke-width="1.3" stroke-linecap="round"/>
      </svg>`;
    } else {
      avatarInner = esc(p.avatarInitials || p.initial || p.name.charAt(0));
    }
    const avatarStyle = p.avatarBg ? ` style="background:${esc(p.avatarBg)}"` : '';
    const days = Object.keys(p.workDays || {}).sort((a, b) => _DOW_ORDER.indexOf(a) - _DOW_ORDER.indexOf(b)).join(', ');
    const priceStr = p.price != null ? `$${p.price.toFixed(2)}` : '';

    // Grey out providers already booked by other guests in this group
    const isUsed = s.groupSize > 1 && !p.type?.startsWith('filter') && s.isProviderUsedByOtherGuest(p.id);
    const disabledClass = isUsed ? ' pp-bk-prov-disabled' : '';
    const disabledAttr = isUsed ? '' : `data-action="pick-provider" data-provid="${esc(String(p.id))}"`;
    const usedNote = isUsed ? '<div class="pp-bk-prov-unavail">Unavailable — already booked for this group</div>' : '';

    const memPill = isUsed ? ''
      : membershipPill(providerVerdict(s.eligibility, svc.id, p.type?.startsWith('filter') ? null : p.id));
    return `
      <div class="pp-bk-prov-item${disabledClass}" ${disabledAttr}>
        <div class="pp-bk-prov-avatar"${avatarStyle}>${avatarInner}</div>
        <div class="pp-bk-prov-info">
          <div class="pp-bk-prov-name">${esc(p.name)}</div>
          ${usedNote}
          ${!isUsed && days ? `<div class="pp-bk-prov-days">${esc(days)}</div>` : ''}
          ${memPill ? `<div class="pp-mem-pill-row">${memPill}</div>` : ''}
        </div>
        ${!isUsed && priceStr ? `<div class="pp-bk-prov-price">${priceStr}</div>` : ''}
      </div>`;
  }).join('');

  return `
    <div class="pp-bk-svc-bar">
      <div class="pp-bk-svc-bar-name">${esc(svc.name)}</div>
      <span class="pp-bk-svc-bar-price">$${s.curr.totalPrice.toFixed(2)}</span>
    </div>
    ${membershipFilterBar(s)}
    ${s.eligibility.ownedPlanIds.length ? membershipLegend('member') : ''}
    <div class="pp-bk-wrap">${items}</div>`;
}

// ── BOOKING — ADD ANOTHER ─────────────────────────────────────────────────

function renderBkAddAnother(s: PortalState): string {
  // Show items for current guest
  const currentItems = s.items.filter(it => (it.guestIndex ?? 0) === s.currentGuestIndex);
  const label = currentItems.length !== 1 ? 'services' : 'service';
  const personLabel = s.groupSize > 1 && s.currentGuestIndex > 0
    ? `Person ${s.currentGuestIndex + 1}'s ${label}`
    : `Your ${label}`;

  const itemsList = currentItems.map((item) => {
    const idx = s.items.indexOf(item);
    const addonSub = item.selectedAddons.length
      ? `<div class="pp-bk-booked-sub">(${esc(item.selectedAddons.map(a => a.name).join(', '))})</div>`
      : '';
    return `
      <div class="pp-bk-booked-item">
        <div class="pp-bk-booked-info">
          <div class="pp-bk-booked-name">${esc(item.service.name)}</div>
          ${addonSub}
          <div class="pp-bk-booked-sub">with ${esc(item.provider.name)}</div>
        </div>
        <button class="pp-bk-remove-link" data-action="remove-item" data-idx="${idx}">Remove</button>
      </div>`;
  }).join('');

  // Determine the "No" button behavior
  const hasMorePeople = s.groupSize > 1 && s.currentGuestIndex < s.groupSize - 1;
  const noAction = hasMorePeople ? 'next-person' : 'no-more';
  const noLabel = hasMorePeople ? 'Continue to next person' : 'No, continue';

  // "Yes" tapped and someone-else is possible → the follow-up question.
  // "For me"/"for this person" keeps today's behavior; "For someone else"
  // grows the group by one, exactly as if N+1 people were chosen up front.
  const prompt = s.addAnotherWho
    ? `
      <div class="pp-bk-add-prompt">
        <div class="pp-bk-add-prompt-q">Who is the next service for?</div>
        <div class="pp-bk-add-prompt-btns">
          <button class="pp-btn-outline" data-action="add-for-me">${s.groupSize > 1 ? 'This person' : 'For me'}</button>
          <button class="pp-btn-outline" data-action="add-for-other">Someone else</button>
        </div>
      </div>`
    : `
      <div class="pp-bk-add-prompt">
        <div class="pp-bk-add-prompt-q">Do you want to add another service${s.groupSize > 1 ? ' for this person' : ''}?</div>
        <div class="pp-bk-add-prompt-btns">
          <button class="pp-btn-outline" data-action="add-another">Yes</button>
          <button class="pp-btn-outline pp-btn-filled" data-action="${noAction}">${esc(noLabel)}</button>
        </div>
      </div>`;

  return `
    <div class="pp-bk-add-wrap">
      <div class="pp-bk-section-label">${esc(personLabel)}</div>
      ${itemsList}
      ${prompt}
    </div>`;
}

// ── BOOKING — DATETIME ────────────────────────────────────────────────────

/**
 * "Or leave your name and number and we'll get back to you" — the lead form
 * under an empty date. Name + phone required, message optional; it posts to
 * /booking-callback/, which opens (or reuses) their INBOX thread. Nothing is
 * texted automatically — the desk answers from the inbox (2026-09-17).
 */
export const CALLBACK_PREFS: Array<{ key: 'either' | 'text' | 'call'; label: string }> = [
  { key: 'either', label: 'Either' },
  { key: 'text', label: 'Text me' },
  { key: 'call', label: 'Call me' },
];

export function callbackForm(s: PortalState): string {
  if ((s.availabilityConfig as any)?.callbackFormEnabled === false) return '';
  const known = s.callbackKnownContact();
  if (s.callbackStatus === 'sent') {
    return `<div class="pp-bk-callback pp-bk-callback-done">
      <strong>Thanks — we have your message.</strong>
      <span>We'll get back to you shortly at ${esc(known?.phone || s.callbackPhone)}.</span>
      <span class="pp-bk-callback-keep">Nothing is booked yet — pick another date above to keep going whenever you like.</span>
    </div>`;
  }
  const sending = s.callbackStatus === 'sending';
  // Known visitor: their details are already on file, so the form asks for a
  // message and nothing else ("Not you?" hands the fields back).
  const ready = known
    ? true
    : !!s.callbackName.trim() && s.callbackPhone.replace(/\D/g, '').length >= 10;
  // Their number is shown by its last four only — the panel says who we think
  // they are without printing a phone number on a shared screen.
  const last4 = (known?.phone || '').replace(/\D/g, '').slice(-4);
  const identity = known
    ? `<div class="pp-bk-callback-known">Hey <b>${esc(known.name)}</b>! We'll contact you back at your number ending in ${esc(last4)}.
        <button type="button" class="pp-link-btn" data-action="callback-not-you"${sending ? ' disabled' : ''}>(not you?)</button>
      </div>`
    : `<label class="pp-bk-callback-field">
        <span class="pp-bk-callback-lbl">Your name <span class="pp-bk-req" aria-hidden="true">*</span></span>
        <input class="pp-bk-callback-input" type="text" data-field="callbackName" required aria-required="true" placeholder="Your name" value="${esc(s.callbackName)}" autocomplete="name"${sending ? ' disabled' : ''}>
      </label>
      <label class="pp-bk-callback-field">
        <span class="pp-bk-callback-lbl">Phone number <span class="pp-bk-req" aria-hidden="true">*</span></span>
        <input class="pp-bk-callback-input" type="tel" data-field="callbackPhone" required aria-required="true" placeholder="Phone number" value="${esc(s.callbackPhone)}" autocomplete="tel"${sending ? ' disabled' : ''}>
      </label>
      <label class="pp-bk-callback-field">
        <span class="pp-bk-callback-lbl">Email <span class="pp-bk-callback-opt">(optional)</span></span>
        <input class="pp-bk-callback-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" data-field="callbackEmail" placeholder="Email address" value="${esc(s.callbackEmail)}" autocomplete="email" inputmode="email"${sending ? ' disabled' : ''}>
      </label>`;
  return `<div class="pp-bk-callback">
    <div class="pp-bk-callback-intro">Or leave us a message and we will get back to you shortly!</div>
    ${identity}
    <label class="pp-bk-callback-field">
      <span class="pp-bk-callback-lbl">Message</span>
      <textarea class="pp-bk-callback-input pp-bk-callback-msg" data-field="callbackMessage" rows="3" placeholder="Let us know how we can help!"${sending ? ' disabled' : ''}>${esc(s.callbackMessage)}</textarea>
    </label>
    <div class="pp-bk-callback-pref-label">How should we reach you?</div>
    <div class="pp-bk-callback-prefs" role="radiogroup" aria-label="How should we reach you?">
      ${CALLBACK_PREFS.map(p => `<button type="button" class="pp-bk-callback-pref${s.callbackPreference === p.key ? ' selected' : ''}" role="radio" aria-checked="${s.callbackPreference === p.key}" data-action="pick-callback-pref" data-pref="${p.key}"${sending ? ' disabled' : ''}>${p.label}</button>`).join('')}
    </div>
    ${s.callbackStatus === 'error' ? '<div class="pp-bk-callback-err">That didn\u2019t send. Please try again, or call us.</div>' : ''}
    <div class="pp-bk-callback-consent">By submitting this form you agree to receive text messages from us about your request. Message and data rates may apply; reply STOP to opt out.</div>
    <button class="pp-bk-callback-btn" data-action="submit-callback"${ready && !sending ? '' : ' disabled'}>${sending ? 'Sending…' : 'Send'}</button>
  </div>`;
}

function renderBkDatetime(s: PortalState): string {
  const resumeNoticeHtml = (s.resumeNotice ? `<div class="pp-bk-notice-block" style="margin-bottom:12px;">${esc(s.resumeNotice)}</div>` : '')
    + rescheduleProviderNote(s);
  // Which plans limit the days this service can be redeemed on. One line above
  // the calendar says so — an asterisk per date on a month grid is where
  // clutter really bites.
  const memDayPlans = s.curr.service ? dayLimitedPlans(s.eligibility, s.curr.service.id) : [];
  const memDayNote = memDayPlans.length
    ? `<div class="pp-mem-day-note">${memDayPlans.map(p =>
        esc(`${p.name} redeems ${dayLabel(p)}`)).join(' · ')}</div>`
    : '';
  const dowRow = s.weekDays.map(d => `<div class="pp-bk-cal-dow">${esc(d.dowLabel)}</div>`).join('');
  const daysRow = s.weekDays.map(d => {
    const noSlots = !d.isPast && !d.hasSlots;
    const waitlistClickable = noSlots && s.allowWaitlist;
    const classes = [
      'pp-bk-cal-day',
      d.isToday ? 'today' : '',
      d.isPast ? 'past' : '',
      (noSlots && !waitlistClickable) ? 'no-slots' : '',
      waitlistClickable ? 'waitlist-day' : '',
      s.selectedDate === d.date ? 'selected' : '',
      d.hasSlots ? 'has-slots' : ''
    ].filter(Boolean).join(' ');
    const action = (d.isPast || (noSlots && !waitlistClickable)) ? '' : `data-action="set-date" data-date="${esc(d.date)}"`;
    // A day the member's plan can't be redeemed on is marked, not disabled —
    // they may still want to book it and pay.
    const offPlan = memDayPlans.length > 0 && !memDayPlans.some(p => planCoversDate(p, d.date));
    return `<div class="${classes}${offPlan ? ' pp-mem-day-off' : ''}" ${action}>${d.dayNum}</div>`;
  }).join('');

  const prevDisabled = disabled(s.weekOffset <= 0);
  let periodsHtml = '';
  if (s.selectedDate) {
    const sameDayToday = s.usesSameDay(s.selectedDate);
    const sdSlots = sameDayToday ? s.timePeriods.reduce((n, p) => n + p.slots.length, 0) : 0;
    const sdTag = sameDayToday && sdSlots > 0
      ? `<div class="pp-bk-sameday-tag"><span class="pp-bk-sameday-dot"></span>Open today — times with therapists already in the spa${s.sameDayPromo ? ` · <b>${esc(s.sameDayPromo.label || s.sameDayPromo.name)}</b>` : ''}</div>`
      : '';
    const qhAny = s.quietHoursAny;
    const qhTag = qhAny
      ? `<div class="pp-bk-quiet-tag">${esc(qhAny.label)}: times showing a price are cheaper than the usual ${fmtSlotPrice(qhAny.price)} — applied when you book.</div>`
      : '';
    const datebar = `
      <div class="pp-bk-selected-date">
        Selected Date: <strong>${esc(s.selectedDateLabel)}</strong>
      </div>${sdTag}${qhTag}`;

    const periods = s.timePeriods.map(period => {
      const isExpanded = s.expandedPeriod === period.label;
      const chevronRot = isExpanded ? '180deg' : '0';
      let slotsHtml = '';
      if (isExpanded) {
        if (period.sameDayMsg) {
          slotsHtml = `<div class="pp-bk-time-unavail">${esc(period.sameDayMsg)}</div>`;
        } else {
          const interval = s.availabilityConfig?.intervalMinutes || 10;
          const slotBtns = period.slots.map((slot, i) => {
            const selClass = s.selectedTime === slot ? ' selected' : '';
            const qh = s.quietHoursFor(slot);
            const qhClass = qh ? ' pp-bk-time-slot--quiet' : '';
            // The PRICE, not the percentage: no mental math on the button.
            const qhTag = qh ? `<span class="pp-bk-time-quiet" title="${esc(qh.label)} — ${qh.pct}% off">${fmtSlotPrice(qh.price)}</span>` : '';
            const btn = `<button class="pp-bk-time-slot${selClass}${qhClass}" data-action="pick-time" data-slot="${esc(slot)}">${esc(slot)}${qhTag}</button>`;
            // Insert ellipsis separator between non-consecutive slots
            if (i > 0) {
              const prev = _parseSlotMins(period.slots[i - 1]);
              const curr = _parseSlotMins(slot);
              if (prev !== null && curr !== null && (curr - prev) > interval) {
                return `<div class="pp-bk-time-gap">⋯</div>${btn}`;
              }
            }
            return btn;
          }).join('');
          slotsHtml = `<div class="pp-bk-time-grid">${slotBtns}</div>`;
        }
      }
      const rangeHtml = period.range ? `: ${esc(period.range)}` : '';
      return `
        <div class="pp-bk-time-period">
          <div class="pp-bk-time-period-hdr" data-action="toggle-period" data-label="${esc(period.label)}">
            <div class="pp-bk-time-period-left">
              <span class="pp-bk-time-period-name">${esc(period.label)}</span>
              <span class="pp-bk-time-period-range"${period.range ? '' : ' style="display:none"'}>${rangeHtml}</span>
            </div>
            <svg style="transform:rotate(${chevronRot}); transition:transform .2s" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 5L7 9L11 5" stroke="#8A7D73" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          ${isExpanded ? slotsHtml : ''}
        </div>`;
    }).join('');

    // Show waitlist block picker when no slots and waitlist is allowed
    const totalSlots = s.timePeriods.reduce((sum, p) => sum + p.slots.length, 0);
    // Times still on their way, or the request failed: never 'no times' yet.
    const slotsWaiting = totalSlots === 0 && (s.slotsPending || s.slotsFailed);
    const hasSameDayMsg = s.timePeriods.length > 0 && s.timePeriods.some(p => !!p.sameDayMsg);
    if (slotsWaiting) {
      periodsHtml = datebar + (s.slotsFailed
        ? `<div class="pp-bk-no-slots-msg pp-slots-failed">We couldn't load the times for this date.
             <button class="pp-link-btn" data-action="retry-slots">Try again</button></div>`
        : `<div class="pp-loading pp-slots-loading" role="status" aria-live="polite">
             <div class="pp-spinner"></div>
             <div class="pp-loading-text">Finding open times…</div>
           </div>`);
    } else if ((totalSlots === 0 || s.showWaitlistPicker) && s.allowWaitlist) {
      // Resolve time block ranges for the selected date's day of week
      const _wlBlocks = (s.availabilityConfig as any)?.waitlistTimeBlocks;
      const _selDow = s.selectedDate ? new Date(s.selectedDate + 'T12:00:00').getDay() : -1;
      // API returns 0=MON..6=SUN but JS getDay() returns 0=SUN..6=SAT, convert:
      const _apiDow = _selDow === 0 ? 6 : _selDow - 1;
      const _dayBlocks = _wlBlocks?.[_apiDow] || null;
      const allBlocks = [
        { key: 'morning', label: 'Morning' },
        { key: 'midday', label: 'Midday' },
        { key: 'afternoon', label: 'Evening' },
      ];
      // Filter out blocks that are entirely in the past for today
      const _isSelToday = s.selectedDate ? s.isDateToday(s.selectedDate) : false;
      const _nowMins = _isSelToday ? (new Date().getHours() * 60 + new Date().getMinutes()) : 0;
      // A block that has already ended today is not a request anyone can fulfil.
      // Business hours can be missing (e.g. "by appointment" days), so fall back
      // to the same default block ends the server uses instead of keeping it.
      const _defaultEndMins: Record<string, number> = { morning: 12 * 60, midday: 15 * 60, afternoon: 19 * 60 };
      const blocks = allBlocks.filter(b => {
        if (!_isSelToday) return true;
        const range = _dayBlocks?.[b.key];
        let endMins = _defaultEndMins[b.key] ?? 24 * 60;
        const m = range?.end?.match(/^(\d+):(\d+)\s*(am|pm)$/i);
        if (m) {
          let h = parseInt(m[1]); const mn = parseInt(m[2]); const ap = m[3].toLowerCase();
          if (ap === 'pm' && h !== 12) h += 12;
          if (ap === 'am' && h === 12) h = 0;
          endMins = h * 60 + mn;
        }
        return endMins > _nowMins;
      });
      const blockBtns = blocks.map(b => {
        const sel = s.waitlistTimeBlock === b.key ? ' selected' : '';
        const range = _dayBlocks?.[b.key];
        const rangeLabel = range ? `<span class="pp-bk-waitlist-block-range">${range.start} – ${range.end}</span>` : '';
        return `<button class="pp-bk-waitlist-block${sel}" data-action="pick-waitlist-block" data-block="${b.key}">${b.label}${rangeLabel}</button>`;
      }).join('');
      const isToday = s.selectedDate ? s.isDateToday(s.selectedDate) : false;
      const isSameDayBlackout = hasSameDayMsg || (isToday && ((s.availabilityConfig.blackoutWindowHours ?? 0) > 0 || s.usesSameDay(s.selectedDate)));
      let sameDayNote = '';
      if (totalSlots === 0) {
        const noteText = isSameDayBlackout
          ? (s.availabilityConfig.sameDayMessage || (s.availabilityConfig as any)?.blackoutMessage || 'Same-day online booking is not available.')
          : ((s.availabilityConfig as any)?.noAvailabilityMessage || 'No times available on this date.');
        sameDayNote = `<div class="pp-bk-waitlist-msg">${esc(noteText)}</div>`;
      }
      // The picker replaced the time grid — always offer the way back when
      // there were times to come back to.
      const backToTimes = totalSlots > 0
        ? `<button class="pp-bk-waitlist-back" data-action="hide-waitlist-picker">← Back to today’s times</button>`
        : '';
      const noBlocks = !blocks.length
        ? `<div class="pp-bk-waitlist-msg">Today’s time blocks have all passed. ${s.businessPhone ? `Please call or text ${esc(s.businessPhone)}, or pick another date above.` : 'Please pick another date above.'}</div>`
        : '';
      periodsHtml = datebar + `
        <div class="pp-bk-waitlist-section">
          ${sameDayNote}
          ${blocks.length ? `<div class="pp-bk-waitlist-label">Join the waitlist — pick your preferred time block:</div>` : ''}
          <div class="pp-bk-waitlist-blocks">${blockBtns}</div>
          ${noBlocks}${backToTimes}
        </div>`;
    } else if (totalSlots === 0) {
      // No openings and no waitlist: say so (configurable — Settings → Online
      // Booking → No availability message) instead of an empty times step,
      // and offer to take their name and number (callbackForm below).
      const isToday = s.selectedDate ? s.isDateToday(s.selectedDate) : false;
      const isSameDayBlackout = hasSameDayMsg || (isToday && ((s.availabilityConfig.blackoutWindowHours ?? 0) > 0 || s.usesSameDay(s.selectedDate)));
      const noteText = isSameDayBlackout
        ? (s.availabilityConfig.sameDayMessage || (s.availabilityConfig as any)?.blackoutMessage
           || 'Nothing is available on the selected date. Please select another day above.')
        : ((s.availabilityConfig as any)?.noAvailabilityMessage || 'No times available on this date.');
      periodsHtml = datebar + `<div class="pp-bk-no-slots-msg">${esc(noteText)}</div>` + callbackForm(s);
    } else {
      // When slots exist but waitlist is enabled, show a link to join anyway
      const waitlistLink = s.allowWaitlist && totalSlots > 0
        ? `<div class="pp-bk-waitlist-link-wrap">
            <button class="pp-bk-waitlist-link" data-action="show-waitlist-picker">${esc((s.availabilityConfig as any)?.waitlistLinkText || "Don't see a time you're looking for? Join the waitlist")}</button>
            ${(s.availabilityConfig as any)?.waitlistExtraText ? `<div class="pp-bk-waitlist-extra">${esc(String((s.availabilityConfig as any).waitlistExtraText).replace(/\{\{\s*businessPhone\s*\}\}/g, s.businessPhone || ''))}</div>` : ''}
          </div>`
        : '';
      periodsHtml = datebar + periods + waitlistLink;
    }
  }

  return `${resumeNoticeHtml}
    ${membershipFilterBar(s)}
    ${memDayNote}
    <div class="pp-bk-wrap">
      <div class="pp-bk-cal-wrap">
        <div class="pp-bk-cal-nav">
          <button class="pp-bk-cal-nav-btn" data-action="prev-week" ${prevDisabled} aria-label="Previous week">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M8 2L4 6L8 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="pp-bk-cal-month">${esc(s.calMonthLabel)}</div>
          <button class="pp-bk-cal-nav-btn" data-action="next-week" aria-label="Next week">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 2L8 6L4 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="pp-bk-cal-dow-row">${dowRow}</div>
        <div class="pp-bk-cal-days">${daysRow}</div>
      </div>
      ${periodsHtml}
    </div>`;
}

// ── BOOKING — CONTACT (full form, booking entry mode) ─────────────────────

/**
 * Keyboard hints for the contact fields. A name or an email is not a word in
 * anyone's dictionary: with spell-check left on, Android underlined them and a
 * tap to "fix" one opened the suggestion pop-up over the keyboard (Tim,
 * 2026-10-08). Autocomplete lets the phone fill the form from its own records.
 */
function contactInputHints(field: FormField): string {
  if (field.type === 'tel') return ' inputmode="tel" autocomplete="tel" autocorrect="off" spellcheck="false"';
  if (field.type === 'email') return ' inputmode="email" autocomplete="email" autocapitalize="off" autocorrect="off" spellcheck="false"';
  if (field.id === 'firstName') return ' autocomplete="given-name" autocapitalize="words" autocorrect="off" spellcheck="false"';
  if (field.id === 'lastName') return ' autocomplete="family-name" autocapitalize="words" autocorrect="off" spellcheck="false"';
  return '';
}

function renderFormField(field: FormField, s: PortalState): string {
  const val = (s.formData[field.id] ?? '') as string;
  const err = s.formErrors[field.id] || '';
  const ring = err && s.showBlockedSummary ? ' pp-f-missing' : '';

  // Special rendering for promo code field
  if (field.id === 'promo') {
    // Same-day offer already applied by the server — codes can't stack with it.
    if (s.sameDayPromo) {
      return `<div class="pp-f-row"><label class="pp-f-label">${esc(field.label)}</label>
        <div class="pp-f-promo-success">&#10003; Same-day offer applied (${esc(s.sameDayPromo.label || s.sameDayPromo.name)}) — promo codes can’t be combined with it.</div></div>`;
    }
    const isValid   = s.promoStatus === 'valid';
    const isInvalid = s.promoStatus === 'invalid';
    const isLoading = s.promoStatus === 'loading';
    const btnLabel  = isValid ? 'Clear' : isLoading ? '...' : 'Apply';
    const feedbackHtml = isValid && s.promoOffer
      ? `<div class="pp-f-promo-success">&#10003; ${esc(s.promoOffer.offerName)}: ${esc(s.promoOffer.discountLabel)}</div>`
      : isInvalid
      ? `<div class="pp-f-error">${esc(s.promoError)}</div>`
      : '';
    return `
      <div class="pp-f-row">
        <label class="pp-f-label">${esc(field.label)}</label>
        <div class="pp-f-promo-row">
          <input class="pp-f-input" type="text" placeholder="${esc(field.placeholder || '')}"
            value="${esc(val)}" data-field="${esc(field.id)}">
          <button class="pp-f-promo-btn" data-action="${isValid ? 'clear-promo' : 'apply-promo'}" ${isLoading ? 'disabled' : ''}>${btnLabel}</button>
        </div>
        ${feedbackHtml}
        ${s.depositGiftCard?.enabled && !isValid && !isInvalid ? `<div class="pp-f-hint">Promo codes only. Paying with a gift card? You'll add it on the payment step.</div>` : ''}
      </div>`;
  }

  if (field.type === 'tel' || field.type === 'text' || field.type === 'email') {
    const errId = `${field.id}-error`;
    // Inline verify banner for phone field
    let verifyHtml = '';
    if (field.id === 'phone' && (s as any)._contactVerifyShow && !(s as any)._contactVerifyOtpSent) {
      verifyHtml = `<div class="pp-ai-phone-bar" style="margin:6px 0 0;border-radius:var(--pp-radius)">
        <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;font-size:13px">
          <span>&#128100;</span>
          <span style="flex:1">Welcome back, <strong>${esc((s as any)._contactVerifyName || '')}</strong>! Verify to auto-fill.</span>
          <button class="pp-ai-phone-submit" style="font-size:12px;padding:4px 10px" data-action="contact-verify-send">Verify</button>
          <button class="pp-btn-link" style="font-size:11px" data-action="contact-verify-skip">Skip</button>
        </div>
      </div>`;
    } else if (field.id === 'phone' && (s as any)._contactVerifyOtpSent) {
      verifyHtml = `<div class="pp-ai-phone-bar" style="margin:6px 0 0;border-radius:var(--pp-radius)">
        <div style="padding:8px 12px 4px;font-size:12px;color:var(--pp-muted)">Please enter the code we texted to your phone.</div>
        <div style="display:flex;align-items:center;gap:8px;padding:4px 12px 8px;font-size:13px">
          <input type="text" class="pp-ai-phone-input" data-field="contactVerifyOtp" placeholder="6-digit code" maxlength="6" value="${esc((s as any)._contactVerifyOtp || '')}" style="letter-spacing:4px;text-align:center;flex:1" />
          <button class="pp-ai-phone-submit" style="font-size:12px;padding:4px 10px" data-action="contact-verify-confirm">Confirm</button>
        </div>
        <div style="display:flex;gap:12px;padding:0 12px 8px">
          ${(s as any)._contactVerifyError ? `<span style="font-size:12px;color:#e53e3e">${esc((s as any)._contactVerifyError)}</span>` : ''}
          <button class="pp-btn-link" style="font-size:11px;margin-left:auto" data-action="contact-verify-send">Resend code</button>
        </div>
      </div>`;
    }
    return `
      <div class="pp-f-row">
        <label class="pp-f-label">${esc(field.label)}</label>
        <input class="pp-f-input${ring}" type="${field.type}" placeholder="${esc(field.placeholder || '')}"
          value="${esc(val)}" data-field="${esc(field.id)}"${contactInputHints(field)}${err ? ` aria-describedby="${esc(errId)}"` : ''}>
        <div class="pp-f-error" id="${esc(errId)}" role="alert" data-error="${esc(field.id)}">${esc(err)}</div>
        ${verifyHtml}
      </div>`;
  }

  if (field.type === 'textarea') {
    return `
      <div class="pp-f-row">
        <label class="pp-f-label">${esc(field.label)}</label>
        <textarea class="pp-f-input pp-f-textarea" placeholder="${esc(field.placeholder || '')}"
          data-field="${esc(field.id)}">${esc(val)}</textarea>
      </div>`;
  }

  if (field.type === 'radio') {
    const opts = (field.options || []).map(opt => {
      // Only check if user has explicitly selected (don't use defaultValue for pre-selection)
      const isChecked = s.formData[field.id] !== undefined && s.formData[field.id] === opt.value;
      const sublabelHtml = opt.sublabel ? `<div class="pp-f-radio-opt-sub">${esc(opt.sublabel)}</div>` : '';
      return `
        <label class="pp-f-radio-opt">
          <input type="radio" name="${esc(field.id)}" value="${esc(opt.value)}"
            data-field="${esc(field.id)}" ${checked(isChecked)}>
          <div>
            <div class="pp-f-radio-opt-label">${esc(opt.label)}</div>
            ${sublabelHtml}
          </div>
        </label>`;
    }).join('');
    const radioErr = field.required && err ? `<div class="pp-f-error" role="alert">${esc(err)}</div>` : '';
    return `
      <div class="pp-f-row">
        <label class="pp-f-label" style="margin-bottom:10px">${esc(field.label)}${field.required ? ' <span style="color:#e53e3e">*</span>' : ''}</label>
        <div class="pp-f-radio-group">${opts}</div>
        ${radioErr}
      </div>`;
  }

  if (field.type === 'toggle') {
    const isOn = !!(s.formData[field.id]);
    let subHtml = '';
    if (field.subFields && isOn) {
      const subEntries = s.processedSubFields(field.subFields);
      const subPromptHtml = field.subPrompt
        ? `<p class="pp-f-subfield-prompt">${esc(field.subPrompt)}</p>`
        : '';
      const subFieldsHtml = subEntries.map(entry => {
        if (entry.type === 'pair' && entry.fields) {
          const cols = entry.fields.map(f => `
            <div>
              <label class="pp-f-label">${esc(f.label)}</label>
              <input class="pp-f-input" type="${f.type}" placeholder="${esc(f.placeholder || '')}"
                value="${esc((s.formData[f.id] ?? '') as string)}" data-field="${esc(f.id)}">
            </div>`).join('');
          return `<div class="pp-f-row-pair">${cols}</div>`;
        } else if (entry.type === 'single' && entry.field) {
          const sf = entry.field;
          const sfVal = (s.formData[sf.id] ?? '') as string;
          const sfErr = s.formErrors[sf.id] || '';
          if (sf.type === 'tel' || sf.type === 'text' || sf.type === 'email') {
            const sfErrId = `${sf.id}-error`;
            return `
              <div class="pp-f-row">
                <label class="pp-f-label">${esc(sf.label)}</label>
                <input class="pp-f-input" type="${sf.type}" placeholder="${esc(sf.placeholder || '')}"
                  value="${esc(sfVal)}" data-field="${esc(sf.id)}"${sfErr ? ` aria-describedby="${esc(sfErrId)}"` : ''}>
                ${sf.hint ? `<div class="pp-f-hint">${esc(sf.hint)}</div>` : ''}
                <div class="pp-f-error" id="${esc(sfErrId)}" role="alert" data-error="${esc(sf.id)}">${esc(sfErr)}</div>
              </div>`;
          }
          if (sf.type === 'toggle') {
            const sfOn = !!(s.formData[sf.id]);
            const sublabelHtml = sf.sublabel
              ? `<div class="pp-f-toggle-sublabel">${esc(sf.sublabel)}</div>`
              : '';
            // The responsibility toggle only while their phone is blank; the
            // input handler shows / hides it as the number is typed.
            const onlyNoPhone = !!sf.onlyWithoutOtherPhone;
            const hideIt = onlyNoPhone && !s.otherPhoneMissing;
            const sfErrHtml = sfErr ? `<div class="pp-f-error" role="alert" data-error="${esc(sf.id)}">${esc(sfErr)}</div>` : `<div class="pp-f-error" role="alert" data-error="${esc(sf.id)}"></div>`;
            return `
              <div class="pp-f-row"${onlyNoPhone ? ' data-only-without-other-phone="1"' : ''}${hideIt ? ' hidden' : ''}>
                <div class="pp-f-toggle-row" style="align-items:flex-start">
                  <div class="pp-f-toggle-label-wrap">
                    <span class="pp-f-toggle-label">${esc(sf.label)}</span>
                    ${sublabelHtml}
                  </div>
                  <label class="pp-f-tgl" style="flex-shrink:0;margin-top:0.154em">
                    <input type="checkbox" data-field="${esc(sf.id)}" ${checked(sfOn)}>
                    <span class="pp-f-tgl-track"></span>
                  </label>
                </div>
                ${onlyNoPhone ? sfErrHtml : ''}
              </div>`;
          }
        }
        return '';
      }).join('');

      subHtml = `
        <div class="pp-f-subfield-block">
          ${subPromptHtml}
          ${subFieldsHtml}
        </div>`;
    }
    return `
      <div>
        <div class="pp-f-toggle-row">
          <span class="pp-f-toggle-label">${esc(field.label)}</span>
          <label class="pp-f-tgl">
            <input type="checkbox" data-field="${esc(field.id)}" ${checked(isOn)}>
            <span class="pp-f-tgl-track"></span>
          </label>
        </div>
        ${subHtml}
      </div>`;
  }

  return '';
}

function renderGuestFields(s: PortalState): string {
  // Each guest card is its OWN autofill section (`section-guestN`): a phone's
  // contact autofill used to pour the BOOKER's name, number and email into
  // the second person's card, and the server then matched that guest to the
  // booker's record — a two-person booking read 'Guest 1' with no second
  // person on it (Tim, 2026-10-16). Nothing typed is ever altered.
  let html = '<div class="pp-bk-guests-section">';
  html += '<div class="pp-bk-guests-title">Guest Details</div>';
  html += '<div class="pp-bk-guests-note">Please provide contact details for each additional person in your group.</div>';
  for (let gi = 1; gi < s.groupSize; gi++) {
    const g = s.guests[gi] || { firstName: '', lastName: '', phone: '', email: '' };
    const phoneErr = s.formErrors[`guest${gi}Phone`] || '';
    const emailErr = s.formErrors[`guest${gi}Email`] || '';
    html += `
      <div class="pp-bk-guest-card">
        <div class="pp-bk-guest-card-title">Person ${gi + 1}</div>
        <div class="pp-f-row-pair">
          <div>
            <label class="pp-f-label">First name</label>
            <input class="pp-f-input" type="text" placeholder="Required" value="${esc(g.firstName)}" data-field="guest${gi}FirstName" autocomplete="section-guest${gi} given-name" autocapitalize="words" autocorrect="off" spellcheck="false">
          </div>
          <div>
            <label class="pp-f-label">Last name</label>
            <input class="pp-f-input" type="text" placeholder="Required" value="${esc(g.lastName)}" data-field="guest${gi}LastName" autocomplete="section-guest${gi} family-name" autocapitalize="words" autocorrect="off" spellcheck="false">
          </div>
        </div>
        <div class="pp-f-row">
          <label class="pp-f-label">Phone</label>
          <input class="pp-f-input" type="tel" placeholder="Required" value="${esc(g.phone)}" data-field="guest${gi}Phone" autocomplete="section-guest${gi} tel">
          <div class="pp-f-error" data-error="guest${gi}Phone" role="alert">${esc(phoneErr)}</div>
        </div>
        <div class="pp-f-row">
          <label class="pp-f-label">Email</label>
          <input class="pp-f-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Required" value="${esc(g.email)}" data-field="guest${gi}Email" autocomplete="section-guest${gi} email">
          <div class="pp-f-error" data-error="guest${gi}Email" role="alert">${esc(emailErr)}</div>
        </div>
      </div>`;
  }
  html += '</div>';
  return html;
}

function renderCheckoutProducts(s: PortalState): string {
  const selectedIds = new Set(s.selectedProducts.map((p: any) => p.id));

  if (!s.productPickerOpen) {
    const count = s.selectedProducts.length;
    const selectedSummary = count > 0
      ? s.selectedProducts.map((p: any) => `<div class="pp-cp-selected-item">${p.quantity > 1 ? `${p.quantity}× ` : ''}${esc(p.name)} <span class="pp-muted">$${(p.price * (p.quantity || 1)).toFixed(2)}</span> <button class="pp-cp-remove" data-action="cp-remove" data-id="${p.id}">&times;</button></div>`).join('')
      : '';
    return `
      <div class="pp-cp-section">
        <button class="pp-cp-toggle" data-action="cp-toggle">
          <span>&#128722; Add products to your visit</span>
          <span class="pp-muted">${count > 0 ? `(${count} selected)` : ''} &#9660;</span>
        </button>
        ${selectedSummary ? `<div class="pp-cp-selected">${selectedSummary}</div>` : ''}
      </div>`;
  }

  // Expanded product picker
  const query = s.productSearchQuery.toLowerCase();
  const filtered = s.checkoutProducts.filter((p: any) =>
    !query || p.name.toLowerCase().includes(query) || (p.brand || '').toLowerCase().includes(query) || (p.category || '').toLowerCase().includes(query)
  );

  // Group by category or brand
  const groups: Record<string, any[]> = {};
  for (const p of filtered) {
    const key = s.productGroupBy === 'brand' ? (p.brand || 'Other') : (p.category || 'Other');
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }

  const groupHtml = Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([group, products]) => {
    const items = products.map((p: any) => {
      const sel = selectedIds.has(p.id);
      return `<label class="pp-cp-item${sel ? ' selected' : ''}">
        <input type="checkbox" ${sel ? 'checked' : ''} data-action="cp-toggle-product" data-id="${p.id}" data-name="${esc(p.name)}" data-price="${p.price}" />
        <span class="pp-cp-item-name">${esc(p.name)}</span>
        <span class="pp-cp-item-price">$${p.price.toFixed(2)}</span>
      </label>`;
    }).join('');
    return `<div class="pp-cp-group"><div class="pp-cp-group-label">${esc(group)}</div>${items}</div>`;
  }).join('');

  return `
    <div class="pp-cp-section pp-cp-expanded">
      <div class="pp-cp-header">
        <button class="pp-cp-toggle" data-action="cp-toggle">
          <span>&#128722; Add products to your visit</span>
          <span>&#9650;</span>
        </button>
        <div class="pp-cp-controls">
          <input type="text" class="pp-cp-search" data-field="productSearchQuery" placeholder="Search products..." value="${esc(s.productSearchQuery)}" />
          <button class="pp-cp-group-btn" data-action="cp-group-by">
            ${s.productGroupBy === 'category' ? 'By category' : 'By brand'} &#8644;
          </button>
        </div>
      </div>
      <div class="pp-cp-list">${groupHtml || '<div class="pp-muted" style="padding:12px">No products found.</div>'}</div>
    </div>`;
}

function renderBkContact(s: PortalState): string {
  // Booking error
  const bookingErrHtml = s.bookingError
    ? `<div class="pp-bk-error">${esc(s.bookingError)}</div>`
    : '';

  // Booking summary
  const rawTotal = s.items.reduce((sum, it) => sum + it.totalPrice, 0);
  // Today under Open Today rules: the same-day Offer is applied by the server
  // (a promo code cannot stack with it) — show it here so the total is honest.
  const sdPromo = s.sameDayPromo;
  const sdDiscount = sdPromo ? Math.max(0, sdPromo.price - sdPromo.discountedPrice) : 0;
  // Quiet-hours pricing: every item's time must be a discounted one (the server
  // applies the same rule), and a promo code takes precedence over it.
  const qhHits = s.items.map((_it, idx) => s.quietHoursFor(s.itemStartTimes[idx] || s.selectedTime || ''));
  const qhAll = qhHits.length > 0 && qhHits.every(Boolean) && s.promoStatus !== 'valid' && !sdPromo;
  const qhDiscount = qhAll ? s.items.reduce((sum, it, idx) => sum + it.totalPrice * (qhHits[idx]!.pct / 100), 0) : 0;
  const discount = sdPromo ? sdDiscount : (s.promoStatus === 'valid' ? (s.promoOffer?.discountAmount ?? 0) : qhDiscount);
  const finalTotal = Math.max(0, rawTotal - discount);

  let summaryItems = '';
  if (s.groupSize > 1) {
    // Group by guest index
    const byGuest = new Map<number, { item: typeof s.items[0]; idx: number }[]>();
    s.items.forEach((item, idx) => {
      const gi = item.guestIndex ?? 0;
      if (!byGuest.has(gi)) byGuest.set(gi, []);
      byGuest.get(gi)!.push({ item, idx });
    });
    const sections = Array.from(byGuest.entries()).sort((a, b) => a[0] - b[0]).map(([gi, entries]) => {
      const gLabel = gi === 0 ? 'Your services' : `Person ${gi + 1}`;
      const rows = entries.map(({ item, idx }) => {
        const addonDetail = item.selectedAddons.length
          ? `<div class="pp-bk-summary-detail">(${esc(item.selectedAddons.map(a => a.name).join(', '))})</div>`
          : '';
        const time = s.itemStartTimes[idx] || s.selectedTime || '';
        return `
          <div class="pp-bk-summary-svc">${esc(item.service.name)} ($${item.totalPrice.toFixed(2)})</div>
          ${addonDetail}
          <div class="pp-bk-summary-detail">with ${esc(item.provider.name)} at ${esc(time)}</div>`;
      }).join('');
      return `<div class="pp-bk-summary-group-label">${esc(gLabel)}</div>${rows}`;
    }).join('');
    summaryItems = sections;
  } else {
    summaryItems = s.items.map((item, idx) => {
      const addonDetail = item.selectedAddons.length
        ? `<div class="pp-bk-summary-detail">(${esc(item.selectedAddons.map(a => a.name).join(', '))})</div>`
        : '';
      const time = s.itemStartTimes[idx] || s.selectedTime || '';
      return `
        <div class="pp-bk-summary-svc">${esc(item.service.name)} ($${item.totalPrice.toFixed(2)})</div>
        ${addonDetail}
        <div class="pp-bk-summary-detail">with ${esc(item.provider.name)} at ${esc(time)}</div>`;
    }).join('');
  }

  // A recognised returning client: the rest of the form collapses to the
  // phone field — same as the same-day surface. Their name and email are on
  // file; asking again is friction, and a deposit may be waived outright.
  const known = s.isKnownClient;
  const phoneField = s.formConfig.fields.find(f => f.id === 'phone');
  const knownBlock = known ? `
      <div class="pp-bk-remembered pp-bk-known">
        <b>Welcome back!</b> We found your details from a previous visit — nothing else to fill in.
        ${s.knownClient!.cardOnFile && s.depositRequired > 0 && s.depositDueAtSubmit === 0
          ? '<div class="pp-bk-known-sub">You have a card on file, so no deposit is needed today.</div>' : ''}
        <button type="button" class="pp-link-btn" data-action="not-me">Not you?</button>
      </div>` : '';
  const memMismatch = s.membershipProviderMismatch ? `
    <div class="pp-mem-mismatch">
      <div class="pp-mem-mismatch-text">Your membership doesn't cover ${
        esc(s.curr.provider?.name || 'this provider')} — this visit would be full price.</div>
      <button class="pp-mem-mismatch-btn" data-action="change-provider">Choose a covered provider</button>
    </div>` : '';
  const lookupLine = !known && s.phoneLookupBusy
    ? '<div class="pp-bk-lookup-busy">Checking your number…</div>' : '';

  // Form fields (hide "forOther" toggle when group booking — guests are collected separately)
  const formEntries = known
    ? (phoneField ? renderFormField(phoneField, s) : '') + knownBlock + memMismatch
    : s.processedFormFields.map(entry => {
    if (s.groupSize > 1 && entry.type === 'single' && entry.field?.id === 'forOther') return '';
    if (entry.type === 'pair' && entry.fields) {
      const cols = entry.fields.map(f => `
        <div>
          <label class="pp-f-label">${esc(f.label)}</label>
          <input class="pp-f-input" type="${f.type}" placeholder="${esc(f.placeholder || '')}"
            value="${esc((s.formData[f.id] ?? '') as string)}" data-field="${esc(f.id)}">
        </div>`).join('');
      return `<div class="pp-f-row-pair">${cols}</div>`;
    } else if (entry.type === 'single' && entry.field) {
      // Marketing consent is separate from the transactional smsOpt radio —
      // render the optional marketing checkbox directly below it (10DLC).
      if (entry.field.id === 'smsOpt') {
        return renderFormField(entry.field, s) + _smsMarketingCheckbox(s);
      }
      return renderFormField(entry.field, s);
    }
    return '';
  }).join('') + lookupLine + memMismatch;

  // Notices
  const noticeItems = (s.formConfig.notices || []).filter(n => n.html && n.html.replace(/<[^>]*>/g, '').trim());
  const notices = noticeItems.map(n =>
    `<div class="pp-bk-notice-block">${sanitizeHtml(n.html)}</div>`
  ).join('');
  const hasPolicy = !!(s.cancellationPolicyHtml && s.cancellationPolicyHtml.replace(/<[^>]*>/g, '').trim());
  const hasConditions = hasPolicy || noticeItems.length > 0;

  const sdPromoLine = sdPromo && sdDiscount > 0
    ? `<div class="pp-bk-summary-detail pp-bk-sameday-line">Same-day offer · ${esc(sdPromo.label || sdPromo.name)}</div>`
    : '';
  const qhLine = qhAll && qhDiscount > 0
    ? `<div class="pp-bk-summary-detail pp-bk-sameday-line">${esc(qhHits[0]!.label)} · ${Math.min(...qhHits.map(h => h!.pct))}% off</div>`
    : '';
  const totalHtml = rawTotal > 0
    ? discount > 0
      ? `${sdPromoLine}${qhLine}<div class="pp-bk-summary-total"><span class="pp-bk-summary-original">$${rawTotal.toFixed(2)}</span>$${finalTotal.toFixed(2)}</div>`
      : `<div class="pp-bk-summary-total">$${rawTotal.toFixed(2)}</div>`
    : '';

  return `
    <div class="pp-bk-booking-summary">
      <div class="pp-bk-summary-date">${esc(s.selectedDateLabel)}</div>
      ${summaryItems}
      ${totalHtml}
    </div>
    ${bookingErrHtml}
    ${s.checkoutProductsEnabled ? renderCheckoutProducts(s) : ''}
    ${renderProductPrepayChoice(s)}
    <div class="pp-bk-form-body">
      ${s.rememberedFirstName ? `<div class="pp-bk-remembered">Welcome back, <b>${esc(s.rememberedFirstName)}</b> — we filled in your details from last time. <button type="button" class="pp-link-btn" data-action="forget-device">Not you?</button></div>` : ''}
      ${formEntries}
      ${s.needsHomeAddress && !known ? `
      <div class="pp-f-address-section">
        <div class="pp-f-section-label">Home Address</div>
        <div>
          <label class="pp-f-label">Street Address</label>
          <input class="pp-f-input" type="text" placeholder="123 Main St"
            value="${esc((s.formData.address1 ?? '') as string)}" data-field="address1">
        </div>
        <div>
          <label class="pp-f-label">Apt / Suite</label>
          <input class="pp-f-input" type="text" placeholder="Apt 4B"
            value="${esc((s.formData.address2 ?? '') as string)}" data-field="address2">
        </div>
        <div class="pp-f-row-pair">
          <div>
            <label class="pp-f-label">City</label>
            <input class="pp-f-input" type="text" placeholder="City"
              value="${esc((s.formData.city ?? '') as string)}" data-field="city">
          </div>
          <div>
            <label class="pp-f-label">State</label>
            <input class="pp-f-input" type="text" placeholder="State"
              value="${esc((s.formData.state ?? '') as string)}" data-field="state">
          </div>
        </div>
        <div>
          <label class="pp-f-label">ZIP Code</label>
          <input class="pp-f-input" type="text" placeholder="12345"
            value="${esc((s.formData.zip ?? '') as string)}" data-field="zip">
        </div>
      </div>` : ''}
    </div>
    ${s.groupSize > 1 ? renderGuestFields(s) : ''}
    ${s.depositRequired > 0 ? `
    <div class="pp-bk-policy-block" style="background:var(--pp-bg-muted,#f9f5f2);padding:14px;border-radius:8px;margin-bottom:12px;">
      <div class="pp-bk-policy-title" style="margin-bottom:6px;">${s.depositDueAtSubmit > 0 ? `Deposit Required: $${s.depositRequired.toFixed(2)}` : 'Deposit waived'}</div>
      <p style="margin:0;font-size:0.9em;">${s.depositCollectionMethod === 'venmo'
        ? 'A Venmo payment request will be sent to your phone after booking.'
        : (s.depositDueAtSubmit > 0
          ? 'A deposit secures your appointment — you\'ll pay it on the next step.'
          : 'You have a card on file, so no deposit is needed today.')}</p>
    </div>
    ` : ''}
    ${s.waitlistMode && (s.availabilityConfig as any)?.waitlistNotice ? `<div class="pp-bk-notice-block pp-bk-notice-block--waitlist">${esc((s.availabilityConfig as any).waitlistNotice)}</div>` : ''}
    ${hasPolicy ? `<div class="pp-bk-policy-block">
      <div class="pp-bk-policy-title">Cancellation Policy</div>
      <div class="pp-bk-policy-content">${sanitizeHtml(s.cancellationPolicyHtml)}</div>
    </div>` : ''}
    ${notices}
    ${hasConditions || (s.waitlistMode && (s.availabilityConfig as any)?.waitlistNotice) ? `<div class="pp-bk-agree-row" data-anchor="agreedToPolicy">
      <div class="pp-f-toggle-row${s.showBlockedSummary && !s.agreedToPolicy ? ' pp-f-missing' : ''}">
        <span class="pp-f-toggle-label" style="font-size:0.923em">${s.waitlistMode ? 'I agree to the waitlist conditions' : 'I agree to the booking conditions'}</span>
        <label class="pp-f-tgl">
          <input type="checkbox" data-field="agreedToPolicy" ${checked(s.agreedToPolicy)}>
          <span class="pp-f-tgl-track"></span>
        </label>
      </div>
    </div>` : ''}`;
}

// ── BOOKING — CONFIRM (portal mode, pre-filled client details) ────────────

/** The portal's confirm step: 'By booking with us, you agree to our
 *  cancellation policy' — muted, the policy a tap away, always the text as it
 *  stands in Settings (the widget config carries it). Nothing to tick; the
 *  public flow keeps its own printout and switch (Tim, 2026-10-12). */
export function policyAgreementLine(s: { cancellationPolicyHtml: string; policyLineOpen: boolean; apiBase?: string }): string {
  const html = s.cancellationPolicyHtml || '';
  if (!html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()) return '';
  const pageUrl = s.apiBase ? `${s.apiBase.replace(/\/$/, '')}/policy/` : '';
  return `
      <div class="pp-policy-line">
        By booking with us, you agree to our
        <button type="button" class="pp-policy-link" data-action="toggle-policy-line" aria-expanded="${s.policyLineOpen}">cancellation policy</button>.
        ${s.policyLineOpen ? `<div class="pp-policy-text">${sanitizeHtml(html)}${pageUrl ? `<a class="pp-policy-open" href="${esc(pageUrl)}" target="_blank" rel="noopener">Open in a new tab ↗</a>` : ''}</div>` : ''}
      </div>`;
}

/** `<div data-calendoula-policy>` on the spa's site. Scoped under
 *  `.pp-policy-embed` so the host page's rules stay out; a 'Print or save'
 *  link to the hosted page. Empty when no policy is set. */
export function policyEmbedMarkup(html: string, opts: { title?: boolean; pageUrl?: string } = {}): string {
  const text = html || '';
  if (!text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()) return '';
  return `<div class="pp-policy-embed">
    ${opts.title === false ? '' : '<div class="pp-policy-embed-title">Cancellation Policy</div>'}
    <div class="pp-policy-embed-body">${sanitizeHtml(text)}</div>
    ${opts.pageUrl ? `<a class="pp-policy-embed-link" href="${esc(opts.pageUrl)}" target="_blank" rel="noopener">Print or save ↗</a>` : ''}
  </div>`;
}

/** 'Booking with your package — 4 sessions left' above the menu. */
export function creditDiscountLabel(c: BookingCredit): string {
  return c.discountType === 'percent' ? `${Number(c.discountValue || 0)}% off` : `${formatCurrency(Number(c.discountValue || 0))} off`;
}

export function creditBanner(s: PortalState): string {
  const c = s.bookingCredit;
  if (!c) return '';
  const n = c.remaining;
  const sub = c.kind === 'promotion'
    ? `${esc(c.name)} · ${esc(creditDiscountLabel(c))}`
    : `${esc(c.name)} · ${n} session${n === 1 ? '' : 's'} left`;
  return `<div class="pp-credit-banner">
    <span class="pp-credit-banner-icon" aria-hidden="true">${ICON_PACKAGE}</span>
    <div class="pp-credit-banner-text"><b>Booking with your ${c.kind === 'promotion' ? 'offer' : c.kind}</b><span>${sub}</span></div>
    <button class="pp-link" data-action="portal-book-credit-clear">Book without it</button>
  </div>`;
}

/** The booking is made and the session reserved; the spa keeps a card on
 *  file for late changes, and this client has none yet (Tim, 2026-10-14:
 *  waive the deposit, still ask for a card, respecting the spa's settings). */
function renderBkCardOnFile(s: PortalState): string {
  const c = s.bookingCredit;
  const busy = s.cardOnFileBusy;
  return `
    <div class="pp-bk-cof">
      <div class="pp-bk-success-icon"><svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
      <div class="pp-bk-success-title">You're booked</div>
      <div class="pp-bk-success-sub">${c ? `A session from ${esc(c.name)} is reserved for this visit, so there is no deposit.` : 'No deposit is due.'} One last thing: please keep a card on file. It is only charged under our cancellation policy.</div>
      ${s.stripePublishableKey
        ? `<div class="pp-gc-card-field-wrap" style="padding:10px 12px"><div id="pp-stripe-card-element-cof" style="min-height:20px"></div></div>`
        : `<div class="pp-empty-small">Card entry is not available right now.</div>`}
      ${s.cardOnFileError ? `<div class="pp-bk-error" role="alert">${esc(s.cardOnFileError)}</div>` : ''}
      <button class="pp-btn-primary" data-action="cof-save" ${disabled(busy || !s.stripePublishableKey)}>${busy ? 'Saving…' : 'Save card'}</button>
    </div>`;
}

function renderBkConfirm(s: PortalState): string {
  const errorHtml = s.bookingError
    ? `<div class="pp-login-error">${esc(s.bookingError)}</div>` : '';

  // Every service in the cart, each at its own chained time (one service
  // reads exactly as it did before the portal got a cart).
  const many = s.items.length > 1;
  const sessions = s.items.filter((_, i) => s.creditCoversAt(i)).length;
  const offerIdx = s.creditItemIndex;
  const itemRows = s.items.map((it, i) => {
    const addonsHtml = it.selectedAddons.length
      ? `<div class="pp-bk-confirm-addons">${it.selectedAddons.map(a => `<span class="pp-bk-confirm-addon">+ ${esc(a.name)}</span>`).join('')}</div>`
      : '';
    const time = many ? (s.itemStartTimes[i] || s.selectedTime || '') : '';
    const covered = s.creditCoversAt(i);
    const off = s.creditDiscountAt(i);
    const price = covered ? `<s style="opacity:.5">$${it.totalPrice.toFixed(2)}</s> $0.00`
      : off > 0 ? `<s style="opacity:.5">$${it.totalPrice.toFixed(2)}</s> $${(it.totalPrice - off).toFixed(2)}` : '';
    return `
      <div class="pp-bk-confirm-row">
        <span class="pp-bk-confirm-label">${many ? `Service ${i + 1}` : 'Service'}</span>
        <span class="pp-bk-confirm-value">${esc(it.service.name)}${time ? ` <span style="opacity:.7">· ${esc(time)}</span>` : ''}${price ? ` <span style="opacity:.8">· ${price}</span>` : ''}</span>
      </div>
      ${addonsHtml}
      <div class="pp-bk-confirm-row">
        <span class="pp-bk-confirm-label">Provider</span>
        <span class="pp-bk-confirm-value">${esc(it.provider.name)}</span>
      </div>`;
  }).join('');
  const totalBefore = s.cartTotal;
  const totalAfter = s.cartTotalAfterCredit;

  const appointmentSection = `
    <div class="pp-bk-confirm-section">
      ${itemRows}
      <div class="pp-bk-confirm-row">
        <span class="pp-bk-confirm-label">Date</span>
        <span class="pp-bk-confirm-value">${esc(s.selectedDateLabel)}</span>
      </div>
      <div class="pp-bk-confirm-row">
        <span class="pp-bk-confirm-label">${many ? 'Starts' : 'Time'}</span>
        <span class="pp-bk-confirm-value">${esc(s.selectedTime || '')}</span>
      </div>
      <div class="pp-bk-confirm-row">
        <span class="pp-bk-confirm-label">Duration</span>
        <span class="pp-bk-confirm-value">${s.cartDuration} min${many ? ' in all, back to back' : ''}</span>
      </div>
      ${s.bookingCredit && s.bookingCredit.kind !== 'promotion' && sessions > 0 ? `
      <div class="pp-bk-confirm-row pp-bk-confirm-credit">
        <span class="pp-bk-confirm-label">Covered by</span>
        <span class="pp-bk-confirm-value">${esc(s.bookingCredit.name)} · ${sessions} session${sessions === 1 ? '' : 's'}</span>
      </div>` : ''}
      ${s.bookingCredit?.kind === 'promotion' && offerIdx >= 0 ? `
      <div class="pp-bk-confirm-row pp-bk-confirm-credit">
        <span class="pp-bk-confirm-label">Offer</span>
        <span class="pp-bk-confirm-value">${esc(s.bookingCredit.name)} · ${esc(creditDiscountLabel(s.bookingCredit))} · ${s.creditSpreads
          ? `−$${s.items.reduce((t, _it, i) => t + s.creditDiscountAt(i), 0).toFixed(2)}`
          : `−$${s.creditDiscountAt(offerIdx).toFixed(2)}${many ? ` on ${esc(s.items[offerIdx].service.name)}` : ''}`}</span>
      </div>` : ''}
      <div class="pp-bk-confirm-row pp-bk-confirm-total">
        <span class="pp-bk-confirm-label">Total</span>
        <span class="pp-bk-confirm-value">${totalAfter < totalBefore
          ? `<s style="opacity:.5">$${totalBefore.toFixed(2)}</s> $${totalAfter.toFixed(2)}`
          : `$${totalBefore.toFixed(2)}`}</span>
      </div>
      ${s.depositRequired > 0 ? `
      <div class="pp-bk-confirm-row" style="margin-top:8px;padding-top:8px;border-top:1px dashed var(--pp-border,#e5e5e5);">
        <span class="pp-bk-confirm-label" style="font-weight:600;">${s.depositWaivedWithCard ? 'Deposit (waived — card on file)' : 'Deposit Due Now'}</span>
        <span class="pp-bk-confirm-value" style="font-weight:600;${s.depositWaivedWithCard ? 'text-decoration:line-through;opacity:.5' : ''}">\$${(s.depositWaivedWithCard || s.depositCollectionMethod === 'venmo' ? s.depositRequired : s.depositDueAtSubmit).toFixed(2)}</span>
      </div>
      ` : ''}
    </div>`;

  if (s.bookForOther) {
    const toggleOn = s.responsibleAgreed;
    const miss = (v: string) => s.showBlockedSummary && !v.trim() ? ' pp-f-missing' : '';
    const blockedHtml = s.showBlockedSummary && !s.otherValid
      ? `<div class="pp-blocked-banner" role="alert">
          <div class="pp-blocked-title">Almost there — still needed:</div>
          ${s.blockedReasons.map(r =>
            `<button type="button" class="pp-blocked-item" data-action="goto-missing" data-target="${esc(r.field)}">${esc(r.label)}</button>`
          ).join('')}
        </div>` : '';
    return `
      <div class="pp-bk-wrap">
        <div class="pp-bk-confirm-card">
          <div class="pp-bk-confirm-icon">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <circle cx="10" cy="10" r="4" stroke="currentColor" stroke-width="1.5"/>
              <path d="M4 22c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <circle cx="19" cy="8" r="3" stroke="currentColor" stroke-width="1.5"/>
              <path d="M20 16c2.2.5 4 2.5 4 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="pp-bk-confirm-title">Booking for Someone Else</div>

          ${appointmentSection}

          <div class="pp-bk-confirm-section">
            <div class="pp-bk-confirm-section-title">Their Details</div>
            <div class="pp-bk-other-fields">
              <input class="pp-input${miss(s.otherFirstName)}" type="text" placeholder="First name *"
                value="${esc(s.otherFirstName)}" data-field="otherFirstName">
              <input class="pp-input${miss(s.otherLastName)}" type="text" placeholder="Last name *"
                value="${esc(s.otherLastName)}" data-field="otherLastName">
              <input class="pp-input" type="tel" placeholder="Their phone (recommended)"
                value="${esc(s.otherPhone)}" data-field="otherPhone">
              <input class="pp-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Their email (optional)"
                value="${esc(s.otherEmail)}" data-field="otherEmail">
            </div>
            <div class="pp-f-hint" style="margin-top:6px">Their phone number lets us reach them about their appointment — we won't spoil a surprise.</div>
            <div class="pp-bk-toggle-wrap" data-action="toggle-other-minor" style="margin-top:10px">
              <button class="pp-bk-toggle${s.otherIsMinor ? ' on' : ''}" aria-pressed="${s.otherIsMinor}">
                <span class="pp-bk-toggle-knob"></span>
              </button>
              <span class="pp-bk-toggle-label">This person is under 18</span>
            </div>
            <div class="pp-bk-confirm-row pp-bk-booked-by">
              <span class="pp-bk-confirm-label">Booked by</span>
              <span class="pp-bk-confirm-value">${esc(s.client?.name || '')}</span>
            </div>
          </div>

          <div class="pp-bk-toggle-wrap${s.showBlockedSummary && !s.responsibleAgreed ? ' pp-f-missing' : ''}" data-action="toggle-responsible" data-anchor="toggleResponsible">
            <button class="pp-bk-toggle${toggleOn ? ' on' : ''}" aria-pressed="${toggleOn}">
              <span class="pp-bk-toggle-knob"></span>
            </button>
            <span class="pp-bk-toggle-label">I accept responsibility for this booking, including cancellation fees and no-show charges${s.otherPhone.trim() ? '' : ` — and since I'm not including their phone number, I'll pass on any messages and make sure they know about any changes`}.</span>
          </div>

          <div class="pp-bk-confirm-policy">
            You are booking on behalf of someone else. The cancellation policy applies to you as the booker.
          </div>

          ${errorHtml}
          ${blockedHtml}

          ${policyAgreementLine(s)}
        <button class="pp-btn-primary${s.bookingSubmitting ? ' is-loading' : ''}"
            data-action="confirm-booking" ${s.bookingSubmitting ? 'disabled' : ''}>
            ${s.bookingSubmitting ? 'Booking...' : 'Confirm Booking'}
          </button>
        </div>
      </div>`;
  }

  // Standard confirm (booking for self)
  return `
    <div class="pp-bk-wrap">
      <div class="pp-bk-confirm-card">
        <div class="pp-bk-confirm-icon">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <rect x="3" y="6" width="22" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11h22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 3v5M20 3v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="pp-bk-confirm-title">Confirm Your Appointment</div>
        ${s.rebookSource ? '<div class="pp-badge pp-badge-active" style="margin-bottom:12px">Rebooking</div>' : ''}

        ${appointmentSection}

        <div class="pp-bk-confirm-section">
          <div class="pp-bk-confirm-section-title">Your Details</div>
          <div class="pp-bk-confirm-row">
            <span class="pp-bk-confirm-label">Name</span>
            <span class="pp-bk-confirm-value">${esc(s.client?.name || '')}</span>
          </div>
          <div class="pp-bk-confirm-row">
            <span class="pp-bk-confirm-label">Email</span>
            <span class="pp-bk-confirm-value">${esc(s.client?.email || '')}</span>
          </div>
          <div class="pp-bk-confirm-row">
            <span class="pp-bk-confirm-label">Phone</span>
            <span class="pp-bk-confirm-value">${esc(s.client?.phone || '')}</span>
          </div>
        </div>


        <div class="pp-bk-confirm-policy">
          Appointments can be cancelled or rescheduled up to 24 hours in advance.
          Late cancellations or no-shows may incur a fee.
        </div>

        ${_smsConsentBlock(s)}

        ${errorHtml}

        ${policyAgreementLine(s)}
        <button class="pp-btn-primary${s.bookingSubmitting ? ' is-loading' : ''}"
          data-action="confirm-booking" ${s.bookingSubmitting ? 'disabled' : ''}>
          ${s.bookingSubmitting ? 'Booking...' : (s.depositDueAtSubmit > 0 ? 'Continue to Deposit' : 'Confirm Booking')}
        </button>
      </div>
    </div>`;
}

// ── BOOKING — DEPOSIT (in-widget payment step) ─────────────────────────────
// The client record and the booking already exist; this collects the deposit
// that turns the hold into an appointment. Card + zip only, a live hold
// countdown (the widget re-renders it every second), and "text me the link"
// for anyone who would rather pay from their phone.

function _holdBanner(expiresAt: string): string {
  return `
    <div class="pp-bk-hold" data-hold-expires="${esc(expiresAt)}">
      Your appointment will be held for <b class="pp-bk-hold-clock">${holdCountdown(expiresAt)}</b>,
      after which it will be automatically cancelled and removed from our calendar.
    </div>`;
}

function _apptSummaryCard(s: PortalState): string {
  if (s.entryMode === 'portal' && s.items.length === 1 && s.curr.service) {
    return `
      <div class="pp-bk-success-card">
        <div class="pp-bk-success-row"><strong>${esc(s.curr.service.name)}</strong></div>
        <div class="pp-bk-success-row">with ${esc(s.curr.provider?.name || '')}</div>
        <div class="pp-bk-success-row">${esc(s.selectedDateLabel)} at ${esc(s.selectedTime || '')}</div>
      </div>`;
  }
  const rows = s.items.map((item, idx) => {
    const time = s.itemStartTimes[idx] || s.selectedTime || '';
    return `<div class="pp-bk-success-row"><strong>${esc(item.service.name)}</strong> with ${esc(item.provider.name)} at ${esc(time)}</div>`;
  }).join('');
  return `
    <div class="pp-bk-success-card">
      <div class="pp-bk-success-row">${esc(s.selectedDateLabel)}</div>
      ${rows}
    </div>`;
}

function renderBkPayment(s: PortalState): string {
  const d = s.deposit;
  if (!d) return renderEmpty('Nothing to pay', 'This booking does not need a deposit.');
  const errHtml = s.depositPayError ? `<div class="pp-bk-error">${esc(s.depositPayError)}</div>` : '';
  if (d.kind === 'products') return renderProductPrepay(s, d, errHtml);

  // "Later" only when the appointment is far enough out that the hold is
  // real. Inside the hold window the deposit is needed now — saying "we'll
  // text you in 20 minutes" about a 30-minute hold would be a trap.
  let laterHtml = '';  // wrapped with data-anchor="depositTextMe" below
  if (s.depositTextState === 'sent') {
    laterHtml = `<div class="pp-bk-texted">We texted a payment link to your phone${s.depositPhoneLast4 ? ` ending in ${esc(s.depositPhoneLast4)}` : ''}. Finish there, or pay here — whichever is easier.</div>`;
  } else if (d.neededNow) {
    laterHtml = `
      <div class="pp-bk-later">
        <div>Your appointment is coming up soon, so the deposit is needed now to keep the time.</div>
        <button type="button" class="pp-btn-outline" data-action="deposit-text-me" ${s.depositTextState === 'sending' ? 'disabled' : ''}>
          ${s.depositTextState === 'sending' ? 'Sending…' : 'Text me a payment link instead'}
        </button>
      </div>`;
  } else {
    laterHtml = `
      <div class="pp-bk-later">
        <div>Want to complete the deposit later? You'll get a text ${esc(minutesPhrase(d.reminderDelayMinutes))} with a link to finish it.</div>
        <button type="button" class="pp-btn-outline" data-action="deposit-text-me" ${s.depositTextState === 'sending' ? 'disabled' : ''}>
          ${s.depositTextState === 'sending' ? 'Sending…' : 'Text me now instead'}
        </button>
      </div>`;
  }
  if (s.depositTextState === 'failed') {
    laterHtml += `<div class="pp-bk-error">We couldn't send that text. Please pay here, or call us.</div>`;
  }

  return `
    <div class="pp-bk-pay-wrap">
      <div class="pp-bk-success-title" style="font-size:1.15em">Almost there — one last step</div>
      <div class="pp-bk-success-sub">Your appointment is requested. A <b>$${d.amount.toFixed(2)}</b> deposit confirms it.</div>
      ${_apptSummaryCard(s)}
      ${_holdBanner(d.expiresAt)}
      ${errHtml}
      ${_depositNotice(s)}
      ${d.policy?.required ? `
      <div class="pp-bk-policy">
        <div class="pp-bk-policy-title">Cancellation policy</div>
        <div class="pp-bk-policy-text">${d.policy.html}</div>
        <div class="pp-f-toggle-row">
          <span class="pp-f-toggle-label" style="font-size:0.923em">I agree to the cancellation policy</span>
          <label class="pp-f-tgl">
            <input type="checkbox" data-field="depositPolicyAgreed" ${checked(s.depositPolicyAgreed)}>
            <span class="pp-f-tgl-track"></span>
          </label>
        </div>
      </div>` : ''}
      <div class="pp-gc-section" style="padding-bottom:8px;" data-anchor="depositCard">
        ${s.depositGiftCard.enabled && s.depositGcOpen ? `
        <div class="pp-gc-section-title" style="margin-bottom:12px;">$${d.amount.toFixed(2)} deposit</div>
        ${_depositGcBlock(s)}
        <div class="pp-bk-gc-step" style="margin-top:16px;"><span class="pp-bk-gc-num">2</span> A credit or debit card</div>
        <div class="pp-f-hint" style="margin:0 0 10px;">Still needed to hold your appointment. It is only charged for any part of the deposit your gift card doesn't cover — nothing, if the gift card covers it all.</div>
        ${renderCardField(s, 'deposit', {})}` : `
        <div class="pp-gc-section-title" style="margin-bottom:12px;">Pay $${d.amount.toFixed(2)} deposit</div>
        ${_depositGcBlock(s)}
        ${renderCardField(s, 'deposit', {})}
        <div class="pp-f-hint">Card number, expiry, CVC and ZIP — that's all we need.</div>`}
      </div>
      <div data-anchor="depositTextMe">${laterHtml}</div>
    </div>`;
}

// Shop-cart pickup: optional pay-now for the products. The pickup is already
// booked; paying sets the products aside, skipping leaves them unreserved.
function renderProductPrepay(s: PortalState, d: NonNullable<PortalState['deposit']>, errHtml: string): string {
  const lines = s.selectedProducts.map((p: any) =>
    `<div class="pp-bk-success-row"><span>${p.quantity > 1 ? `${p.quantity}× ` : ''}${esc(p.name)}</span><span class="pp-muted">$${(p.price * (p.quantity || 1)).toFixed(2)}</span></div>`).join('');
  const taxRow = (d.tax || 0) > 0
    ? `<div class="pp-bk-success-row"><span class="pp-muted">Tax</span><span class="pp-muted">$${(d.tax || 0).toFixed(2)}</span></div>` : '';
  return `
    <div class="pp-bk-pay-wrap">
      <div class="pp-bk-success-title" style="font-size:1.15em">Your pickup is booked</div>
      <div class="pp-bk-success-sub">Pay for your products now and we'll set them aside for you.</div>
      ${_apptSummaryCard(s)}
      <div class="pp-bk-success-card pp-bk-success-products">
        <div class="pp-bk-success-products-title">&#128722; Your products</div>
        ${lines}${taxRow}
        <div class="pp-bk-success-row"><b>Total</b><b>$${d.amount.toFixed(2)}</b></div>
      </div>
      ${errHtml}
      <div class="pp-gc-section" style="padding-bottom:8px;" data-anchor="depositCard">
        <div class="pp-gc-section-title" style="margin-bottom:12px;">Pay $${d.amount.toFixed(2)} now</div>
        ${renderCardField(s, 'deposit', {})}
        <div class="pp-f-hint">Paid products are reserved for you until your pickup.</div>
      </div>
      <div class="pp-bk-later">
        <div>Rather pay when you come in? Products aren't reserved until they're paid for, so they could sell out before your pickup.</div>
        <button type="button" class="pp-btn-outline" data-action="prepay-skip" ${s.depositPaying ? 'disabled' : ''}>Pay at pickup instead</button>
      </div>
    </div>`;
}

/** The choice on the contact step (pay now pre-selected — it's what we encourage). */
function renderProductPrepayChoice(s: PortalState): string {
  if (!s.productPrepayOffered) return '';
  const opt = (value: 'now' | 'later', title: string, note: string) => {
    const on = (value === 'now') === s.productsPayNow;
    return `
      <button type="button" class="pp-prepay-opt${on ? ' is-on' : ''}" data-action="prepay-choice" data-value="${value}" aria-pressed="${on}">
        <span class="pp-prepay-dot" aria-hidden="true"></span>
        <span class="pp-prepay-text"><span class="pp-prepay-title">${title}</span><span class="pp-prepay-note">${note}</span></span>
      </button>`;
  };
  return `
    <div class="pp-prepay" role="group" aria-label="When would you like to pay for your products?">
      <div class="pp-f-section-label">Your products ($${s.productsTotal.toFixed(2)})</div>
      ${opt('now', 'Pay now', 'Reserved for you — we\'ll set them aside until your pickup.')}
      ${opt('later', 'Pay at pickup', 'Not reserved — they could sell out before you arrive.')}
    </div>`;
}

// The settings-configurable custom message on the deposit screen (the same
// depositPageNotice the texted payment page shows).
function _depositNotice(s: PortalState): string {
  const notice = (s.depositPageNotice || '').replace(/<[^>]*>/g, '').trim()
    ? s.depositPageNotice : '';
  if (!notice) return '';
  return `<div class="pp-bk-notice-block" style="margin-bottom:12px;">${sanitizeHtml(notice)}</div>`;
}

// "Paying with a gift card?" — number + agreement toggle. Deliberately NOT
// validated here: a live check would be a gift-card-number guessing oracle.
// The server resolves it at charge time; the card stays required as the backstop.
//
// Laid out as TWO numbered steps, gift card first (Tim, 2026-09-27): people
// put the number in the promo box, or didn't realise the card is still
// needed, or typed the number and never flipped the switch (it was then
// silently ignored — now the pay step stops and says so).
function _depositGcBlock(s: PortalState): string {
  if (!s.depositGiftCard.enabled) return '';
  if (!s.depositGcOpen) {
    return `
      <button type="button" class="pp-bk-gc-choice" data-action="deposit-gc-toggle">
        <span class="pp-bk-gc-choice-title">&#127873; Paying with a gift card?</span>
        <span class="pp-bk-gc-choice-sub">Tap here to use it — it pays the deposit first.</span>
      </button>`;
  }
  const agreement = (s.depositGiftCard.agreement || '').trim() || DEFAULT_GIFT_CARD_AGREEMENT;
  const needsAgree = !!s.depositGcNumber.trim() && !s.depositGcAgreed;
  return `
    <div class="pp-bk-gc-block">
      <div class="pp-bk-gc-step"><span class="pp-bk-gc-num">1</span> Your gift card</div>
      <label class="pp-f-label" for="pp-deposit-gc-number">Gift card number</label>
      <input id="pp-deposit-gc-number" class="pp-bk-gc-input" type="text" inputmode="numeric" autocomplete="off"
             placeholder="The number on your gift card" data-field="depositGcNumber"
             value="${esc(s.depositGcNumber)}" />
      <div class="pp-f-toggle-row${needsAgree ? ' pp-bk-gc-agree-needed' : ''}" style="align-items:flex-start;padding:12px 0 2px;gap:10px;">
        <span class="pp-bk-gc-agree-text"><b>Use this gift card for the deposit.</b> ${esc(agreement)}</span>
        <label class="pp-f-tgl" style="flex:none;margin-top:2px;">
          <input type="checkbox" data-action="deposit-gc-agree" ${s.depositGcAgreed ? 'checked' : ''}>
          <span class="pp-f-tgl-track"></span>
        </label>
      </div>
      ${needsAgree ? `<div class="pp-bk-gc-agree-hint">Turn this on to use your gift card.</div>` : ''}
      <button type="button" class="pp-bk-gc-link" data-action="deposit-gc-toggle">I don't have a gift card</button>
    </div>`;
}

// Found a saved draft at open: pick up where they left off, or not.
function renderBkResume(s: PortalState): string {
  const d = s.resumeDraft;
  if (!d) return renderLoading();
  const onDeposit = !!d.deposit;
  const title = onDeposit ? 'Finish your deposit?' : 'Pick up where you left off?';
  const sub = onDeposit
    ? `Your appointment for <b>${esc(describeDraft(d))}</b> is reserved and waiting for its $${(d.deposit!.amount || 0).toFixed(2)} deposit.`
    : `You were booking <b>${esc(describeDraft(d))}</b>.`;
  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-title">${title}</div>
      <div class="pp-bk-success-sub">${sub}${onDeposit ? '' : ' We\'ll check it is still available.'}</div>
      ${onDeposit && d.deposit ? _holdBanner(d.deposit.expiresAt) : ''}
      <button class="pp-btn-primary" data-action="resume-booking" ${s.resumeChecking ? 'disabled' : ''}>${s.resumeChecking ? 'Checking…' : (onDeposit ? 'Continue to deposit' : 'Resume booking')}</button>
      <button class="pp-btn-outline" data-action="resume-start-over" ${s.resumeChecking ? 'disabled' : ''}>Start over</button>
    </div>`;
}

// The server found a LIVE deposit hold on this person (409 hold_exists): their
// own earlier booking is what made the time they wanted look taken. Ask
// instead of writing a second hold — two back-to-back holds for one client
// was the symptom (2026-10-09).
function renderBkHoldExists(s: PortalState): string {
  const h = s.existingHold;
  if (!h) return renderLoading();
  const when = `${esc(formatDate(h.date))}${h.time ? ` at ${esc(h.time)}` : ''}`;
  const what = h.services ? ` — ${esc(h.services)}${h.staffName ? ` with ${esc(h.staffName.split(' ')[0])}` : ''}` : '';
  const amount = Number(h.depositAmount || 0);
  const busy = s.existingHoldBusy;
  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-title">You already have a time held</div>
      <div class="pp-bk-success-sub"><b>${when}</b>${what} is reserved for you and waiting for its ${amount ? `$${amount.toFixed(2)} ` : ''}deposit. That is why it was not offered again.</div>
      ${h.payExpiresAt ? _holdBanner(h.payExpiresAt) : ''}
      <button class="pp-btn-primary" data-action="hold-pay" ${busy ? 'disabled' : ''}>Pay the deposit for ${esc(h.time || 'that time')}</button>
      <button class="pp-btn-outline" data-action="hold-release" ${busy ? 'disabled' : ''}>${busy ? 'One moment…' : 'Release it and book this time instead'}</button>
      <button class="pp-btn-link" data-action="hold-keep" ${busy ? 'disabled' : ''} style="margin-top:10px;font-size:13px;color:#888;background:none;border:none;cursor:pointer">I want both appointments</button>
    </div>`;
}

// The end screen after "text me the link": the booking is in, but it is not
// confirmed until the deposit lands — say so plainly, with the clock running.
function renderBkPendingText(s: PortalState): string {
  const d = s.deposit;
  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-icon pp-bk-success-icon--pending">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <circle cx="15" cy="15" r="10" stroke="currentColor" stroke-width="2.2"/>
          <path d="M15 9v6l4 2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="pp-bk-success-title">Submitted — not confirmed yet</div>
      <div class="pp-bk-success-sub">
        We texted a payment link to your phone${s.depositPhoneLast4 ? ` ending in ${esc(s.depositPhoneLast4)}` : ''}.
        Your appointment is confirmed as soon as the <b>$${(d?.amount || 0).toFixed(2)}</b> deposit is paid.
      </div>
      ${_apptSummaryCard(s)}
      ${d ? _holdBanner(d.expiresAt) : ''}
      <button class="pp-btn-outline" data-action="deposit-pay-here">Pay here instead</button>
      <button class="pp-btn-primary" data-action="booking-done">Done</button>
    </div>`;
}

// ── BOOKING — SUCCESS ─────────────────────────────────────────────────────

function _waitlistBlockLabel(block: string): string {
  return block === 'morning' ? 'Morning' : block === 'midday' ? 'Midday' : block === 'afternoon' ? 'Evening' : block;
}

function _renderBirthdayPrompt(s: PortalState): string {
  if (s.birthdayPromptSaved) {
    return `<p class="pp-bk-bday-saved">Birthday saved!</p>`;
  }
  if (s.promptBirthdayAfterBooking && !s.birthdayPromptSaved && s.lastBookingClientId) {
    return `<div class="pp-bk-bday-prompt">
      <p class="pp-bk-bday-label">Add your birthday for easy portal login</p>
      <input type="date" class="pp-input" data-field="birthdayPromptValue" value="${s.birthdayPromptValue}" />
      <button class="pp-btn pp-btn--secondary" data-action="save-birthday" ${s.birthdayPromptSaving || !s.birthdayPromptValue ? 'disabled' : ''}>
        ${s.birthdayPromptSaving ? 'Saving...' : 'Save'}
      </button>
    </div>`;
  }
  return '';
}

// "Your gift card covered $40.50 and your $27.00 tip was charged to your
// card." — without it a gift card on the deposit plus a tip on the card read
// as being charged twice (Sep 2026).
function _depositSplitLine(s: PortalState): string {
  const p = s.depositPaidSplit;
  if (!p || !(p.giftCard > 0)) return '';
  let line = `Your gift card covered $${p.giftCard.toFixed(2)}`;
  if (p.card > 0) {
    line += p.tip > 0 && Math.abs(p.card - p.tip) < 0.005
      ? ` and your $${p.tip.toFixed(2)} tip was charged to your card`
      : ` and $${p.card.toFixed(2)} was charged to your card${p.tip > 0 ? ` (including your $${p.tip.toFixed(2)} tip)` : ''}`;
  }
  return line + '. ';
}

function renderBkSuccess(s: PortalState): string {
  const isWaitlist = s.waitlistMode;
  // The queue had not confirmed when we stopped waiting: soften the claim.
  // The booking almost always lands seconds later and the confirmation text
  // still arrives; if it fails instead, the apology text no longer follows a
  // screen that said "You're all set".
  const outcomePending = s.bookingOutcomePending && !isWaitlist;
  const successTitle = isWaitlist ? "You're on the Waitlist!"
    : outcomePending ? 'Almost done!' : "You're All Set!";
  const successSub = isWaitlist
    ? "We'll reach out when a slot opens up for your preferred time."
    : outcomePending
      ? "We're finishing your booking now — your confirmation will arrive by text in the next few minutes."
      : (s.deposit
        ? `${_depositSplitLine(s)}Your $${s.deposit.amount.toFixed(2)} deposit is paid and your appointment is confirmed. A confirmation has been sent to your email.`
        : 'We look forward to welcoming you. A confirmation has been sent to your email.');

  // Portal entry mode: success with "Back to Dashboard" (one service as it
  // always was; a cart lists each service at its time)
  if (s.entryMode === 'portal') {
    const svc = s.curr.service!;
    const prov = s.curr.provider!;
    const addons = s.curr.selectedAddons.length
      ? ` (${s.curr.selectedAddons.map(a => esc(a.name)).join(', ')})`
      : '';
    const timeRow = isWaitlist
      ? `${esc(s.selectedDateLabel)} &middot; ${_waitlistBlockLabel(s.waitlistTimeBlock)}`
      : `${esc(s.selectedDateLabel)} at ${esc(s.selectedTime || '')}`;
    const cartRows = s.items.length > 1 ? s.items.map((it, i) => {
      const a = it.selectedAddons.length ? ` (${it.selectedAddons.map(x => esc(x.name)).join(', ')})` : '';
      const t = isWaitlist ? '' : ` at ${esc(s.itemStartTimes[i] || s.selectedTime || '')}`;
      return `<div class="pp-bk-success-row"><strong>${esc(it.service.name)}</strong>${a}<br>with ${esc(it.provider.name)}${t}</div>`;
    }).join('') : '';
    return `
      <div class="pp-bk-success-wrap">
        <div class="pp-bk-success-icon${isWaitlist ? ' pp-bk-success-icon--waitlist' : ''}">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
            <path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="pp-bk-success-title">${successTitle}</div>
        <div class="pp-bk-success-sub">${successSub}</div>
        ${s.bookingCredit && !isWaitlist ? (s.bookingCredit.kind === 'promotion'
          ? `<div class="pp-credit-note">Your offer <b>${esc(s.bookingCredit.name)}</b> (${esc(creditDiscountLabel(s.bookingCredit))}) is reserved for this visit and comes off at checkout.</div>`
          : `<div class="pp-credit-note">A session from <b>${esc(s.bookingCredit.name)}</b> is reserved for this visit${s.bookingCredit.remaining > 1 ? ` · ${s.bookingCredit.remaining - 1} left after this one` : s.bookingCredit.remaining === 1 ? ' · that was the last one' : ''}.</div>`) : ''}
        <div class="pp-bk-success-card">
          ${cartRows ? `<div class="pp-bk-success-row">${esc(s.selectedDateLabel)}${isWaitlist ? ` &middot; ${_waitlistBlockLabel(s.waitlistTimeBlock)}` : ''}</div>${cartRows}` : `
          <div class="pp-bk-success-row">
            <strong>${esc(svc.name)}</strong>${addons}
          </div>
          <div class="pp-bk-success-row">
            with ${esc(prov.name)}
          </div>
          <div class="pp-bk-success-row">
            ${timeRow}
          </div>`}
          <div class="pp-bk-success-row pp-bk-success-price">
            $${(cartRows ? s.cartTotalAfterCredit : s.curr.totalPrice).toFixed(2)}
          </div>
        </div>
        ${_renderBirthdayPrompt(s)}
        <button class="pp-btn-primary" data-action="booking-done">Back to Dashboard</button>
      </div>`;
  }

  // Booking entry mode: multi-service success with "Done" / "Book Again"
  let rows = '';
  if (s.groupSize > 1) {
    // Group by guest
    const byGuest = new Map<number, { item: typeof s.items[0]; idx: number }[]>();
    s.items.forEach((item, idx) => {
      const gi = item.guestIndex ?? 0;
      if (!byGuest.has(gi)) byGuest.set(gi, []);
      byGuest.get(gi)!.push({ item, idx });
    });
    rows = Array.from(byGuest.entries()).sort((a, b) => a[0] - b[0]).map(([gi, entries]) => {
      const guest = gi > 0 ? s.guests[gi] : null;
      const gLabel = gi === 0 ? 'Your services' : `Person ${gi + 1}${guest?.firstName ? ' — ' + esc(guest.firstName) : ''}`;
      const itemRows = entries.map(({ item, idx: itemIdx }) => {
        const addons = item.selectedAddons.length
          ? ` (${esc(item.selectedAddons.map(a => a.name).join(', '))})`
          : '';
        const time = s.itemStartTimes[itemIdx] || s.selectedTime || '';
        return `
          <div class="pp-bk-success-row">
            <span style="font-size:1.231em">&#127807;</span>
            <div>
              <strong>${esc(item.service.name)}</strong>${addons}<br>
              with ${esc(item.provider.name)}<br>
              ${esc(s.selectedDateLabel)} at ${esc(time)}
            </div>
          </div>`;
      }).join('');
      return `<div class="pp-bk-success-group-label">${gLabel}</div>${itemRows}`;
    }).join('');
  } else {
    rows = s.items.map((item, idx) => {
      const addons = item.selectedAddons.length
        ? ` (${esc(item.selectedAddons.map(a => a.name).join(', '))})`
        : '';
      const time = isWaitlist
        ? _waitlistBlockLabel(s.waitlistTimeBlock)
        : (s.itemStartTimes[idx] || s.selectedTime || '');
      const timeLine = isWaitlist
        ? `${esc(s.selectedDateLabel)} &middot; ${esc(time)}`
        : `${esc(s.selectedDateLabel)} at ${esc(time)}`;
      return `
        <div class="pp-bk-success-row">
          <span style="font-size:1.231em">&#127807;</span>
          <div>
            <strong>${esc(item.service.name)}</strong>${addons}<br>
            with ${esc(item.provider.name)}<br>
            ${timeLine}
          </div>
        </div>`;
    }).join('');
  }

  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-icon${isWaitlist ? ' pp-bk-success-icon--waitlist' : ''}">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="pp-bk-success-title">${successTitle}</div>
      <div class="pp-bk-success-sub">${successSub}</div>
      <div class="pp-bk-success-card">${rows}</div>
      ${s.selectedProducts.length ? `
      <div class="pp-bk-success-card pp-bk-success-products">
        <div class="pp-bk-success-products-title">&#128722; We'll have these ready for you</div>
        ${s.selectedProducts.map((p: any) => `<div class="pp-bk-success-row"><span>${p.quantity > 1 ? `${p.quantity}× ` : ''}${esc(p.name)}</span><span class="pp-muted">$${(p.price * (p.quantity || 1)).toFixed(2)}</span></div>`).join('')}
        <div class="pp-bk-success-products-note">${s.productsPrepaid ? 'Paid — set aside for you.' : 'Pay when you pick them up.'}</div>
      </div>` : ''}
      ${_renderBirthdayPrompt(s)}
      ${s.upsellDismissed || isWaitlist ? '' : membershipUpsell(s)}
      ${s.clientId && s.portalGroupBooking
        ? '<button class="pp-bk-success-done-btn" data-action="booking-done">Back to my account</button>'
        : `<button class="pp-bk-success-done-btn" data-action="close-panel">${s.isStandalone ? 'Book Again' : 'Done'}</button>`}
    </div>`;
}

// ── GC HELPERS ─────────────────────────────────────────────────────────────

function renderGcPromoCard(s: PortalState, p: { id: number; name: string; description: string; cardValue: number; salePrice: number; artworkUrl: string }): string {
  const isSelected = s.gcSelectedPromotion?.id === p.id;
  const savings = p.cardValue - p.salePrice;
  const artworkHtml = p.artworkUrl
    ? `<img src="${esc(p.artworkUrl)}" class="pp-gc-promo-artwork" alt="${esc(p.name)}">`
    : '';
  return `
    <div class="pp-gc-promo-card${isSelected ? ' selected' : ''}" data-action="gc-select-promo" data-promoid="${p.id}">
      ${artworkHtml}
      <div class="pp-gc-promo-info">
        <div class="pp-gc-promo-name">${esc(p.name)}</div>
        <div class="pp-gc-promo-pricing">
          <span class="pp-gc-promo-value">$${p.cardValue.toFixed(0)} value</span>
          <span class="pp-gc-promo-price">$${p.salePrice.toFixed(0)}</span>
          <span class="pp-gc-promo-savings">Save $${savings.toFixed(0)}</span>
        </div>
        ${p.description ? `<div class="pp-gc-promo-desc">${esc(p.description)}</div>` : ''}
      </div>
      ${isSelected ? '<div class="pp-gc-promo-check">&#10003;</div>' : ''}
    </div>`;
}

function renderGcAmountSection(s: PortalState, gc: { minAmount: number; maxAmount: number }, amtErr: string): string {
  const promos = s.gcPromotions;
  if (!promos.length) {
    return `
      <div class="pp-gc-section" style="padding-top:14px;">
        <div class="pp-f-row">
          <label class="pp-f-label">Amount</label>
          <input class="pp-f-input" type="number" min="${gc.minAmount}" max="${gc.maxAmount}"
            placeholder="Enter amount ($${gc.minAmount.toFixed(2)} – $${gc.maxAmount.toFixed(2)})"
            value="${s.gcAmount !== '' ? esc(String(s.gcAmount)) : ''}" data-field="gcAmount">
          <div class="pp-f-error" data-error="gcAmount">${esc(amtErr)}</div>
        </div>
      </div>`;
  }

  const promoCards = promos.map(p => renderGcPromoCard(s, p)).join('');
  const hasPromoSelected = !!s.gcSelectedPromotion;

  const customAmountSection = hasPromoSelected
    ? `<div class="pp-gc-custom-amount-link">
        <button class="pp-gc-text-btn" data-action="gc-clear-promo">Or enter a custom amount</button>
      </div>`
    : `<div class="pp-gc-custom-amount">
        <div class="pp-f-row">
          <label class="pp-f-label">Custom amount</label>
          <input class="pp-f-input" type="number" min="${gc.minAmount}" max="${gc.maxAmount}"
            placeholder="$${gc.minAmount.toFixed(2)} – $${gc.maxAmount.toFixed(2)}"
            value="${s.gcAmount !== '' ? esc(String(s.gcAmount)) : ''}" data-field="gcAmount">
          <div class="pp-f-error" data-error="gcAmount">${esc(amtErr)}</div>
        </div>
      </div>`;

  return `
    <div class="pp-gc-section" style="padding-top:14px;">
      <div class="pp-gc-section-title">Special Offers</div>
      <div class="pp-gc-promo-list">${promoCards}</div>
      <div class="pp-gc-or-divider">
        <span>or</span>
      </div>
      ${customAmountSection}
    </div>`;
}

// ── GC DETAILS ─────────────────────────────────────────────────────────────

function renderGcDetails(s: PortalState): string {
  const gc = s.gcConfig;
  const artwork = gc.artworkUrl
    ? `<img src="${esc(gc.artworkUrl)}" class="pp-gc-artwork" alt="Gift Card">`
    : GC_SVG_FULL;

  const someoneActive = s.gcPurchaseFor === 'someone' ? ' active' : '';
  const yourselfActive = s.gcPurchaseFor === 'yourself' ? ' active' : '';

  const amtErr = s.gcErrors.amount || '';
  const yourPhoneErr = s.gcErrors.yourPhone || '';
  const yourEmailErr = s.gcErrors.yourEmail || '';
  const recipEmailErr = s.gcErrors.recipientEmail || '';

  const recipientSection = s.gcPurchaseFor === 'someone' ? `
    <div class="pp-gc-section">
      <div class="pp-gc-section-title">Recipient details</div>
      <div class="pp-f-row-pair">
        <div>
          <label class="pp-f-label">First name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.gcRecipient.firstName)}" data-field="gcRecipientFirstName">
        </div>
        <div>
          <label class="pp-f-label">Last name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.gcRecipient.lastName)}" data-field="gcRecipientLastName">
        </div>
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Phone number</label>
        <input class="pp-f-input" type="tel" placeholder="(000) 000-0000"
          value="${esc(s.gcRecipient.phone)}" data-field="gcRecipientPhone">
        <div style="font-size:0.769em; color:var(--pp-muted, #8A7D73); margin-top:4px;">So we can match this card to their account.</div>
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Gift message</label>
        <textarea class="pp-f-input pp-f-textarea" placeholder="Enter an optional gift message"
          data-field="gcRecipientMessage">${esc(s.gcRecipient.message)}</textarea>
      </div>
    </div>
    <div class="pp-gc-section" style="padding-bottom:20px;">
      <div class="pp-gc-section-title">Send to</div>
      <div class="pp-f-row">
        <label class="pp-f-label">What email address should we send this gift card to?</label>
        <input class="pp-f-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Required"
          value="${esc(s.gcRecipient.email)}" data-field="gcRecipientEmail">
        <div class="pp-f-error" data-error="gcRecipientEmail">${esc(recipEmailErr)}</div>
      </div>
      <div style="font-size:0.769em; color:var(--pp-muted, #8A7D73); margin-top:8px; line-height:1.5;">
        Gift card will be sent immediately after successful payment.
      </div>
    </div>` : '';

  return `
    <div class="pp-gc-artwork-wrap">${artwork}</div>

    <div style="padding: 16px 20px 0;">
      <div style="font-size:0.923em; color:var(--pp-muted, #8A7D73); text-align:center; margin-bottom:10px;">Purchase for:</div>
      <div class="pp-gc-for-toggle">
        <button class="pp-gc-for-btn${someoneActive}" data-action="gc-set-for" data-for="someone">
          <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><rect x="2.5" y="6.5" width="12" height="8" rx="1" stroke="currentColor" stroke-width="1.3"/><line x1="2.5" y1="9" x2="14.5" y2="9" stroke="currentColor" stroke-width="1.3"/><line x1="8.5" y1="6.5" x2="8.5" y2="14.5" stroke="currentColor" stroke-width="1.3"/><path d="M8.5 6.5C8.5 6.5 6.5 4 5.5 4C4.5 4 3.5 5 4.5 5.8C5.5 6.5 8.5 6.5 8.5 6.5Z" stroke="currentColor" stroke-width="1.3"/><path d="M8.5 6.5C8.5 6.5 10.5 4 11.5 4C12.5 4 13.5 5 12.5 5.8C11.5 6.5 8.5 6.5 8.5 6.5Z" stroke="currentColor" stroke-width="1.3"/></svg>
          Someone else
        </button>
        <button class="pp-gc-for-btn${yourselfActive}" data-action="gc-set-for" data-for="yourself">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" stroke-width="1.3"/><path d="M2 14C2 11.2 4.686 9 8 9C11.314 9 14 11.2 14 14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
          Yourself
        </button>
      </div>
    </div>

    ${renderGcAmountSection(s, gc, amtErr)}

    <div class="pp-gc-section">
      <div class="pp-gc-section-title">Your details</div>
      ${s.gcRememberedFirstName ? `<div class="pp-bk-remembered">Welcome back, <b>${esc(s.gcRememberedFirstName)}</b> — we filled in your details from last time. <button type="button" class="pp-link-btn" data-action="gc-forget-device">Not you?</button></div>` : ''}
      <div class="pp-f-row">
        <label class="pp-f-label">Phone number</label>
        <input class="pp-f-input" type="tel" placeholder="(000) 000-0000" autocomplete="tel"
          value="${esc(s.gcYourDetails.phone)}" data-field="gcYourPhone">
        <div class="pp-f-error" data-error="gcYourPhone">${esc(yourPhoneErr)}</div>
      </div>
      ${s.gcYourDetailsExpanded ? `
      <div class="pp-f-row-pair">
        <div>
          <label class="pp-f-label">First name</label>
          <input class="pp-f-input" type="text" placeholder="Required" autocomplete="given-name"
            value="${esc(s.gcYourDetails.firstName)}" data-field="gcYourFirstName">
        </div>
        <div>
          <label class="pp-f-label">Last name</label>
          <input class="pp-f-input" type="text" placeholder="Required" autocomplete="family-name"
            value="${esc(s.gcYourDetails.lastName)}" data-field="gcYourLastName">
        </div>
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Your email (for the receipt)</label>
        <input class="pp-f-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Required" autocomplete="email"
          value="${esc(s.gcYourDetails.email)}" data-field="gcYourEmail">
        <div class="pp-f-error" data-error="gcYourEmail">${esc(yourEmailErr)}</div>
      </div>` : '<div class="pp-f-hint">Start with your number — the rest appears once it\'s in.</div>'}
    </div>

    ${recipientSection}
    ${renderGcMissing(s)}
    <div class="pp-gc-spacer-bottom"></div>`;
}

/** After a tap on Continue with gaps: say exactly what is still needed. */
function renderGcMissing(s: PortalState): string {
  if (!s.gcShowMissing) return '';
  const missing = s.gcMissingDetails;
  if (!missing.length) return '';
  return `
    <div class="pp-gc-missing" role="alert">
      <div class="pp-gc-missing-title">Almost there — still needed:</div>
      <ul>${missing.map(([id, label]) => `<li><button type="button" class="pp-link-btn" data-action="gc-focus" data-target="${esc(id)}">${esc(label)}</button></li>`).join('')}</ul>
    </div>`;
}

// ── GC PAYMENT ─────────────────────────────────────────────────────────────

function renderGcPayment(s: PortalState): string {
  const gc = s.gcConfig;
  const pd = s.gcPaymentData;
  const artwork = gc.artworkUrl
    ? `<img src="${esc(gc.artworkUrl)}" class="pp-gc-artwork" alt="Gift Card" style="max-height:140px;object-position:center;">`
    : GC_SVG_COMPACT;

  const payErrHtml = s.gcPayError
    ? `<div class="pp-gc-pay-error">${esc(s.gcPayError)}</div>`
    : '';

  const sendEmail = s.gcPurchaseFor === 'someone' ? s.gcRecipient.email : s.gcYourDetails.email;
  const cardValue = s.gcSelectedPromotion ? s.gcSelectedPromotion.cardValue : Number(s.gcAmount);
  const sendNotice = `A gift card with the value of <strong>$${cardValue.toFixed(2)}</strong> will be sent to <strong>${esc(sendEmail)}</strong> immediately.`;

  const promoLabel = s.gcSelectedPromotion
    ? `<div class="pp-gc-promo-label">${esc(s.gcSelectedPromotion.name)} — $${s.gcSelectedPromotion.cardValue.toFixed(0)} value</div>`
    : '';

  return `
    <div class="pp-gc-artwork-wrap" style="padding-bottom:0;">${artwork}</div>

    <div class="pp-gc-amount-due">
      ${promoLabel}
      <div class="pp-gc-amount-due-label">Amount due:</div>
      <div class="pp-gc-amount-due-value">$${Number(s.gcAmount).toFixed(2)}</div>
      <button class="pp-gc-details-link" data-action="gc-go-back">Gift card details</button>
    </div>

    ${payErrHtml}

    <div class="pp-gc-section" style="padding-bottom:20px;">
      <div class="pp-gc-section-title" style="margin-bottom:16px;">Enter your card</div>

      ${renderCardField(s, 'gc', pd)}
      <div class="pp-f-hint">Card number, expiry, CVC and ZIP — that's all we need.</div>
    </div>

    <div class="pp-gc-send-notice">${sendNotice}</div>
    ${gc.disclaimer ? `<div class="pp-gc-disclaimer">${gc.disclaimer}</div>` : ''}
    ${s.gcSelectedPromotion && gc.promoDisclaimer ? `<div class="pp-gc-disclaimer pp-gc-disclaimer-promo">${gc.promoDisclaimer}</div>` : ''}
    ${(gc.requireTermsAgreement && (gc.disclaimer || (s.gcSelectedPromotion && gc.promoDisclaimer))) ? `
      <label class="pp-gc-terms-agree">
        <input type="checkbox" data-field="gcAgreedTerms" ${s.gcAgreedTerms ? 'checked' : ''}>
        <span>I have read and agree to the gift card terms above.</span>
      </label>` : ''}`;
}

// ── GC SUCCESS ─────────────────────────────────────────────────────────────

function renderGcSuccess(s: PortalState): string {
  const viewUrl = s.gcCardViewUrl || '#';
  return `
    <div class="pp-gc-success-wrap">
      <div class="pp-gc-success-icon">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="pp-gc-success-title">Gift Card Sent!</div>
      <div class="pp-gc-success-msg">${s.gcConfig.successMessage}</div>
      <a href="${esc(viewUrl)}" target="_blank" rel="noopener" class="pp-gc-view-btn">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M1.5 8C1.5 8 4 3 8 3C12 3 14.5 8 14.5 8C14.5 8 12 13 8 13C4 13 1.5 8 1.5 8Z" stroke="white" stroke-width="1.3" stroke-linejoin="round"/>
          <circle cx="8" cy="8" r="2.2" stroke="white" stroke-width="1.3"/>
        </svg>
        View / Download Gift Card
      </a>
      <button class="pp-gc-close-btn" data-action="close-panel">${s.clientId && s.entryMode === 'portal' ? 'Back to my gift cards' : (s.isStandalone ? 'Book Again' : 'Done')}</button>
    </div>`;
}

// ── PACKAGE PURCHASE — LIST ───────────────────────────────────────────────

function renderPkgList(s: PortalState): string {
  const pkgs = s.packages;
  if (pkgs.length === 0) {
    return `<div class="pp-pkg-step-intro">No packages available at this time.</div>`;
  }
  const cards = pkgs.map(pkg => {
    const expiryStr = pkg.expiresEnabled
      ? `Valid for ${pkg.expiresAmount} ${pkg.expiresUnit}`
      : 'No expiration';
    const desc = pkg.shortDescription || pkg.description || '';
    return `
      <div class="pp-pkg-purchase-card">
        <div class="pp-pkg-purchase-info">
          <div class="pp-pkg-purchase-name">${esc(pkg.name)}</div>
          ${desc ? `<div class="pp-pkg-purchase-desc">${esc(desc)}</div>` : ''}
          <div class="pp-pkg-purchase-meta">${esc(expiryStr)} · ${pkg.groupCount} service group${pkg.groupCount !== 1 ? 's' : ''}</div>
        </div>
        <div class="pp-pkg-purchase-right">
          <div class="pp-pkg-purchase-price">$${pkg.totalPrice.toFixed(2)}</div>
          <button class="pp-btn-outline pp-btn-sm" data-action="pick-package" data-pkgid="${pkg.id}">Purchase</button>
        </div>
      </div>`;
  }).join('');
  return `
    <div class="pp-pkg-step-intro">Select a package:</div>
    ${cards}`;
}

// ── PACKAGE PURCHASE — CONTACT ────────────────────────────────────────────

function renderPkgContact(s: PortalState): string {
  const pkg = s.selectedPackage!;
  const expiryStr = pkg.expiresEnabled
    ? `Valid for ${pkg.expiresAmount} ${pkg.expiresUnit}`
    : 'No expiration';
  return `
    <div class="pp-pkg-amount-due" style="margin-bottom:20px;">
      <div class="pp-pkg-amount-due-label">${esc(pkg.name)}</div>
      <div class="pp-pkg-amount-due-value">$${pkg.totalPrice.toFixed(2)}</div>
      <div style="font-size:0.769em;color:var(--pp-muted, #8A7D73)">${esc(expiryStr)}</div>
    </div>
    <div class="pp-pkg-section">
      <div class="pp-pkg-section-title">Your contact details</div>
      <div class="pp-f-row-pair">
        <div>
          <label class="pp-f-label">First name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.pkgContact.firstName)}" data-field="pkgFirstName">
        </div>
        <div>
          <label class="pp-f-label">Last name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.pkgContact.lastName)}" data-field="pkgLastName">
        </div>
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Phone number</label>
        <input class="pp-f-input" type="tel" placeholder="(000) 000-0000"
          value="${esc(s.pkgContact.phone)}" data-field="pkgPhone">
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Email address</label>
        <input class="pp-f-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Required"
          value="${esc(s.pkgContact.email)}" data-field="pkgEmail">
      </div>
    </div>`;
}

// ── PACKAGE PURCHASE — PAYMENT ────────────────────────────────────────────

function renderPkgPayment(s: PortalState): string {
  const pkg = s.selectedPackage!;
  const pd = s.pkgPaymentData;
  const errHtml = s.pkgPayError
    ? `<div class="pp-gc-pay-error">${esc(s.pkgPayError)}</div>` : '';
  return `
    <div class="pp-pkg-amount-due" style="margin-bottom:12px;">
      <div class="pp-pkg-amount-due-label">Amount due:</div>
      <div class="pp-pkg-amount-due-value">$${pkg.totalPrice.toFixed(2)}</div>
      <div style="font-size:0.769em;color:var(--pp-muted, #8A7D73)">${esc(pkg.name)}</div>
    </div>
    ${errHtml}
    <div class="pp-pkg-section" style="padding-bottom:20px;">
      <div class="pp-pkg-section-title" style="margin-bottom:16px;">Credit card</div>
      ${renderCardField(s, 'pkg', pd)}
    </div>`;
}

// ── PACKAGE PURCHASE — SUCCESS ────────────────────────────────────────────

function renderPkgSuccess(s: PortalState): string {
  const pkg = s.selectedPackage!;
  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-icon">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="pp-bk-success-title">Package Purchased!</div>
      <div class="pp-bk-success-sub">Your package has been set up. We look forward to seeing you!</div>
      <div class="pp-bk-success-card">
        <div class="pp-bk-success-row">
          <span style="font-size:1.231em">&#128230;</span>
          <div>
            <strong>${esc(pkg.name)}</strong><br>
            $${pkg.totalPrice.toFixed(2)}
          </div>
        </div>
      </div>
      <button class="pp-bk-success-done-btn" data-action="close-panel">${s.isStandalone ? 'Book Again' : 'Done'}</button>
    </div>`;
}

// ── MEMBERSHIP ENROLLMENT — LIST ──────────────────────────────────────────

/** 'month' → 'mo'. The portal hardcoded '/mo', so an annual plan was shown as
 *  "$900.00/mo" — including on the screen the client uses to authorise it. */
function _unitAbbrev(unit?: string): string {
  return ({ day: 'day', week: 'wk', month: 'mo', year: 'yr' } as Record<string, string>)[unit || 'month'] || (unit || 'mo');
}

function _billingLabel(plan: { price: number; billingInterval: number; billingUnit: string }): string {
  const units: Record<string, string> = { day: 'day', week: 'wk', month: 'mo', year: 'yr' };
  const u = units[plan.billingUnit] || plan.billingUnit;
  return plan.billingInterval === 1
    ? `$${plan.price.toFixed(2)}/${u}`
    : `$${plan.price.toFixed(2)} every ${plan.billingInterval} ${u}s`;
}

function renderMemList(s: PortalState): string {
  const plans = s.membershipPlans;
  if (plans.length === 0) {
    return `<div class="pp-pkg-step-intro">No membership plans available at this time.</div>`;
  }
  const cards = plans.map(plan => {
    const benefits = membershipPerks(plan);
    const benefitsHtml = benefits.length
      ? `<div class="pp-pkg-purchase-meta">${esc(benefits.join(' · '))}</div>` : '';
    const providerNote = membershipProviderNote(plan);
    const providerHtml = providerNote
      ? `<div class="pp-pkg-purchase-meta">${esc(providerNote)}</div>` : '';
    const chargeNote = signupChargeNote(plan);
    const chargeHtml = chargeNote
      ? `<div class="pp-mem-charge-note">${esc(chargeNote)}</div>` : '';
    const desc = plan.description
      ? `<div class="pp-pkg-purchase-desc">${esc(plan.description)}</div>` : '';
    return `
      <div class="pp-pkg-purchase-card">
        <div class="pp-pkg-purchase-info">
          <div class="pp-pkg-purchase-name">${esc(plan.name)}</div>
          ${desc}
          ${benefitsHtml}
          ${providerHtml}
          ${chargeHtml}
        </div>
        <div class="pp-pkg-purchase-right">
          <div class="pp-pkg-purchase-price">${esc(_billingLabel(plan))}</div>
          <button class="pp-btn-outline pp-btn-sm" data-action="pick-membership" data-memid="${plan.id}">Enroll</button>
        </div>
      </div>`;
  }).join('');
  return `
    <div class="pp-pkg-step-intro">Select a membership plan:</div>
    ${cards}`;
}

// ── MEMBERSHIP ENROLLMENT — CONTACT ───────────────────────────────────────

function renderMemContact(s: PortalState): string {
  const plan = s.selectedMembershipPlan!;
  // The provider limit follows the plan all the way to the card field. Naming
  // it only on the list would mean a buyer who arrived by direct link, or who
  // stopped reading after the price, pays without ever seeing it.
  const withWho = membershipProviderNote(plan);
  return `
    <div class="pp-pkg-amount-due" style="margin-bottom:20px;">
      <div class="pp-pkg-amount-due-label">${esc(plan.name)}</div>
      <div class="pp-pkg-amount-due-value">${esc(_billingLabel(plan))}</div>
      ${withWho ? `<div class="pp-mem-with-note">${esc(withWho)}</div>` : ''}
      ${signupChargeNote(plan) ? `<div class="pp-mem-charge-note">${esc(signupChargeNote(plan))}</div>` : ''}
    </div>
    <div class="pp-pkg-section">
      <div class="pp-pkg-section-title">Your contact details</div>
      <div class="pp-f-row-pair">
        <div>
          <label class="pp-f-label">First name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.memContact.firstName)}" data-field="memFirstName">
        </div>
        <div>
          <label class="pp-f-label">Last name</label>
          <input class="pp-f-input" type="text" placeholder="Required"
            value="${esc(s.memContact.lastName)}" data-field="memLastName">
        </div>
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Phone number</label>
        <input class="pp-f-input" type="tel" placeholder="(000) 000-0000"
          value="${esc(s.memContact.phone)}" data-field="memPhone">
      </div>
      <div class="pp-f-row">
        <label class="pp-f-label">Email address</label>
        <input class="pp-f-input" type="email" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Required"
          value="${esc(s.memContact.email)}" data-field="memEmail">
      </div>
    </div>`;
}

// ── MEMBERSHIP ENROLLMENT — PAYMENT ───────────────────────────────────────

function renderMemPayment(s: PortalState): string {
  const plan = s.selectedMembershipPlan!;
  const pd = s.memPaymentData;
  const errHtml = s.memPayError
    ? `<div class="pp-gc-pay-error">${esc(s.memPayError)}</div>` : '';
  return `
    <div class="pp-pkg-amount-due" style="margin-bottom:12px;">
      <div class="pp-pkg-amount-due-label">Membership:</div>
      <div class="pp-pkg-amount-due-value">${esc(_billingLabel(plan))}</div>
      <div style="font-size:0.769em;color:var(--pp-muted, #8A7D73)">${esc(plan.name)}</div>
      ${membershipProviderNote(plan) ? `<div class="pp-mem-with-note">${esc(membershipProviderNote(plan))}</div>` : ''}
      ${signupChargeNote(plan) ? `<div class="pp-mem-charge-note">${esc(signupChargeNote(plan))}</div>` : ''}
    </div>
    ${errHtml}
    <div class="pp-pkg-section" style="padding-bottom:20px;">
      <div class="pp-pkg-section-title" style="margin-bottom:16px;">Credit card</div>
      ${renderCardField(s, 'mem', pd)}
    </div>`;
}

// ── MEMBERSHIP ENROLLMENT — SUCCESS ───────────────────────────────────────

function renderMemSuccess(s: PortalState): string {
  const plan = s.selectedMembershipPlan!;
  return `
    <div class="pp-bk-success-wrap">
      <div class="pp-bk-success-icon">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path d="M7 15L12 20L23 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="pp-bk-success-title">Membership Enrolled!</div>
      <div class="pp-bk-success-sub">Your membership is now active. Welcome aboard!</div>
      <div class="pp-bk-success-card">
        <div class="pp-bk-success-row">
          <span style="font-size:1.231em">&#11088;</span>
          <div>
            <strong>${esc(plan.name)}</strong><br>
            ${esc(_billingLabel(plan))}
          </div>
        </div>
      </div>
      <button class="pp-bk-success-done-btn" data-action="close-panel">${s.isStandalone ? 'Book Again' : 'Done'}</button>
    </div>`;
}


/** "Cancelling refunds $47.50 of your deposit to your card (a $2.50 card
 *  processing fee is kept)." — said before an in-time cancel, from the server. */
export function depositRefundNote(t: SelfServiceTerms): string {
  const r = t?.depositRefund;
  if (!r || !(r.refund > 0)) return '';
  if (r.fee > 0) {
    return `Cancelling refunds ${formatCurrency(r.back)} of your ${formatCurrency(r.refund)} deposit to your card — a ${formatCurrency(r.fee)} card processing fee (${r.percent}%) is not refundable.`;
  }
  return `Cancelling refunds your ${formatCurrency(r.refund)} deposit to your card.`;
}

/** "Cancelling now: your deposit is kept…" — what a late change costs, in words. */
export function lateChangeNote(t: SelfServiceTerms, verb: 'Cancelling' | 'Rescheduling'): string {
  if (!t || t.mode === 'none') return '';
  const bits: string[] = [];
  if (t.chargeFull) bits.push(`the full service is due (${formatCurrency(t.feeAmount)}${t.forfeitDeposit ? ' after your deposit' : ''})`);
  else if (t.chargeDeposit) bits.push(`a ${formatCurrency(t.feeAmount)} late fee applies`);
  else if (t.forfeitDeposit) bits.push(verb === 'Cancelling' ? 'your deposit is not refunded' : 'your deposit becomes non-refundable');
  return bits.length ? `${verb} this close to the appointment: ${bits.join(' and ')}, per our cancellation policy.` : '';
}
